<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex flex-wrap align-items-center justify-content-between mb-1 page-header-row" style="gap:8px;">

      <!-- Left: View mode segmented control -->
      <b-button-group size="sm">
        <template v-if="isAdminLike">
          <b-button :variant="viewMode === 'my' ? 'primary' : 'outline-secondary'" @click="viewMode = 'my'">
            <feather-icon icon="UserIcon" size="13" class="mr-25" />My Calls
      </b-button>
          <b-button :variant="viewMode === 'team' ? 'primary' : 'outline-secondary'" @click="viewMode = 'team'">
            <feather-icon icon="UsersIcon" size="13" class="mr-25" />Team
          </b-button>
          <b-button :variant="viewMode === 'org' ? 'primary' : 'outline-secondary'" @click="viewMode = 'org'">
            <feather-icon icon="GlobeIcon" size="13" class="mr-25" />Org
          </b-button>
        </template>
        <template v-else-if="$can('read', 'account_manager')">
          <b-button :variant="viewMode === 'my' ? 'primary' : 'outline-secondary'" @click="viewMode = 'my'">
            <feather-icon icon="UserIcon" size="13" class="mr-25" />My Calls
          </b-button>
          <b-button :variant="viewMode === 'team' ? 'primary' : 'outline-secondary'" @click="viewMode = 'team'">
            <feather-icon icon="UsersIcon" size="13" class="mr-25" />Team
          </b-button>
        </template>
      </b-button-group>

      <!-- Right: DID pill + agent availability + actions -->
      <div class="d-flex align-items-center flex-wrap" style="gap:6px;">
        <!-- DID indicator -->
        <span
          v-if="myDid"
          class="d-flex align-items-center px-75 py-25 rounded"
          style="gap:5px; background:rgba(40,199,111,0.08); border:1px solid rgba(40,199,111,0.25); font-size:0.8rem;"
        >
          <feather-icon icon="PhoneOutgoingIcon" size="12" class="text-success" />
          <span class="text-muted">DID:</span>
          <strong class="text-success">{{ myDid }}</strong>
        </span>
        <span
          v-else-if="exotelConfig"
          class="d-flex align-items-center px-75 py-25 rounded"
          style="gap:5px; background:rgba(255,159,67,0.08); border:1px solid rgba(255,159,67,0.25); font-size:0.8rem; cursor:default;"
        >
          <feather-icon icon="AlertCircleIcon" size="12" class="text-warning" />
          <span class="text-warning font-weight-bold">No DID assigned</span>
        </span>

        <!-- Agent availability: only when this user has a non-empty DID in exotel config -->
        <span
          v-if="showAvailabilityToggle"
          class="d-inline-flex align-items-center px-75 py-25 rounded start-calling-pill"
          :class="callsAvailable ? 'start-calling-pill--active' : 'start-calling-pill--paused'"
        >
          <b-form-checkbox
            v-model="callsAvailable"
            switch
            size="sm"
            inline
            class="mb-0 start-calling-switch"
            :disabled="callsAvailableSaving"
            @change="onCallsAvailableToggle"
          >
            <span
              class="font-weight-bold start-calling-label"
              :class="callsAvailable ? 'text-success' : 'text-muted'"
            >
              {{ callsAvailable ? 'Available for calls' : 'Calling Paused' }}
            </span>
          </b-form-checkbox>
        </span>

        <div class="header-action-divider d-none d-sm-block" style="width:1px;height:20px;background:#e0e0e0;" />

        <b-button variant="outline-secondary" size="sm" class="header-icon-btn" aria-label="Filters" @click="$bvModal.show('modal-filters')">
          <feather-icon icon="FilterIcon" size="13" />
          <b-badge v-if="activeFilterCount > 0" variant="primary" pill class="ml-25" style="font-size:0.7rem;">{{ activeFilterCount }}</b-badge>
        </b-button>
        <b-dropdown variant="outline-primary" size="sm" right no-caret toggle-class="header-icon-btn" aria-label="Export">
          <template #button-content>
            <feather-icon icon="DownloadIcon" size="13" />
          </template>
          <b-dropdown-item :disabled="isExporting" @click="exportAllReports">
            {{ isExporting ? 'Preparing export…' : 'All reports (ZIP)' }}
          </b-dropdown-item>
          <b-dropdown-item :disabled="isExporting" @click="exportCSV">
            {{ isExporting ? 'Preparing export…' : 'Current tab only' }}
          </b-dropdown-item>
        </b-dropdown>
        <b-button variant="outline-secondary" size="sm" class="header-icon-btn" aria-label="Refresh data" @click="refreshAnalytics">
          <feather-icon icon="RefreshCwIcon" size="13" />
        </b-button>
            </div>
            </div>

    <!-- Filters Modal -->
    <b-modal id="modal-filters" title="Filters" size="lg" hide-footer @shown="onFiltersModalShown">
      <my-calls-filters
        :filters="filters"
        :applied-filters="appliedFilters"
        :view-mode="viewMode"
        :is-admin-like="isAdminLike"
        :recruiter-options="recruiterOptions"
        :manager-options="managerOptions"
        :client-options="clientOptions"
        :vertical-options="verticalFilterOptions"
        :category-options="categoryFilterOptions"
        :candidate-status-options="candidateStatusFilterOptions"
        @apply="applyFilters"
        @reset="resetFilters"
        @reset-page="_setCurrentPageSilent(1)"
      />
    </b-modal>

    <!-- Your Call Quality sits above KPI cards (My view) -->
    <my-calls-analysis-report
      v-if="viewMode === 'my'"
      layout="compact-summary"
      :analyzed-calls="callsWithAnalysis"
      :recruiter-name-map="recruiterNameMap"
      :view-mode="viewMode"
      :is-loading="callQualityLoading"
    />

    <!-- KPI Analytics -->
    <my-calls-kpi-cards
      :kpi="kpi"
      :is-loading="isLoadingAggregations"
      :active-filter="kpiFilter"
      @filter="setKpiFilter"
    />
    <b-alert
      v-if="analysisFilterClientOnly && (appliedFilters.callAnalysis === 'analyzed' || appliedFilters.callAnalysis === 'not_analyzed')"
      show
      variant="warning"
      class="mb-1 py-75"
    >
      <strong>Call Analysis was not applied by the API.</strong>
      History is showing this page as returned (not a client-only slice).
      Deploy the <code>p_analyzed</code> SQL migration so <code>analyzed=true|false</code> filters rows and totals on the server.
    </b-alert>
    <div
      v-if="!isLoading && (callAnalyticsTotalRecords || allCalls.length)"
      class="d-flex flex-wrap align-items-center text-muted mb-1"
      style="font-size:0.8rem; gap:8px;"
    >
      <span>
        Page {{ currentPage }}
        · {{ analyzedOnCurrentPageCount.toLocaleString('en-IN') }} on this page
        <template v-if="analysisFilterClientOnly && (appliedFilters.callAnalysis === 'analyzed' || appliedFilters.callAnalysis === 'not_analyzed')">
          · Showing {{ appliedFilters.callAnalysis === 'analyzed' ? 'analyzed' : 'unanalyzed' }} on this page ({{ analyzedOnCurrentPageCount.toLocaleString('en-IN') }} rows)
          <template v-if="callAnalyticsTotalRecords > analyzedOnCurrentPageCount">
            · Unfiltered API total {{ callAnalyticsTotalRecords.toLocaleString('en-IN') }}
          </template>
        </template>
        <template v-else-if="hasExtraListFilters || historySearchActive">
          · {{ (historySearchActive ? Number(historyServerTotal || 0).toLocaleString('en-IN') : extraFilterTotalLabel) }} matching{{ historySearchActive ? ' search' : ' filters' }}
          <template v-if="!periodCacheReady"> (loading full range)</template>
        </template>
        <template v-else-if="appliedFilters.callAnalysis === 'analyzed' || appliedFilters.callAnalysis === 'not_analyzed'">
          · {{ (historyClientPaginationActive ? historyServerTotal : callAnalyticsTotalRecords).toLocaleString('en-IN') }}
          {{ appliedFilters.callAnalysis === 'analyzed' ? 'analyzed' : 'answered, not analyzed' }} total
          <template v-if="historyPeriodCountsLoading"> (loading full range)</template>
        </template>
        <template v-else>
          · {{ callAnalyticsTotalRecords.toLocaleString('en-IN') }} total
        </template>
        <template v-if="appliedFilters.datePreset === 'today' || (appliedFilters.startDate === appliedFilters.endDate)">
          · {{ appliedFilters.startDate }}
        </template>
        <template v-else-if="appliedFilters.startDate && appliedFilters.endDate">
          · {{ appliedFilters.startDate }} – {{ appliedFilters.endDate }}
        </template>
      </span>
      <span v-if="isFetchingHistorySearch" class="d-inline-flex align-items-center" style="gap:6px;">
        <b-spinner small variant="secondary" />
        Searching all pages…
      </span>
      <span v-else-if="isFetchingSearchCache" class="d-inline-flex align-items-center" style="gap:6px;">
        <b-spinner small variant="secondary" />
        Loading full-range KPIs…
      </span>
      <span v-if="isFetchingMore" class="d-inline-flex align-items-center" style="gap:6px;">
        <b-spinner small variant="secondary" />
        Loading page…
      </span>
    </div>

    <!-- Alert Panel: always three static cards — never v-if away when empty -->
    <b-row class="mb-2 align-items-stretch alert-panel-strip">

      <!-- Not Answered -->
      <b-col cols="12" md="4" class="mb-1 mb-md-0 d-flex alert-panel-strip__col">
        <b-card no-body class="h-100 flex-fill alert-panel-card">
          <b-card-header class="py-75 alert-panel-card__header">
            <span class="font-weight-bold text-secondary">
              <feather-icon icon="PhoneMissedIcon" size="14" class="mr-50" />
              Not Answered
              <b-badge variant="secondary" class="ml-50">
                {{ notAnsweredDisplayCount }}{{ notAnsweredCountPartial ? '+' : '' }} pending
              </b-badge>
              <small v-if="alertUsesOrgAttention" class="text-muted font-weight-normal ml-50">org today</small>
            </span>
          </b-card-header>
          <div class="alert-panel-card__body">
            <div v-if="!displayNotAnswered.length" class="text-center py-2 text-muted small">No not-answered calls</div>
            <b-list-group v-else flush>
              <b-list-group-item
                v-for="(c, idx) in displayNotAnswered.slice(0, alertPreviewLimit)"
                :key="'na-'+idx"
                class="px-2 py-50"
              >
                <div class="alert-call-row">
                  <div class="alert-call-row__info">
                    <div class="small font-weight-bold text-truncate">{{ getCandidateName(c) || c.phone || c._recruiterName || '—' }}</div>
                    <small class="text-muted d-block text-truncate">
                      {{ c.date }}<template v-if="hasDisplayPhone(c)"> · {{ c.phone }}</template><template v-else> · No phone</template>
                    </small>
                    <small
                      v-if="(viewMode !== 'my' || alertUsesOrgAttention) && c._rawRecruiter"
                      class="text-muted alert-call-row__recruiter"
                    >
                      <feather-icon icon="UserIcon" size="10" class="mr-25 flex-shrink-0" />
                      <span class="text-truncate">{{ c._recruiterName || recruiterNameMap[c._rawRecruiter] || c._rawRecruiter }}</span>
                    </small>
            </div>
                  <b-button
                    v-if="canDialItem(c)"
                    variant="outline-success"
                    size="sm"
                    class="alert-call-row__action"
                    aria-label="Call"
                    @click="confirmCall(c)"
                  >
                    <feather-icon icon="PhoneIcon" size="12" />
                  </b-button>
                  <b-button
                    v-else
                    variant="outline-secondary"
                    size="sm"
                    class="alert-call-row__action"
                    disabled
                    aria-label="No valid phone number on this call record"
                  >
                    <feather-icon icon="PhoneOffIcon" size="12" />
                  </b-button>
            </div>
              </b-list-group-item>
            </b-list-group>
            </div>
          <div v-if="displayNotAnswered.length > alertPreviewLimit" class="alert-panel-card__footer text-center py-50">
            <b-badge
              variant="light-secondary"
              style="cursor:pointer;"
              @click="setKpiFilter('not-answered')"
            >View All {{ notAnsweredDisplayCount }}{{ notAnsweredCountPartial ? '+' : '' }}</b-badge>
            </div>
        </b-card>
      </b-col>

      <!-- Missed Calls -->
      <b-col cols="12" md="4" class="mb-1 mb-md-0 d-flex alert-panel-strip__col">
        <b-card no-body class="h-100 flex-fill alert-panel-card">
          <b-card-header class="py-75 alert-panel-card__header">
            <span class="font-weight-bold text-secondary">
              <feather-icon icon="PhoneIncomingIcon" size="14" class="mr-50" />
              Missed Calls
              <b-badge variant="secondary" class="ml-50">
                {{ missedDisplayCount }}{{ missedCountPartial ? '+' : '' }}
              </b-badge>
              <small v-if="missedWidgetRangeLabel" class="text-muted font-weight-normal ml-50">{{ missedWidgetRangeLabel }}</small>
            </span>
          </b-card-header>
          <div class="alert-panel-card__body">
            <div v-if="!displayMissed.length" class="text-center py-2 text-muted small">
              <div>No missed calls</div>
              <div class="mt-25">Inbound to your number while unavailable will show here.</div>
            </div>
            <b-list-group v-else flush>
              <b-list-group-item
                v-for="(c, idx) in displayMissed.slice(0, alertPreviewLimit)"
                :key="'mc-'+idx"
                class="px-2 py-50"
              >
                <div class="alert-call-row">
                  <div class="alert-call-row__info">
                    <div class="alert-call-row__name font-weight-bold text-truncate">{{ getCandidateName(c) || c.phone || c._recruiterName || '—' }}</div>
                    <small class="alert-call-row__meta d-block text-truncate">
                      {{ c.date }}<template v-if="hasDisplayPhone(c)"> · {{ c.phone }}</template><template v-else> · No phone</template>
                    </small>
                    <b-badge
                      v-if="c._callsAvailableSkip"
                      variant="light-secondary"
                      class="alert-call-row__away"
                    >Received while away</b-badge>
                    <small
                      v-if="(viewMode !== 'my' || alertUsesOrgAttention) && c._rawRecruiter"
                      class="alert-call-row__meta alert-call-row__recruiter"
                    >
                      <feather-icon icon="UserIcon" size="10" class="mr-25 flex-shrink-0" />
                      <span class="text-truncate">{{ c._recruiterName || recruiterNameMap[c._rawRecruiter] || c._rawRecruiter }}</span>
                    </small>
            </div>
                  <b-button
                    v-if="canDialItem(c)"
                    variant="outline-success"
                    size="sm"
                    class="alert-call-row__action"
                    aria-label="Call Back"
                    @click="callBackMissed(c)"
                  >
                    <feather-icon icon="PhoneIcon" size="12" />
                  </b-button>
                  <b-button
                    v-else
                    variant="outline-secondary"
                    size="sm"
                    class="alert-call-row__action"
                    disabled
                    aria-label="No valid phone number on this call record"
                  >
                    <feather-icon icon="PhoneOffIcon" size="12" />
                  </b-button>
            </div>
              </b-list-group-item>
            </b-list-group>
            </div>
          <div v-if="displayMissed.length > alertPreviewLimit" class="alert-panel-card__footer text-center py-50">
            <b-badge
              variant="light-secondary"
              style="cursor:pointer;"
              @click="viewAllMissedCalls"
            >View All {{ missedDisplayCount }}{{ missedCountPartial ? '+' : '' }}</b-badge>
            </div>
        </b-card>
      </b-col>

      <!-- Output: Not in ATS, AI flags, callback reminders -->
      <b-col cols="12" md="4" class="mb-1 mb-md-0 d-flex alert-panel-strip__col">
        <b-card no-body class="h-100 flex-fill alert-panel-card">
          <b-card-header class="py-75 alert-panel-card__header">
            <span class="font-weight-bold text-secondary">
              <feather-icon icon="BellIcon" size="14" class="mr-50" />
              Output
              <b-badge variant="secondary" class="ml-50">{{ outputCardCount }}</b-badge>
            </span>
          </b-card-header>
          <div class="alert-panel-card__body">
            <recruiter-calling-tracking
              :preview-limit="alertPreviewLimit"
              :can-view-org="isAdminLike"
              :can-view-team="canManageCalls || $can('read', 'account_manager')"
              :refresh-key="attentionRefreshKey"
              :source-calls="outputSourceCalls"
              :source-complete="periodCacheReady"
              :period-loading="isFetchingSearchCache || isLoading"
              :start-date="appliedFilters.startDate"
              :end-date="appliedFilters.endDate"
              :has-callback-rows="upcomingReminders.length > 0"
              @count="callingTrackingCount = $event"
              @attention="onCallingAttention"
            />
            <div v-if="upcomingReminders.length && callingTrackingCount" class="px-2 pt-50">
              <small class="font-weight-bold text-secondary">Reminders</small>
            </div>
            <b-list-group v-if="upcomingReminders.length" flush>
              <b-list-group-item
                v-for="(r, idx) in upcomingReminders.slice(0, alertPreviewLimit)"
                :key="'rem-'+idx"
                class="px-2 py-50"
              >
                <div class="alert-call-row">
                  <div class="alert-call-row__info">
                    <div class="small font-weight-bold text-truncate">{{ r.candidateName || r.phone || '—' }}</div>
                    <small class="text-muted alert-call-row__recruiter">
                      <feather-icon icon="ClockIcon" size="11" class="mr-25 flex-shrink-0" />
                      <span class="text-truncate">{{ formatReminderTime(r.remindAt) }}</span>
                      <b-badge
                        v-if="new Date(r.remindAt) <= new Date()"
                        variant="warning"
                        class="ml-50 flex-shrink-0"
                        style="font-size:0.6rem;"
                      >Due</b-badge>
                    </small>
                    <small
                      v-if="viewMode !== 'my' && (r.recruiterName || r.recruiterEmpId)"
                      class="text-muted alert-call-row__recruiter"
                    >
                      <feather-icon icon="UserIcon" size="10" class="mr-25 flex-shrink-0" />
                      <span class="text-truncate">{{ r.recruiterName || r.recruiterEmpId }}</span>
                    </small>
                    <small v-if="r.note" class="text-secondary d-block text-truncate">{{ r.note }}</small>
            </div>
                  <div class="alert-call-row__actions">
                    <b-button
                      v-if="canDialReminder(r)"
                      variant="outline-success"
                      size="sm"
                      aria-label="Call now"
                      @click="callReminderDirect(r)"
                    >
                      <feather-icon icon="PhoneIcon" size="12" />
                    </b-button>
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      aria-label="Dismiss"
                      @click="dismissReminder(r, idx)"
                    >
                      <feather-icon icon="XIcon" size="12" />
                    </b-button>
            </div>
            </div>
              </b-list-group-item>
            </b-list-group>
            </div>
          <div
            v-if="callingTrackingCount > alertPreviewLimit || upcomingReminders.length > alertPreviewLimit"
            class="alert-panel-card__footer text-center py-50"
          >
            <small v-if="callingTrackingCount > alertPreviewLimit" class="text-muted d-block">
              Showing {{ alertPreviewLimit }} of {{ callingTrackingCount }} calls
            </small>
            <small v-if="upcomingReminders.length > alertPreviewLimit" class="text-muted d-block">
              Showing {{ alertPreviewLimit }} of {{ upcomingReminders.length }} reminders
            </small>
          </div>
        </b-card>
      </b-col>

    </b-row>

    <!-- Content Tabs: Team Performance | Datewise Performance | Call History -->
    <b-tabs ref="mainTabs" v-model="tabIndex" lazy class="mt-1 main-tabs">
      <b-tab v-if="viewMode !== 'my'">
        <template #title>
          <feather-icon icon="UsersIcon" size="13" class="mr-25 d-none d-sm-inline" />
          <span class="d-none d-sm-inline">Team Performance</span>
          <span class="d-sm-none">Team</span>
        </template>
        <div class="pt-1">
          <div v-if="isLoadingAggregations" class="text-center py-4">
            <b-spinner variant="primary" />
            <p class="mt-1 text-muted mb-0">Loading team analytics…</p>
          </div>
          <my-calls-team-table
            v-else-if="teamAnalytics.length"
            :team-analytics="teamAnalytics"
            :view-mode="viewMode"
          />
          <b-card v-else class="text-center py-4 text-muted">
            No team analytics data for the selected period.
          </b-card>
        </div>
      </b-tab>
      <b-tab v-if="viewMode !== 'my'">
        <template #title>
          <feather-icon icon="CalendarIcon" size="13" class="mr-25 d-none d-sm-inline" />
          <span class="d-none d-sm-inline">Datewise Performance</span>
          <span class="d-sm-none">Datewise</span>
        </template>
        <div class="pt-1">
          <div v-if="isLoadingAggregations" class="text-center py-4">
            <b-spinner variant="primary" />
            <p class="mt-1 text-muted mb-0">Loading datewise analytics…</p>
          </div>
          <my-calls-datewise-table
            v-else-if="datewiseAnalytics.length"
            :datewise-analytics="datewiseAnalytics"
          />
          <b-card v-else class="text-center py-4 text-muted">
            No datewise analytics data for the selected period.
          </b-card>
        </div>
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon icon="PhoneCallIcon" size="13" class="mr-25 d-none d-sm-inline" />
          <span class="d-none d-sm-inline">Call History</span>
          <span class="d-sm-none">History</span>
        </template>
        <div class="pt-1">
          <!-- My view: highlighted personal quality summary + history table -->
          <template v-if="viewMode === 'my'">
            <my-calls-history-table
              ref="historyTableRef"
              :filtered-calls="historyTableCalls"
              :server-total="historyServerTotal"
              :api-total="callAnalyticsTotalRecords"
              :loaded-count="analyzedOnCurrentPageCount"
              :period-analyzed-count="historyPeriodAnalyzedCount"
              :period-total-count="historyPeriodTotalCount"
              :period-counts-loading="historyPeriodCountsLoading"
              :client-paged="historyClientPaginationActive"
              :has-more="hasMoreCalls"
              :is-fetching-more="isFetchingMore"
              :fields="fields"
              :is-loading="isLoading || historySearchLoading"
              :per-page.sync="perPage"
              :current-page.sync="currentPage"
              :analysis-filter="appliedFilters.callAnalysis"
              :analysis-filter-client-only="analysisFilterClientOnly"
              :calling-row-id="callingRowId"
              :call-outcome-by-row-key="callOutcomeByRowKey"
              :call-notes="callNotes"
              :candidate-map="candidateMap"
              :job-map="jobMap"
              :recruiter-name-map="recruiterNameMap"
              :show-recruiter="showRecruiterInHistory"
              :date-range-label="historyDateRangeLabel"
              :search-query="historySearchQuery"
              @analysis-filter-change="onAnalysisFilterChange"
              @search-change="onHistorySearchChange"
              @call-again="confirmCall"
              @call-candidate="confirmCall"
              @open-note="openNoteModal"
              @open-add-to-ats="openAddToATS"
              @open-reminder="openReminderModal"
            />
          </template>

          <!-- Team/Org: keep both panes mounted — lazy remounts Analysis and re-drains. -->
          <b-tabs v-else v-model="historyTabIndex" class="history-inner-tabs">
            <b-tab>
              <template #title>
                <feather-icon icon="ListIcon" size="13" class="mr-25 d-none d-sm-inline" />
                <span class="d-none d-sm-inline">Call History</span>
                <span class="d-sm-none">History</span>
              </template>
              <my-calls-history-table
                ref="historyTableRef"
                :filtered-calls="historyTableCalls"
                :server-total="historyServerTotal"
                :api-total="callAnalyticsTotalRecords"
                :loaded-count="analyzedOnCurrentPageCount"
                :period-analyzed-count="historyPeriodAnalyzedCount"
                :period-total-count="historyPeriodTotalCount"
                :period-counts-loading="historyPeriodCountsLoading"
                :client-paged="historyClientPaginationActive"
                :has-more="hasMoreCalls"
                :is-fetching-more="isFetchingMore"
                :fields="fields"
                :is-loading="isLoading || historySearchLoading"
                :per-page.sync="perPage"
                :current-page.sync="currentPage"
                :analysis-filter="appliedFilters.callAnalysis"
                :analysis-filter-client-only="analysisFilterClientOnly"
                :calling-row-id="callingRowId"
                :call-outcome-by-row-key="callOutcomeByRowKey"
                :call-notes="callNotes"
                :candidate-map="candidateMap"
                :job-map="jobMap"
                :recruiter-name-map="recruiterNameMap"
                :show-recruiter="showRecruiterInHistory"
                :date-range-label="historyDateRangeLabel"
                :search-query="historySearchQuery"
                @analysis-filter-change="onAnalysisFilterChange"
                @search-change="onHistorySearchChange"
                @call-again="confirmCall"
                @call-candidate="confirmCall"
                @open-note="openNoteModal"
                @open-add-to-ats="openAddToATS"
                @open-reminder="openReminderModal"
              />
            </b-tab>
            <b-tab>
              <template #title>
                <feather-icon icon="CpuIcon" size="13" class="mr-25 d-none d-sm-inline" />
                <span class="d-none d-sm-inline">Analysis Summary</span>
                <span class="d-sm-none">Analysis</span>
              </template>
              <my-calls-analysis-report
                layout="analysis-panel"
                :analyzed-calls="callsWithAnalysis"
                :recruiter-name-map="recruiterNameMap"
                :view-mode="viewMode"
                :is-loading="callQualityLoading"
                :period-start="appliedFilters.startDate"
                :period-end="appliedFilters.endDate"
              />
            </b-tab>
          </b-tabs>
        </div>
      </b-tab>
      <b-tab v-if="viewMode !== 'my'">
        <template #title>
          <feather-icon icon="ClockIcon" size="13" class="mr-25 d-none d-sm-inline" />
          <span class="d-none d-sm-inline">Hourly Report</span>
          <span class="d-sm-none">Hourly</span>
        </template>
        <div class="pt-1">
          <div v-if="isLoadingAggregations" class="text-center py-4">
        <b-spinner variant="primary" />
            <p class="mt-1 text-muted mb-0">Loading hourly report…</p>
      </div>
          <my-calls-hourly-table
            v-else-if="hourlyAnalytics.length"
            :hourly-analytics="hourlyAnalytics"
          />
          <b-card v-else class="text-center py-4 text-muted">
            No hourly data for the selected period.
          </b-card>
      </div>
      </b-tab>
    </b-tabs>

    <!-- ── Feature 3: Call Note / Tag Modal ── -->
    <b-modal
      v-model="noteModal.show"
      title="Call Note"
      ok-title="Save Note"
      cancel-title="Close"
      @ok="saveNote"
      @hidden="resetNoteForm"
    >
      <!-- Read-only preview when note exists -->
      <b-alert v-if="noteForm.outcome || noteForm.note" show variant="light" class="mb-1 p-1">
        <div class="d-flex align-items-center justify-content-between mb-25">
          <b-badge :variant="getNoteVariant(noteForm.outcome)">
            <feather-icon icon="BookmarkIcon" size="10" class="mr-25" />
            {{ noteForm.outcome || 'No tag' }}
          </b-badge>
          <small v-if="noteModal.item && callNotes[getCallSid(noteModal.item)] && callNotes[getCallSid(noteModal.item)].savedAt" class="text-muted">
            <feather-icon icon="ClockIcon" size="10" class="mr-25" />
            {{ new Date(callNotes[getCallSid(noteModal.item)].savedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) }}
          </small>
          </div>
        <p v-if="noteForm.note" class="mb-0 small" style="font-style:italic;">"{{ noteForm.note }}"</p>
      </b-alert>

      <hr class="my-1" />
      <p class="small text-muted mb-1">Edit note below:</p>

      <div class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Outcome Tag</label>
        <b-form-select
          v-model="noteForm.outcome"
          :options="noteOutcomeOptions"
        />
      </div>
      <div>
        <label class="d-block small font-weight-bold mb-50">Note <small class="text-muted">(max 200 chars)</small></label>
        <b-form-textarea
          v-model="noteForm.note"
          rows="3"
          maxlength="200"
          placeholder="Add a short note..."
        />
        <small class="text-muted">{{ (noteForm.note || '').length }}/200</small>
      </div>
    </b-modal>

    <!-- ── Feature 4: Reminder Modal ── -->
    <b-modal
      v-model="reminderModal.show"
      title="Set Callback Reminder"
      ok-title="Set Reminder"
      cancel-title="Cancel"
      @ok="saveReminder"
    >
      <p v-if="reminderModal.item" class="mb-1">
        Remind me to call
        <strong>{{ getCandidateName(reminderModal.item) }}</strong>
        <span v-if="reminderModal.remindAt"> at {{ formatReminderTime(reminderModal.remindAt) }}</span>
      </p>
          <div>
        <label class="d-block small font-weight-bold mb-50">Date &amp; Time</label>
        <b-form-input
          v-model="reminderModal.remindAt"
          type="datetime-local"
        />
          </div>
    </b-modal>

    <!-- Post-dial outcome (matches Job View dialer — no number lookup) -->
    <call-post-dial-modal
      :show.sync="postDialModal.show"
      :phone="postDialModal.phone"
      :candidate-name="postDialModal.candidateName"
      :candidate-id="postDialModal.candidateId"
      :job-id="postDialModal.jobId"
      :job-category="postDialModal.jobCategory"
      :job-vertical="postDialModal.jobVertical"
      :has-candidate-job="postDialModal.hasCandidateJob"
      :current-status="postDialModal.currentStatus"
      :status-flow-context="postDialModal.statusFlowContext"
      :saving="postDialSaving"
      @outcome="onPostDialOutcome"
      @dismissed="onPostDialDismissed"
    />

    <!-- (Status update removed — opens candidate profile instead) -->
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BRow,
  BCol,
  BButton,
  BButtonGroup,
  BBadge,
  BSpinner,
  BFormSelect,
  BFormInput,
  BFormTextarea,
  BModal,
  BAlert,
  BListGroup,
  BListGroupItem,
  BTabs,
  BTab,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
} from 'bootstrap-vue'
import { saveAs } from 'file-saver'
import MyCallsFilters from './components/MyCallsFilters.vue'
import {
  DEFAULT_DATE_PRESET,
  DEFAULT_JOB_CATEGORIES,
  DEFAULT_JOB_VERTICALS,
  describeWideRangeFilterError,
  last7InclusiveStart,
  buildCandidateStatusFilterOptions,
  callMatchesCandidateStatusFilter,
  callMatchesClientTarget,
  callMatchesDirectionFilter,
  callMatchesStatusFilter,
  callMatchesVerticalCategory,
  isActiveEmployeeStatus,
  resolveCallCandidateStatus,
  resolveCallJobCategory,
  resolveCallJobVertical,
  resolveClientFilterTarget,
  trackerLineMatchesClientTarget,
} from './utils/call-filter-scope'
import MyCallsKpiCards from './components/MyCallsKpiCards.vue'
import MyCallsTeamTable from './components/MyCallsTeamTable.vue'
import MyCallsDatewiseTable from './components/MyCallsDatewiseTable.vue'
import MyCallsHistoryTable from './components/MyCallsHistoryTable.vue'
import MyCallsHourlyTable from './components/MyCallsHourlyTable.vue'
import MyCallsAnalysisReport from './components/MyCallsAnalysisReport.vue'
import CallPostDialModal from './components/CallPostDialModal.vue'
import RecruiterCallingTracking from './components/RecruiterCallingTracking.vue'
import {
  buildCallNotesOnlyPayload,
  buildCallOutcomeStatusPayload,
  evaluateCallSurfaceStatusWrite,
  resolveJobPipelineStatus,
} from '@/utils/call-outcome-helpers'
import { statusFlowContextFromJob } from '@/utils/candidate-status-flow'
import axios from 'axios'
import candidateService from '@/services/candidate-service'
import callAnalyticsService, { isPresentKpiCount, PERIOD_CACHE_TTL_MS } from '@/services/call-analytics-service'
import clientService from '@/services/client-service'
import reportService from '@/services/report-service'
import adminConfigService from '@/services/admin-config-service'
import userService from '@/services/user-service'
import jobService from '@/services/job-service'
import {
  buildExotelCallPayload,
  isExotelCallSuccess,
  getExotelCallErrorMessage,
  resolveCallerId,
  resolveCallRowPhone,
  isDialableIndianPhone,
  isTelephonyConnectedCall,
} from '@/utils/exotel-call-helpers'
import {
  loadAgentAvailability,
  setAgentAvailability,
  getCallsAvailableOrDefault,
  onAvailabilityChange,
  CALLS_UNAVAILABLE_MESSAGE,
} from '@/utils/exotel-agent-availability'
import { pickS3RecordingUrl, pickVendorRecordingUrl, pickRecordingS3Key, isExplicitlyUnavailableRecording } from '@/utils/call-recording-helpers'
import { buildNaAgingSuppressedKeys, excludeNaAgedCandidates } from '@/utils/call-na-aging'
import {
  isInboundMissed,
  isOutboundNotAnswered,
  isStartCallingUnavailableCall,
  callerUniqueKey,
  latestCallerStateBuckets,
} from '@/utils/ai-calling-tracking'
import {
  hasCompletedAnalysis,
  applyHistoryAnalysisFilter,
  getCallTypeTag,
  normalizeDirection,
  isInboundDirection,
  parseAnalysis,
  getHygieneScore,
  formatScorePercent,
  formatInterestLabel,
  formatOutcomeLabel,
  getCallQuality,
  getRedFlags,
  formatQualityLevel,
  getDisplayDirection,
  groupAnalysisByRecruiter,
  resolveRecruiterDisplayName,
  stripMappedCallTranscripts,
  slimAnalysisToListMeta,
  slimMappedListRow,
  pickCallRowDate,
  pickExplicitCallRowDate,
  parseCallRowTimestamp,
  pageMissingCallDates,
  mergeFatCallDates,
  formatHistoryDateIst,
  sortCallsNewestFirst,
} from '@/utils/call-analysis-helpers'

// IST-aware date helpers — mirrors the Asia/Kolkata calendar-day logic used by
// MyCallsFilters.vue's date-range presets, so the default last-7-days filter
// (and its initial data fetch) line up with the same "today" end boundary
// regardless of the browser's local timezone.
const IST_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
const IST_HOUR_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  hour12: false,
  timeZone: 'Asia/Kolkata',
})

function yieldToMain() {
  return new Promise(resolve => setTimeout(resolve, 0))
}

function getISTDateParts(d = new Date()) {
  const parts = IST_DAY_FMT.formatToParts(d)
  const map = {}
  parts.forEach(p => { map[p.type] = p.value })
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day) }
}

function istDateObj(d = new Date()) {
  const { year, month, day } = getISTDateParts(d)
  return new Date(Date.UTC(year, month - 1, day))
}

function fmt(d) {
  return d.toISOString().slice(0, 10)
}

function todayStr() {
  return fmt(istDateObj())
}

function last7StartStr() {
  return last7InclusiveStart(todayStr())
}

function defaultDateFilter() {
  return {
    datePreset: DEFAULT_DATE_PRESET,
    startDate: last7StartStr(),
    endDate: todayStr(),
  }
}

/** Asia/Kolkata calendar day (YYYY-MM-DD) for a mapped call timestamp. */
function istCalendarDay(rawDate) {
  const d = parseISTDate(rawDate)
  if (!d || Number.isNaN(d.getTime())) return ''
  try {
    return IST_DAY_FMT.format(d)
  } catch (e) {
    return ''
  }
}

function periodCoversDay(start, end, day) {
  return Boolean(start && end && day && start <= day && end >= day)
}

/**
 * Days in [widgetStart, widgetEnd] (IST last 7) the page date filter does not cover.
 * Returns null when the period already includes the full widget window.
 */
function missedWidgetMissingRange(periodStart, periodEnd, widgetStart, widgetEnd) {
  if (!widgetStart || !widgetEnd) return null
  if (
    periodCoversDay(periodStart, periodEnd, widgetStart)
    && periodCoversDay(periodStart, periodEnd, widgetEnd)
  ) return null
  return { startDate: widgetStart, endDate: widgetEnd }
}

/** Call time for latest-call-wins + newest-first lists. */
function callTimestamp(c) {
  if (!c) return 0
  if (typeof c._ts === 'number' && c._ts) return c._ts
  const d = parseISTDate(c._rawDate)
  return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0
}

/** Merge period + extra widget rows. Keep dateless inbound misses (received while away). */
function mergeUnfilteredCallScope(periodRows, extraRows, start, end) {
  const seen = new Set()
  const merged = []
  const list = (Array.isArray(periodRows) ? periodRows : [])
    .concat(Array.isArray(extraRows) ? extraRows : [])
  list.forEach(c => {
    const day = c._istDay || istCalendarDay(c._rawDate)
    if (day) {
      if (start && day < start) return
      if (end && day > end) return
    } else if (!isInboundMissed(c)) {
      return
    }
    if (isSeenCallRow(seen, c)) return
    markSeenCallRow(seen, c)
    merged.push(c)
  })
  return merged
}

function sortNewestCallFirst(a, b) {
  return callTimestamp(b) - callTimestamp(a)
}

/**
 * Latest inbound-miss per caller (exclusive of later outbound NA or answer).
 * After a connect, a newer inbound miss returns to Missed. Session Call Back
 * dismiss still drops that row until the live set refreshes.
 */
function unresolvedMissedInbound(scopeCalls, calledBackKeys) {
  const backed = calledBackKeys instanceof Set
    ? calledBackKeys
    : new Set(Array.isArray(calledBackKeys) ? calledBackKeys : [])
  const buckets = latestCallerStateBuckets(scopeCalls, {
    keyFn: missedCallerUniqueKey,
    timeFn: callTimestamp,
  })
  return Array.from(buckets.missedByCaller.values())
    .filter(c => {
      const key = c.rowKey || c.sid || c.phone
      return !(key && backed.has(key))
    })
    .sort(sortNewestCallFirst)
}

const MY_CALLS_FILTERS_KEY = 'myCalls_filters'

function formatDuration(seconds) {
  const s = parseInt(seconds, 10)
  if (!s || Number.isNaN(s)) return '—'
  const m = Math.floor(s / 60)
  const rem = s % 60
  if (m > 0) return `${m}m ${rem}s`
  return `${rem}s`
}

/** Strip leading country code and return a clean 10-digit Indian mobile number */
function cleanPhone(raw) {
  if (!raw || raw === '—') return raw
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2)
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1)
  return digits || raw
}

/** Normalize phone for distinct KPI counts — strip non-digits, last 10 digits for Indian numbers */
function normalizePhoneForUniqueCount(raw) {
  if (!raw || raw === '—') return null
  const cleaned = cleanPhone(raw)
  if (!cleaned || cleaned === '—') return null
  const digits = String(cleaned).replace(/\D/g, '')
  if (!digits) return null
  return digits.length >= 10 ? digits.slice(-10) : digits
}

/**
 * Unique Missed card key: last-10 phone, else candidate_id, else sid / rowKey.
 */
function missedCallerUniqueKey(c) {
  return callerUniqueKey(c)
}

/**
 * Newest row per caller (Missed card + unique KPI counts).
 * History stays every row. Called-back misses must already be dropped before this runs.
 */
function uniqueMissedByCaller(rows) {
  const list = Array.isArray(rows) ? rows : []
  if (list.length < 2) return list
  const best = new Map()
  const leftovers = []
  for (let i = 0; i < list.length; i += 1) {
    const c = list[i]
    const key = missedCallerUniqueKey(c)
    if (!key) {
      leftovers.push(c)
      continue
    }
    const prev = best.get(key)
    if (!prev || callTimestamp(c) > callTimestamp(prev)) best.set(key, c)
  }
  return Array.from(best.values())
    .concat(leftovers)
    .sort((a, b) => callTimestamp(b) - callTimestamp(a))
}

/** Keep newest row per caller key (Org/Team volume buckets). */
function keepNewestCallerRow(bucket, key, call) {
  const prev = bucket.get(key)
  if (!prev || callTimestamp(call) > callTimestamp(prev)) bucket.set(key, call)
}

/**
 * Stable identity for unique Profiles Shared counts.
 * Prefer candidateId, then normalized phone; fall back to call sid/rowKey.
 */
function profileUniqueKey(c) {
  if (!c) return null
  if (c.candidateId != null && c.candidateId !== '') return `c:${c.candidateId}`
  const phone = normalizePhoneForUniqueCount(c.phone)
  if (phone) return `p:${phone}`
  if (c.sid) return `s:${c.sid}`
  if (c.rowKey) return `r:${c.rowKey}`
  return null
}

/** Profile Shared or later pipeline stage (shared+). */
const SHARED_PLUS_STATUSES = new Set([
  'profileshared', 'screenselect', 'shortlisted', 'offered', 'joined',
])

function isSharedPlus(candidateStatus) {
  return SHARED_PLUS_STATUSES.has((candidateStatus || '').toLowerCase().replace(/\s/g, ''))
}

/** Normalize person name for recruiter join (work tracker rec_name ↔ Org Performance). */
function normalizePersonName(name) {
  return String(name || '').toLowerCase().replace(/\s+/g, ' ').trim()
}

/** Parse ProfileShared-like count fields from recruiter tracker rows. */
function parseTrackerCount(value) {
  const n = parseInt(value, 10)
  return Number.isFinite(n) ? n : 0
}

function normalizeStatus(raw) {
  if (!raw) return 'Unknown'
  const lower = String(raw).toLowerCase()
  if (lower === 'completed' || lower === 'answered') return 'Answered'
  if (lower === 'no-answer' || lower === 'no_answer' || lower === 'noanswered' || lower === 'not answered' || lower === 'noanswer') return 'Not Answered'
  if (lower === 'not_rang' || lower === 'not-rang' || lower === 'notrang' || lower === 'missed' || lower === 'missed-call') return 'Missed'
  if (lower === 'busy') return 'Busy'
  if (isFailedStatus(lower)) return 'Failed'
  return String(raw)
}

function statusVariant(raw) {
  if (!raw) return 'secondary'
  const lower = String(raw).toLowerCase()
  if (lower === 'completed' || lower === 'answered') return 'success'
  if (lower === 'no-answer' || lower === 'no_answer' || lower === 'noanswered' || lower === 'not answered' || lower === 'noanswer') return 'warning'
  if (lower === 'not_rang' || lower === 'not-rang' || lower === 'notrang' || lower === 'missed' || lower === 'missed-call') return 'secondary'
  if (lower === 'busy') return 'warning'
  if (isFailedStatus(lower)) return 'danger'
  return 'secondary'
}

function isInbound(raw) {
  return isInboundDirection(raw)
}

// Active-user convention reused from the `allusers` records already consulted
// for recruiter/manager dropdowns — handles the various casings / boolean
// shapes the API has returned for employee status.
function isActiveUser(u) {
  return isActiveEmployeeStatus(u)
}

const NO_ANSWER_STATUSES = new Set([
  'no-answer', 'no_answer', 'noanswered', 'noanswer', 'not answered', 'notanswered',
  'not_answered', 'not_rang', 'not-rang', 'notrang', 'not connected', 'not-connected',
  'notconnected', 'missed', 'missed-call', 'missed_call',
])

const FAILED_STATUSES = new Set([
  'failed', 'temporaryfailed', 'temporary-failed', 'temporary_failed', 'temporary failed',
])

function isNoAnswerStatus(s) {
  return NO_ANSWER_STATUSES.has((s || '').toLowerCase())
}

function isFailedStatus(s) {
  return FAILED_STATUSES.has((s || '').toLowerCase())
}

function isOutboundNoAnswerRow(c) {
  return isOutboundNotAnswered(c)
}

function listHasOutboundNoAnswer(rows) {
  const list = Array.isArray(rows) ? rows : []
  for (let i = 0; i < list.length; i += 1) {
    if (isOutboundNoAnswerRow(list[i])) return true
  }
  return false
}

function listHasInboundMissed(rows) {
  const list = Array.isArray(rows) ? rows : []
  for (let i = 0; i < list.length; i += 1) {
    if (isInboundMissed(list[i])) return true
  }
  return false
}

function isConnectedStatus(s) {
  const lower = (s || '').toLowerCase()
  return lower === 'answered' || lower === 'completed' || lower === 'connected'
    || lower === 'in-progress' || lower === 'in_progress' || lower === 'inprogress'
}

/**
 * Unique KPI buckets — latest call per caller (last-10 phone / candidate_id).
 * Total = any call. Connected / NA / Failed = latest-state (Connected stays
 * after a later inbound miss). NA and Missed never overlap.
 */
function uniqueKpiCallerBuckets(rows) {
  return latestCallerStateBuckets(rows, {
    keyFn: (c, i) => missedCallerUniqueKey(c) || `row:${i}`,
    timeFn: callTimestamp,
  })
}

// Parses a DB timestamp to a JS Date correctly in IST.
// created_at  → "2026-06-26 16:15:18.389 +0530"  (IST, explicit offset) — preferred
// DateCreated → "2026-06-26 10:45:18"             (UTC, no offset, Exotel API time)
function parseISTDate(str) {
  return parseCallRowTimestamp(str)
}

/** Vendor / DB sid aliases — list + analysis-summary payloads do not share one field. */
function pickCallSid(call, rawPayload) {
  if (!call && !rawPayload) return ''
  const payload = rawPayload && typeof rawPayload === 'object' ? rawPayload : {}
  const webhook = payload.webhook_response && payload.webhook_response.data
    ? payload.webhook_response.data
    : {}
  return String(
    (call && (call.sid || call.Sid || call.CallSid || call.callSid || call.call_sid || call.call_Sid))
    || payload.sid || payload.Sid || payload.CallSid || payload.callSid || payload.call_sid
    || webhook.Sid || webhook.CallSid || webhook.sid || webhook.call_sid
    || '',
  ).trim()
}

function pickCallDbId(call) {
  if (!call) return null
  const id = call.callDbId || call.id || call.call_id || call.exotel_call_id
  return id != null && id !== '' ? id : null
}

function isSeenCallRow(seen, row) {
  const sid = String((row && row.sid) || '').trim()
  const id = row && row.callDbId != null && row.callDbId !== '' ? String(row.callDbId) : ''
  if (sid && seen.has(`s:${sid}`)) return true
  if (id && seen.has(`id:${id}`)) return true
  return false
}

function markSeenCallRow(seen, row) {
  const sid = String((row && row.sid) || '').trim()
  const id = row && row.callDbId != null && row.callDbId !== '' ? String(row.callDbId) : ''
  if (sid) seen.add(`s:${sid}`)
  if (id) seen.add(`id:${id}`)
}

function dedupeMappedCalls(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return Array.isArray(rows) ? rows : []
  const seen = new Set()
  const out = []
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]
    if (isSeenCallRow(seen, row)) continue
    markSeenCallRow(seen, row)
    out.push(row)
  }
  return out
}

function mapCallBatch(rawRows) {
  const list = Array.isArray(rawRows) ? rawRows : []
  return dedupeMappedCalls(list.map(c => slimMappedListRow(mapCall(c))))
}

function mapCall(call) {
  let rawPayload = call.raw_payload || {}
  if (typeof rawPayload === 'string') {
    try { rawPayload = JSON.parse(rawPayload) } catch { rawPayload = {} }
  }
  const webhook = rawPayload.webhook_response && typeof rawPayload.webhook_response === 'object'
    ? (rawPayload.webhook_response.data || rawPayload.webhook_response)
    : {}
  const rawDate = pickExplicitCallRowDate(call)
    || pickExplicitCallRowDate(rawPayload)
    || pickExplicitCallRowDate(webhook)
    || pickCallRowDate(call)
    || pickCallRowDate(rawPayload)
    || pickCallRowDate(webhook)
  let date = '—'
  let parsedDate = null
  if (rawDate) {
    try {
      parsedDate = parseISTDate(rawDate)
      if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
        const parts = formatHistoryDateIst(parsedDate)
        date = parts.time ? `${parts.date}, ${parts.time}` : parts.date
      } else {
        date = String(rawDate)
      }
    } catch {
      date = String(rawDate)
    }
  }

  const ctx = rawPayload.context_resolution || {}

  const candidateId = call.candidate_id || call.candidateId || ctx.candidate_id || null
  const candidateName = call.candidate_name || call.candidateName || ctx.candidate_name || '—'
  const candidateIdDisplay = candidateId ? `SHC-${Number(candidateId) + 1000}` : '—'
  const candidateStatus = call.candidate_status
    || ctx.candidate_status
    || ctx.pipeline_status
    || call.current_status
    || ctx.current_status
    || '—'

  const jobId = call.job_id || call.jobId || ctx.job_id || null
  const jobName = call.position_name || call.job_name || call.jobName || rawPayload.position_name || ''
  const clientName = call.client_name || call.clientName
    || (typeof call.client === 'string' && !/^\d+$/.test(String(call.client).trim()) ? call.client : '')
    || ctx.client_name || ctx.client
    || rawPayload.client_name || rawPayload.client
    || ''
  const clientId = call.client_id || call.clientId || call.clientID
    || ctx.client_id || ctx.clientId
    || rawPayload.client_id || rawPayload.clientId
    || (typeof call.client === 'number' || (typeof call.client === 'string' && /^\d+$/.test(String(call.client).trim()))
      ? call.client
      : '')
  const jobCode = jobId ? `SHJ-${jobId}` : '—'

  let rawDir = call.direction
    || call.Direction
    || rawPayload.direction
    || rawPayload.Direction
    || rawPayload.webhook_response?.data?.Direction
    || rawPayload.webhook_response?.data?.direction
    || ''
  const startCallingSkip = isStartCallingUnavailableCall({
    ...call,
    status: call.status || call.Status,
    raw_payload: rawPayload,
  })
  const inboundMiss = startCallingSkip || isInboundMissed({
    ...call,
    status: call.status || call.Status,
    direction: rawDir,
    raw_payload: rawPayload,
  })
  if (inboundMiss && !isInboundDirection(rawDir)) {
    rawDir = 'inbound'
  }
  const directionNorm = normalizeDirection(rawDir)

  const durationSec = parseInt(rawPayload.answer_duration ?? call.duration ?? call.Duration ?? 0, 10) || 0

  const callForPhone = {
    ...call,
    From: call.from_number || call.From || call.from,
    To: call.to_number || call.To || call.to,
    from: call.from_number || call.from,
    to: call.to_number || call.to,
    phone: call.phone || call.candidate_contact_no || call.contact_no,
  }

  const phoneLocal = resolveCallRowPhone(callForPhone, rawPayload, ctx, rawDir)

  const sid = pickCallSid(call, rawPayload)
  const callDbId = pickCallDbId(call)
  const rowKey = sid
    ? `${candidateId || ''}_${jobId || ''}_${sid}`
    : `${candidateId || ''}_${jobId || ''}_${callDbId || ''}`

  let pipelineNotes = call.pipeline_notes || null
  if (typeof pipelineNotes === 'string') {
    try { pipelineNotes = JSON.parse(pipelineNotes) } catch { pipelineNotes = null }
  }

  let callAnalysis = call.call_analysis ?? call.callAnalysis ?? null
  if (callAnalysis == null) {
    callAnalysis = rawPayload.call_analysis ?? rawPayload.callAnalysis ?? null
  }
  if (typeof callAnalysis === 'string') {
    try { callAnalysis = JSON.parse(callAnalysis) } catch { callAnalysis = null }
  }

  const mapped = {
    date,
    candidate: candidateName,
    candidateId,
    candidateIdDisplay,
    phone: phoneLocal || '—',
    phoneDialable: isDialableIndianPhone(phoneLocal),
    email: call.candidate_email || call.email || call.candidateEmail
      || ctx.email || ctx.candidate_email || '',
    job: jobName,
    jobId,
    jobCode,
    clientName,
    clientId: clientId !== '' && clientId != null ? clientId : '',
    candidateStatus,
    durationSec,
    duration: formatDuration(durationSec),
    status: normalizeStatus(call.status || call.Status),
    statusVariant: statusVariant(call.status || call.Status),
    direction: directionNorm,
    _rawDirection: String(rawDir).toLowerCase(),
    _rawStatus: String(call.status || call.Status || '').toLowerCase(),
    _callsAvailableSkip: startCallingSkip,
    _rawDate: rawDate || null,
    _ts: parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate.getTime() : 0,
    _istDay: parsedDate && !Number.isNaN(parsedDate.getTime()) ? IST_DAY_FMT.format(parsedDate) : '',
    _clientNameLower: clientName ? String(clientName).trim().toLowerCase() : '',
    _rawRecruiter: call.recruiter || call.Recruiter || '',
    _recruiterName: call.recruiter_name || '',
    _jobVertical: pickJobFieldFromCall(call, ctx, rawPayload, 'vertical'),
    _jobCategory: pickJobFieldFromCall(call, ctx, rawPayload, 'category'),
    _pipelineNotes: Array.isArray(pipelineNotes) ? pipelineNotes : null,
    recordingUrl: pickS3RecordingUrl(call)
      || pickVendorRecordingUrl(call)
      || pickVendorRecordingUrl({ RecordingUrl: rawPayload.webhook_response?.data?.recording })
      || (isExplicitlyUnavailableRecording(call, rawPayload) ? 'not_available' : null),
    recordingS3Key: pickRecordingS3Key(call, rawPayload),
    sid,
    rowKey,
    callDbId,
    _callAnalysis: slimAnalysisToListMeta(callAnalysis),
    _callAnalysisStatus: call.call_analysis_status || call.callAnalysisStatus || null,
    _callAnalysisAt: call.call_analysis_at || call.callAnalysisAt || null,
  }
  mapped.callTypeTag = getCallTypeTag(mapped)
  mapped._hasAnalysis = hasCompletedAnalysis(mapped)
  return mapped
}

/** Job vertical/category only — never call_analysis.call_outcome.category. */
function pickJobFieldFromCall(call, ctx, rawPayload, kind) {
  if (kind === 'vertical') {
    return String(
      call.job_vertical || call.jobVertical || call.JobVertical
      || (ctx && (ctx.job_vertical || ctx.vertical))
      || (rawPayload && (rawPayload.job_vertical || rawPayload.jobVertical))
      || '',
    ).trim()
  }
  return String(
    call.job_category || call.jobCategory || call.JobCategory
    || (ctx && (ctx.job_category || ctx.category))
    || (rawPayload && (rawPayload.job_category || rawPayload.jobCategory))
    || '',
  ).trim()
}

function jobMetaFromMap(vm, call) {
  const jobId = call && (call.jobId != null ? call.jobId : call.job_id)
  if (jobId == null || !vm.jobMap) return {}
  return vm.jobMap[jobId] || vm.jobMap[String(jobId)] || {}
}

function getUserData() {
  try { return JSON.parse(localStorage.getItem('userData') || '{}') } catch { return {} }
}

function crc32(bytes) {
  let c = ~0
  for (let i = 0; i < bytes.length; i++) {
    c ^= bytes[i]
    for (let j = 0; j < 8; j++) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1)
    }
  }
  return (~c) >>> 0
}

function writeU16(n, arr, offset) {
  arr[offset] = n & 0xff
  arr[offset + 1] = (n >>> 8) & 0xff
}

function writeU32(n, arr, offset) {
  arr[offset] = n & 0xff
  arr[offset + 1] = (n >>> 8) & 0xff
  arr[offset + 2] = (n >>> 16) & 0xff
  arr[offset + 3] = (n >>> 24) & 0xff
}

/** Build a ZIP blob from UTF-8 text files (stored, no compression). */
function createZipBlob(files) {
  const encoder = new TextEncoder()
  const fileParts = []
  const centralParts = []
  let offset = 0

  files.forEach(file => {
    const nameBytes = encoder.encode(file.name)
    const dataBytes = encoder.encode(file.content)
    const crc = crc32(dataBytes)

    const localHeader = new Uint8Array(30 + nameBytes.length)
    writeU32(0x04034b50, localHeader, 0)
    writeU16(20, localHeader, 4)
    writeU16(0, localHeader, 6)
    writeU16(0, localHeader, 8)
    writeU16(0, localHeader, 10)
    writeU16(0, localHeader, 12)
    writeU32(crc, localHeader, 14)
    writeU32(dataBytes.length, localHeader, 18)
    writeU32(dataBytes.length, localHeader, 22)
    writeU16(nameBytes.length, localHeader, 26)
    writeU16(0, localHeader, 28)
    localHeader.set(nameBytes, 30)

    fileParts.push(localHeader, dataBytes)

    const central = new Uint8Array(46 + nameBytes.length)
    writeU32(0x02014b50, central, 0)
    writeU16(20, central, 4)
    writeU16(20, central, 6)
    writeU16(0, central, 8)
    writeU16(0, central, 10)
    writeU16(0, central, 12)
    writeU16(0, central, 14)
    writeU32(crc, central, 16)
    writeU32(dataBytes.length, central, 20)
    writeU32(dataBytes.length, central, 24)
    writeU16(nameBytes.length, central, 28)
    writeU16(0, central, 30)
    writeU16(0, central, 32)
    writeU16(0, central, 34)
    writeU16(0, central, 36)
    writeU32(0, central, 38)
    writeU32(offset, central, 42)
    central.set(nameBytes, 46)
    centralParts.push(central)

    offset += localHeader.length + dataBytes.length
  })

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0)
  const end = new Uint8Array(22)
  writeU32(0x06054b50, end, 0)
  writeU16(0, end, 4)
  writeU16(0, end, 6)
  writeU16(files.length, end, 8)
  writeU16(files.length, end, 10)
  writeU32(centralSize, end, 12)
  writeU32(offset, end, 16)
  writeU16(0, end, 20)

  return new Blob([...fileParts, ...centralParts, end], { type: 'application/zip' })
}

/** Job/client on a mapped call: API `client_name` first, then job / candidate-job fallback. */
function resolveCallClientName(vm, call) {
  if (!call) return ''
  const fromRow = call.clientName || call.client_name || call.client || ''
  if (fromRow) return String(fromRow).trim()
  const jobId = call.jobId || call.job_id
  if (jobId != null && vm.jobMap && vm.jobMap[jobId] && vm.jobMap[jobId].client) {
    return String(vm.jobMap[jobId].client).trim()
  }
  const entry = jobId != null && call.candidateId && vm.candidateMap
    ? vm.candidateMap[call.candidateId]
    : null
  const jobs = entry && Array.isArray(entry.candidateJobs) ? entry.candidateJobs : []
  if (!jobs.length) return ''
  const jobIdStr = String(jobId)
  const job = jobs.find(j => (
    String(j.id) === jobIdStr
    || String(j.job_id) === jobIdStr
    || String(j.jobId) === jobIdStr
  ))
  if (job) {
    const fromJob = job.client || job.client_name || job.clientName || ''
    if (fromJob) return String(fromJob).trim()
  }
  return ''
}

function candidateJobsForCall(vm, call) {
  const jobId = call && (call.jobId != null ? call.jobId : call.job_id)
  const entry = jobId != null && call && call.candidateId && vm.candidateMap
    ? vm.candidateMap[call.candidateId]
    : null
  const jobs = entry && Array.isArray(entry.candidateJobs) ? entry.candidateJobs : []
  if (!jobs.length || jobId == null) return jobs
  const jobIdStr = String(jobId)
  const match = jobs.filter(j => (
    String(j.id) === jobIdStr
    || String(j.job_id) === jobIdStr
    || String(j.jobId) === jobIdStr
  ))
  return match.length ? match : jobs
}

function callMatchesClientFilter(vm, call, target) {
  if (!target || !target.want) return true
  return callMatchesClientTarget(
    call,
    target,
    jobMetaFromMap(vm, call),
    candidateJobsForCall(vm, call),
  )
}

function candidateStatusJobMatch(vm, call) {
  const jobs = candidateJobsForCall(vm, call)
  if (!jobs.length) return null
  const jobId = call && (call.jobId != null ? call.jobId : call.job_id)
  if (jobId == null) return jobs[0] || null
  const jobIdStr = String(jobId)
  return jobs.find(j => (
    String(j.id) === jobIdStr
    || String(j.job_id) === jobIdStr
    || String(j.jobId) === jobIdStr
  )) || null
}

function historySearchText(vm) {
  return (vm.historySearchQuery && String(vm.historySearchQuery).trim())
    || (vm.appliedFilters && vm.appliedFilters.search && String(vm.appliedFilters.search).trim())
    || ''
}

function callMatchesHistorySearch(vm, call, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return true
  const entry = (call.candidateId && vm.candidateMap) ? vm.candidateMap[call.candidateId] : null
  const name = (
    (entry && entry.name)
    || call.candidate
    || call.candidateLabel
    || call.candidate_name
    || ''
  ).toLowerCase()
  const recruiter = (call._recruiterName || call._rawRecruiter || call.recruiter_name || '').toLowerCase()
  const job = (call.job || call.jobTitle || call.position_name || call.jobCode || '').toLowerCase()
  const email = (
    (entry && entry.email)
    || call.email
    || call.candidateEmail
    || call.candidate_email
    || ''
  ).toLowerCase()
  const phones = [
    call.phone,
    call.contactNo,
    call.candidate_contact_no,
    entry && entry.phone,
  ]
  const qDigits = q.replace(/\D/g, '')
  const qLast10 = qDigits.length >= 10 ? qDigits.slice(-10) : qDigits
  const phoneHit = phones.some(raw => {
    if (!raw || raw === '—') return false
    const asText = String(raw).toLowerCase()
    if (asText.includes(q)) return true
    if (!qDigits) return false
    const last10 = normalizePhoneForUniqueCount(raw)
    if (last10 && qLast10 && last10.includes(qLast10)) return true
    const digits = String(raw).replace(/\D/g, '')
    return Boolean(digits && digits.includes(qDigits))
  })
  return name.includes(q)
    || email.includes(q)
    || recruiter.includes(q)
    || job.includes(q)
    || phoneHit
    || String(call.candidateIdDisplay || '').toLowerCase().includes(q)
    || (call.candidateId != null && String(call.candidateId).includes(q))
}

/** History / report / export / unique-KPI search. History table stays every matching row. */
function applyHistorySearchFilter(vm, calls) {
  const list = Array.isArray(calls) ? calls : []
  const q = historySearchText(vm)
  if (!q) return list
  return list.filter(c => callMatchesHistorySearch(vm, c, q))
}

/** Shared client-side filters for paginated rows and the live period set (KPIs, reports). */
function filterCallsList(vm, calls, options = {}) {
  const { skipCallAnalysis = false } = options
  let result = Array.isArray(calls) ? calls : []
  const af = vm.appliedFilters

  // Date range, emp_id, and mgr_id are applied server-side by get_enriched_call_logs.
  // Do not re-filter those client-side (timezone parsing and empty users/* caches
  // drop rows and make Apply look like a no-op until a second click).
  // Client has no reliable call-analytics query param — always refine the live
  // Team / My / History / KPI rows by name or id (jobMap + row.client*).
  // History toolbar search is applied in historyTableCalls + unique KPI aggregation.

  // Direction before any inbound-missed keep (vertical). Do not pass missed inbound
  // just because they are inbound — Outbound is outbound/outgoing/out/click-to-call only.
  if (af.direction && af.direction !== 'all') {
    result = result.filter(c => callMatchesDirectionFilter(c, af.direction))
  }

  if (af.client && af.client !== 'all') {
    const target = resolveClientFilterTarget(af.client, vm.clientsList)
    result = result.filter(c => callMatchesClientFilter(vm, c, target))
  }

  if (af.status && af.status !== 'all') {
    // Answered = connected/completed/answered only. Inbound NA / missed must not pass.
    result = result.filter(c => callMatchesStatusFilter(c, af.status))
  }

  if (af.candidateStatus && af.candidateStatus !== 'all') {
    result = result.filter(c => callMatchesCandidateStatusFilter(
      c,
      af.candidateStatus,
      candidateStatusJobMatch(vm, c),
    ))
  }

  // emp_id / mgr_id / My-Team-Org are applied server-side in buildCallAnalyticsParams.
  // Do not re-filter those here — allusers / team-members are loaded only when
  // Filters opens, so a client re-filter emptied the table until a second Apply.

  if ((af.vertical && af.vertical !== 'all') || (af.category && af.category !== 'all')) {
    result = result.filter(c => isInboundMissed(c) || callMatchesVerticalCategory(c, af, jobMetaFromMap(vm, c)))
  }

  if (!skipCallAnalysis) {
    result = applyHistoryAnalysisFilter(af.callAnalysis, result)
  }

  // Display-only: 7 distinct IST NA days → hide candidate from day 8+ until Answered.
  if (!options.skipNaAging) {
    const keys = vm.naAgingSuppressedKeySet
    if (keys && keys.size) {
      result = excludeNaAgedCandidates(result, keys)
    }
  }

  return result
}

function applyKpiFilter(kpiFilter, calls) {
  const list = Array.isArray(calls) ? calls : []
  if (kpiFilter === 'all') return list
  if (kpiFilter === 'connected') {
    return list.filter(c => isConnectedStatus(c._rawStatus))
  }
  if (kpiFilter === 'not-answered') {
    return list.filter(c => isNoAnswerStatus(c._rawStatus))
  }
  if (kpiFilter === 'failed') {
    return list.filter(c => isFailedStatus(c._rawStatus))
  }
  if (kpiFilter === 'missed') {
    return list.filter(c => isInboundMissed(c))
  }
  return list
}

export default {
  name: 'MyCalls',

  components: {
    BCard,
    BCardHeader,
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BBadge,
    BSpinner,
    BFormSelect,
    BFormInput,
    BFormTextarea,
    BModal,
    BAlert,
    BListGroup,
    BListGroupItem,
    BTabs,
    BTab,
    BDropdown,
    BDropdownItem,
    MyCallsFilters,
    MyCallsKpiCards,
    MyCallsTeamTable,
    MyCallsDatewiseTable,
    MyCallsHistoryTable,
    MyCallsHourlyTable,
    MyCallsAnalysisReport,
    CallPostDialModal,
    RecruiterCallingTracking,
    BFormCheckbox,
  },

  data() {
    const dates = defaultDateFilter()
    return {
      allCalls: [],
      /** Server total from call-analytics `totalRecords` / `total_count` (not loaded-row length). */
      callAnalyticsTotalRecords: 0,
      /**
       * True when Call Analysis filter was sent as analyzed= but the API ignored it
       * (page still contains opposite rows / unfiltered total). Client filters the page only.
       */
      analysisFilterClientOnly: false,
      isLoading: true,
      /** True while fetching a non-initial page (pagination click). */
      isFetchingMore: false,
      /** Cap alert-panel preview lists; View All opens filtered Call History. */
      alertPreviewLimit: 20,
      /** 1-based API page currently shown (classic pagination — replace, not append). */
      currentPage: 1,
      /** Page size for call-analytics fetches (= API limit). */
      perPage: 50,
      /** Last successfully fetched API page (guards pagination watcher from double-fetch). */
      _loadedAnalyticsPage: 0,
      /** Suppress currentPage watcher while syncing page after a fetch. */
      _pagingSilent: false,
      /**
       * Monotonic fetch id — stale responses must not clear loading or overwrite data.
       * NOTE: must NOT use a leading underscore — Vue 2 does not proxy data._* onto `this`,
       * so `this.fetchGeneration += 1` became NaN and every response looked stale forever.
       */
      fetchGeneration: 0,
      /** In-flight live period drain — export awaits this instead of the current page. */
      periodCacheInflight: null,
      /** True while Report Download is paging all filtered rows. */
      isExporting: false,
      /** Call History tab search — filters the live period set client-side. */
      historySearchQuery: '',
      /**
       * Live period rows (dates + emp + mgr, not analyzed).
       * 5-minute in-memory TTL; date / recruiter / view change uses a new key.
       */
      searchCallsCache: null,
      isFetchingSearchCache: false,
      /** Filter key (viewer + view + dates + emp + mgr) the live period set was built for. */
      periodCacheKey: '',
      periodCacheFetchingKey: '',
      periodCacheFetchGen: 0,
      /** When this complete drain was stored (ms). Used for the 5-minute TTL. */
      periodCacheFetchedAt: 0,
      /** Unfiltered API `total` for the live period scope (all pages). */
      periodTotalRecords: 0,
      periodCacheComplete: false,
      /**
       * API 1 KPI breakdown (counts only) for the current date + emp/mgr scope.
       * Prefer these over paging unique phones. Null until the backend ships them.
       */
      periodSummaryCounts: null,
      periodSummaryCountsKey: '',
      /**
       * Scope key of the last finished live drain (complete or not).
       * Not used to skip a refetch after date / recruiter / search change.
       */
      periodCacheDrainKey: '',
      /**
       * Request-scoped History search working set. Discarded when filters change.
       */
      historySearchCache: null,
      isFetchingHistorySearch: false,
      historySearchFetchGen: 0,
      /**
       * Analysis-agnostic rows for Not Answered / Missed cards.
       * Never replaced by analyzed=true|false history fetches.
       */
      alertCallsCache: null,
      alertCallsScopeKey: '',
      alertCallsFetchGen: 0,
      isFetchingAlertCalls: false,
      alertCallsFetchingScope: '',
      /**
       * Extra call-analytics rows so the Missed widget can cover last 7 IST days
       * when the page date filter does not already include that window.
       */
      missedWidgetCache: null,
      missedWidgetCacheKey: '',
      missedWidgetFetchingKey: '',
      missedWidgetFetchGen: 0,
      isFetchingMissedWidget: false,
      missedWidgetCacheComplete: false,
      missedWidgetCacheStart: '',
      missedWidgetCacheEnd: '',
      /** Skip one viewMode watcher fetch after mount (Vue 2 deferred flush duplicates mount fetch). */
      _skipNextViewModeFetch: false,
      /** Failsafe timer — force-clear spinner if call-analytics never settles. */
      _analyticsLoadingGuard: null,
      // Visit-scoped enrichment maps — never restored from sessionStorage.
      candidateMap: {},
      jobMap: {},
      phoneLookupCache: {},
      enrichFetchGen: 0,
      kpiFilter: 'all',
      tabIndex: 0,
      historyTabIndex: 0,
      calledBackKeys: [],
      recruitersList: [],
      /** GET /api/jobs/client — same master list as jobs / invoice filters. */
      clientsList: [],
      /** GET /admin/config/jobs — Service/Infra + White/Blue (not invoice types). */
      jobsConfigVerticals: [...DEFAULT_JOB_VERTICALS],
      jobsConfigCategories: [...DEFAULT_JOB_CATEGORIES],
      /** Reactive users/all copy for recruiter/manager dropdowns. */
      allUsersList: [],
      teamRecruiters: [],
      viewMode: 'team',
      apiRecruiterNames: {},
      _viewModeReady: false,
      _allUsersReady: false,
      /** True after Filters modal first opens — users/* loaded on demand, not mount. */
      _teamContextLoaded: false,
      _jobsConfigLoaded: false,

      filters: {
        ...dates,
        status: 'all',
        direction: 'all',
        search: '',
        recruiter: 'all',
        manager: 'all',
        client: 'all',
        candidateStatus: 'all',
        callAnalysis: 'all',
        vertical: 'all',
        category: 'all',
      },

      appliedFilters: {
        ...dates,
        status: 'all',
        direction: 'all',
        search: '',
        recruiter: 'all',
        manager: 'all',
        client: 'all',
        candidateStatus: 'all',
        callAnalysis: 'all',
        vertical: 'all',
        category: 'all',
      },

      // Feature 2: click-to-call in-progress tracker
      callingRowId: null,
      callOutcomeByRowKey: {},
      postDialModal: {
        show: false,
        item: null,
        phone: '',
        candidateName: '',
        candidateId: null,
        jobId: null,
        jobCategory: '',
        jobVertical: '',
        hasCandidateJob: false,
        currentStatus: '',
        statusFlowContext: {},
      },
      postDialSaving: false,

      // Feature 3: call notes
      callNotes: {},
      noteModal: {
        show: false,
        item: null,
      },
      noteForm: {
        outcome: '',
        note: '',
      },
      noteOutcomeOptions: Object.freeze([
        { value: '', text: '— Select outcome —' },
        { value: 'Interested', text: 'Interested' },
        { value: 'Not Interested', text: 'Not Interested' },
        { value: 'Callback', text: 'Callback' },
        { value: 'Wrong Number', text: 'Wrong Number' },
        { value: 'No Answer - Follow Up', text: 'No Answer - Follow Up' },
      ]),

      // Feature 4: reminders
      reminderModal: {
        show: false,
        item: null,
        remindAt: '',
      },
      reminderInterval: null,

      // Exotel DID + agent availability (unknown → treat as available until GET)
      exotelConfig: null,
      callsAvailable: getCallsAvailableOrDefault(),
      callsAvailableSaving: false,
      _availabilityUnsub: null,

      // Tracks reminders explicitly dismissed by the user (key = `${candidateId}_${remindAt}_${phone}`)
      dismissedReminders: {},
      callingTrackingCount: 0,
      attentionNotAnswered: [],
      attentionMissed: [],
      attentionNotAdded: [],
      attentionFlagged: [],
      attentionScope: '',
      attentionRefreshKey: 0,

      // Work-tracker lines (re-aggregated when Client changes — not a long-lived call cache)
      recruiterTrackerRaw: [],

    }
  },

  computed: {
    canManageCalls() {
      return this.$can('manage', 'call_analytics')
    },

    activeFilterCount() {
      const f = this.appliedFilters
      let count = 0
      if (f.recruiter && f.recruiter !== 'all') count++
      if (f.manager && f.manager !== 'all') count++
      if (f.client && f.client !== 'all') count++
      if (f.candidateStatus && f.candidateStatus !== 'all') count++
      if (f.status && f.status !== 'all') count++
      if (f.direction && f.direction !== 'all') count++
      if (f.datePreset && f.datePreset !== DEFAULT_DATE_PRESET) count++
      if (f.vertical && f.vertical !== 'all') count++
      if (f.category && f.category !== 'all') count++
      if (historySearchText(this)) count++
      return count
    },

    clientFilterActive() {
      const c = this.appliedFilters && this.appliedFilters.client
      return Boolean(c && c !== 'all')
    },

    /**
     * Client-side-only filters (no call-analytics query param).
     * Date / recruiter / manager / analyzed stay server-side.
     */
    hasExtraListFilters() {
      const af = this.appliedFilters || {}
      if (af.client && af.client !== 'all') return true
      if (af.candidateStatus && af.candidateStatus !== 'all') return true
      if (af.status && af.status !== 'all') return true
      if (af.direction && af.direction !== 'all') return true
      if (af.vertical && af.vertical !== 'all') return true
      if (af.category && af.category !== 'all') return true
      return false
    },

    verticalFilterOptions() {
      return [
        { value: 'all', text: 'All Verticals' },
        ...this.jobsConfigVerticals.map(v => ({ value: v, text: v })),
      ]
    },

    categoryFilterOptions() {
      return [
        { value: 'all', text: 'All Categories' },
        ...this.jobsConfigCategories.map(v => ({ value: v, text: v })),
      ]
    },

    candidateStatusFilterOptions() {
      const extras = []
      ;(Array.isArray(this.allCalls) ? this.allCalls : []).forEach((c) => {
        extras.push(resolveCallCandidateStatus(c, candidateStatusJobMatch(this, c)))
      })
      extras.push(this.filters && this.filters.candidateStatus !== 'all' ? this.filters.candidateStatus : '')
      extras.push(this.appliedFilters && this.appliedFilters.candidateStatus !== 'all'
        ? this.appliedFilters.candidateStatus
        : '')
      return buildCandidateStatusFilterOptions(extras)
    },

    clientOptions() {
      const seen = new Set()
      const names = []
      const add = name => {
        const n = String(name || '').trim()
        if (!n) return
        const key = n.toLowerCase()
        if (seen.has(key)) return
        seen.add(key)
        names.push(n)
      }
      ;(Array.isArray(this.clientsList) ? this.clientsList : []).forEach(c => {
        if (typeof c === 'string') add(c)
        else add(c && (c.client_name || c.name || c.title || c.client))
      })
      // Current page only — do not scan the full period set for dropdown names.
      ;(Array.isArray(this.allCalls) ? this.allCalls : []).forEach(c => add(c && (c.clientName || c.client_name)))
      add(this.filters && this.filters.client !== 'all' ? this.filters.client : '')
      add(this.appliedFilters && this.appliedFilters.client !== 'all' ? this.appliedFilters.client : '')
      names.sort((a, b) => a.localeCompare(b))
      return [
        { value: 'all', text: 'All Clients' },
        ...names.map(n => ({ value: n, text: n })),
      ]
    },

    /** True when more API pages exist after the current classic page. */
    hasMoreCalls() {
      const total = Number(this.callAnalyticsTotalRecords) || 0
      if (!total) return false
      return this.currentPage * this.perPage < total
    },

    /**
     * Rows matching Call Analysis on the current fetched page (after client filter).
     * Used for "on this page" chrome so we never show raw API page size as analyzed count.
     */
    analyzedOnCurrentPageCount() {
      if (!Array.isArray(this.allCalls) || !this.allCalls.length) return 0
      return applyHistoryAnalysisFilter(
        this.appliedFilters.callAnalysis,
        filterCallsList(this, this.allCalls, { skipCallAnalysis: true }),
      ).length
    },

    historyAnalysisFilterActive() {
      return this.appliedFilters.callAnalysis === 'analyzed'
        || this.appliedFilters.callAnalysis === 'not_analyzed'
    },

    /** Client-page the full filtered period (search / extra filters / analysis / KPI chips). */
    historyClientPaginationActive() {
      return this.kpiFilter === 'missed'
        || this.kpiFilter === 'not-answered'
        || this.historyAnalysisFilterActive
        || this.historyNeedsFullPeriodSet
    },

    /** History uses the live period set once extra filters / search need more than this page. */
    historyNeedsFullPeriodSet() {
      if (this.historySearchActive) return true
      // Client / status / direction / candidateStatus / vertical / category must page the live
      // filtered Team + My list — not unfiltered API pages (that looked like a no-op).
      if (this.hasExtraListFilters) return true
      const cacheKey = this._alertScopeKey()
      const cacheReadyForScope = this.periodCacheKey === cacheKey && Array.isArray(this.searchCallsCache)
      const wantsPeriod = this.historyAnalysisFilterActive
        || (this.kpiFilter && this.kpiFilter !== 'all')
      if (!wantsPeriod) return false
      if (this.periodCacheReady) return true
      return cacheReadyForScope
    },

    /**
     * Full date + My/Team/Org + emp/mgr rows (same live set as KPI cards).
     * Never the current page or an analyzed-only history fetch.
     */
    historyPeriodBaseCalls() {
      return this.periodScopeCalls
    },

    /** M in "N analyzed / M total" — full filtered period, not analyzed-only API total. */
    historyPeriodTotalCount() {
      if (this.periodCacheReady) return this.periodScopeCalls.length
      if (this.hasExtraListFilters || (this.naAgingSuppressedKeySet && this.naAgingSuppressedKeySet.size > 0)) {
        if (this.periodCacheKey === this._alertScopeKey() && Array.isArray(this.searchCallsCache)) {
          return this.periodScopeCalls.length
        }
        const n = this.periodScopeCalls.length
        return n > 0 ? n : null
      }
      const periodTotal = Number(this.periodTotalRecords) || 0
      if (periodTotal > 0) return periodTotal
      if (!this.historyAnalysisFilterActive) {
        return Number(this.callAnalyticsTotalRecords) || this.periodScopeCalls.length
      }
      return this.periodScopeCalls.length
    },

    /** N in "N analyzed / M total" — analyzed rows in the live period set, not this page. */
    historyPeriodAnalyzedCount() {
      if (!this.periodCacheReady) return null
      return this.analyzedPeriodCalls.length
    },

    historyPeriodCountsLoading() {
      return !this.periodCacheReady && this.isFetchingSearchCache
    },

    extraFilterTotalLabel() {
      const n = this.historyPeriodTotalCount
      if (n == null) return '…'
      return Number(n).toLocaleString('en-IN')
    },

    /**
     * Total passed to Call History pagination/badge.
     * With a complete live period set, use filtered row count; otherwise API total.
     */
    historyServerTotal() {
      if (this.historyClientPaginationActive) {
        return this.historyTableCalls.length
      }
      return Number(this.callAnalyticsTotalRecords) || this.historyTableCalls.length
    },

    isAdminLike() {
      return (
        this.$can('read', 'admin') || this.$can('read', 'master') ||
        this.$can('read', 'hr') || this.$can('read', 'mis') ||
        this.$can('read', 'program_manager')
      )
    },

    myDid() {
      const user = getUserData()
      const empId = user && (user.emp_id != null && String(user.emp_id).trim() !== ''
        ? user.emp_id
        : user.empId)
      return resolveCallerId(this.exotelConfig, empId)
    },

    showAvailabilityToggle() {
      return Boolean(this.myDid)
    },

    managerOptions() {
      try {
        const allUsers = Object.freeze(JSON.parse(localStorage.getItem('allusers') || '[]'))
        // Manager dropdown should only ever list currently-active managers
        const activeIds = this.activeEmployeeIds
        const managers = new Map()
        allUsers.forEach(u => {
          const mgrId = u.mgr_id || u.manager_id || u.tl_emp_id || u.manager || null
          if (mgrId && activeIds.has(String(mgrId)) && !managers.has(String(mgrId))) {
            managers.set(String(mgrId), this.recruiterNameMap[mgrId] || String(mgrId))
          }
        })
        return [
          { value: 'all', text: 'All Managers' },
          ...[...managers.entries()]
            .map(([id, name]) => ({ value: id, text: name }))
            .sort((a, b) => a.text.localeCompare(b.text)),
        ]
      } catch (e) {
        return [{ value: 'all', text: 'All Managers' }]
      }
    },

    // Always-active employee IDs for recruiter/manager dropdowns.
    // Reuses the `item.status === 'ACTIVE'` convention (AddJob / EditJob),
    // adapted via isActiveUser() for the `allusers` data shape.
    activeEmployeeIds() {
      const allUsers = this.allUsersList && this.allUsersList.length
        ? this.allUsersList
        : (() => {
          try { return JSON.parse(localStorage.getItem('allusers') || '[]') } catch (e) { return [] }
        })()
      return new Set(
        (Array.isArray(allUsers) ? allUsers : []).filter(isActiveUser).map(u => String(u.emp_id)).filter(Boolean),
      )
    },

    recruiterOptions() {
      const resolveName = empId => this.recruiterNameMap[empId] || empId
      // Recruiter dropdown should only ever list currently-active recruiters
      const statusFilter = empId => this.activeEmployeeIds.has(String(empId))
      if (this.isAdminLike) {
        return [
          { value: 'all', text: 'All Recruiters' },
          ...this.recruitersList.filter(r => statusFilter(r.value)).map(r => ({ value: r.value, text: resolveName(r.value) })),
        ]
      }
      if (this.$can('read', 'account_manager')) {
        return [
          { value: 'all', text: 'My Team' },
          ...this.teamRecruiters.filter(r => statusFilter(r.emp_id)).map(r => ({ value: r.emp_id, text: resolveName(r.emp_id) })),
        ]
      }
      return []
    },

    /** ProfileShared totals for Team Not Called — Client-filtered when a client is selected. */
    recruiterProfileSharedTotals() {
      return this._aggregateRecruiterProfileShared(this.recruiterTrackerRaw)
    },

    filteredCalls() {
      return filterCallsList(this, this.allCalls)
    },

    calledBackKeySet() {
      return new Set(this.calledBackKeys || [])
    },

    /**
     * Widest already-loaded period rows (no client filters / NA aging).
     * Used only to decide which candidates completed 7 IST NA days.
     */
    periodRawCalls() {
      const key = this._alertScopeKey()
      if (this.periodCacheKey === key && Array.isArray(this.searchCallsCache)) {
        return this.searchCallsCache
      }
      if (this.alertCallsScopeKey === key && Array.isArray(this.alertCallsCache) && this.alertCallsCache.length) {
        return this.alertCallsCache
      }
      return this.allCalls
    },

    /**
     * Candidates with 7 distinct IST outbound-NA days and no Answer in the
     * loaded set — hidden from History / KPI NA / Missed / attention (display-only).
     */
    naAgingSuppressedKeySet() {
      return buildNaAgingSuppressedKeys(this.periodRawCalls, { today: todayStr() })
    },

    /**
     * Single filter pass for KPIs / cards / reports (Vue 2 has no computed memo).
     * scope = local filters except Call Analysis; filtered includes Call Analysis.
     * NA aging is applied inside filterCallsList (same keys as History + attention).
     */
    periodFilterBundle() {
      const skip = { skipCallAnalysis: true }
      const key = this._alertScopeKey()
      // Touch maps so Vue 2 recomputes Client when job / master-list hydration lands.
      void this.jobMap
      void this.clientsList
      void this.candidateMap
      let source = this.allCalls
      if (this.periodCacheKey === key && Array.isArray(this.searchCallsCache)) {
        source = this.searchCallsCache
      } else if (this.alertCallsScopeKey === key && Array.isArray(this.alertCallsCache) && this.alertCallsCache.length) {
        source = this.alertCallsCache
      } else if (this.historyAnalysisFilterActive) {
        // History All-calls dropdown replaced allCalls with an analyzed page —
        // do not feed that slice to KPIs / widgets. Keep last unfiltered snapshot
        // if we have one; otherwise wait for the live period set.
        source = Array.isArray(this.alertCallsCache) && this.alertCallsCache.length
          ? this.alertCallsCache
          : []
      }
      const scope = filterCallsList(this, source, skip)
      return { scope, filtered: scope }
    },

    /** Full-period rows for KPIs + NA/Missed cards (dates + emp + mgr + local filters, ignores analyzed). */
    periodScopeCalls() {
      return this.periodFilterBundle.scope
    },

    /** True when the live period set covers the current date / emp / mgr scope and is still fresh. */
    periodCacheReady() {
      const key = this._alertScopeKey()
      if (this.periodCacheKey !== key || !Array.isArray(this.searchCallsCache)) return false
      if (!this._periodCacheIsFresh()) return false
      if (this.periodCacheComplete) return true
      const total = Number(this.periodTotalRecords) || 0
      return total > 0 && this.searchCallsCache.length >= total
    },

    /** Reminders / Output: same local filters as KPIs (skip analyzed — Output is actionable, not analysis). */
    outputSourceCalls() {
      return this.periodScopeCalls
    },

    /** KPI chips: Filters-modal scope only — never History All-calls / analyzed. */
    kpiSourceCalls() {
      return this.periodScopeCalls
    },

    /** Report tabs: same date / recruiter / local filters as KPIs, plus History search. */
    periodFilteredCalls() {
      return applyHistorySearchFilter(this, this.periodScopeCalls)
    },

    analyzedPeriodCalls() {
      return this.periodScopeCalls.filter(c => (
        c._hasAnalysis != null ? c._hasAnalysis : hasCompletedAnalysis(c)
      ))
    },

    /**
     * Not Answered / Missed cards: same date + My/Team/Org scope as history,
     * but never apply analyzed=true|false (unanswered/missed rarely have analysis).
     */
    alertScopeCalls() {
      return this.periodScopeCalls
    },

    /** True when KPI/report aggregations may still be missing rows beyond the current page. */
    aggregationsPartial() {
      if (this.periodCacheReady) return false
      if (!this.isFetchingSearchCache) return false
      const apiTotal = Number(this.periodTotalRecords) || Number(this.callAnalyticsTotalRecords) || 0
      if (!apiTotal) return false
      const cacheLoaded = Array.isArray(this.searchCallsCache)
        ? this.searchCallsCache.length
        : this.allCalls.length
      return cacheLoaded < apiTotal
    },

    isLoadingAggregations() {
      const haveKpiScope = this.hasApiKpiCounts
        || this.periodCacheReady
        || this._periodCacheHasScopeRows()
      // History page / All-calls refetch must not flash KPI cards.
      if (haveKpiScope) {
        return this.isFetchingSearchCache && !Array.isArray(this.searchCallsCache)
      }
      return this.isLoading || (this.isFetchingSearchCache && !Array.isArray(this.searchCallsCache))
    },

    kpiFilteredCalls() {
      return applyKpiFilter(this.kpiFilter, this.filteredCalls)
    },

    historySearchActive() {
      return Boolean(historySearchText(this))
    },

    /** True while search is waiting for pages beyond the current API page. */
    historySearchLoading() {
      return this.historySearchActive
        && this.isFetchingSearchCache
        && !this._periodCacheHasScopeRows()
    },

    /** Filtered History rows. Search / extra filters use the full-period set, not this API page. */
    historyTableCalls() {
      let rows
      // History lists every matching call. KPI / NA / Missed cards use latest-state.
      if (this.kpiFilter === 'missed') {
        rows = (this.missedPeriodScopeCalls || []).filter(c => isInboundMissed(c))
      } else if (this.kpiFilter === 'not-answered') {
        rows = (this.periodScopeCalls || []).filter(c => isOutboundNoAnswerRow(c))
      } else if (this.historyNeedsFullPeriodSet) {
        rows = applyHistoryAnalysisFilter(
          this.appliedFilters.callAnalysis,
          applyKpiFilter(this.kpiFilter, this.periodScopeCalls),
        )
      } else {
        rows = applyHistoryAnalysisFilter(
          this.appliedFilters.callAnalysis,
          applyKpiFilter(
            this.kpiFilter,
            filterCallsList(this, this.allCalls, { skipCallAnalysis: true }),
          ),
        )
      }
      return sortCallsNewestFirst(applyHistorySearchFilter(this, rows))
    },

    historyDateRangeLabel() {
      return this.missedWidgetRangeLabel
    },

    /**
     * Call Quality source: full-range live period set (dates + view + recruiter/manager),
     * then only rows that have analysis. Not the current page / analyzed-only slice.
     */
    callsWithAnalysis() {
      return this.analyzedPeriodCalls
    },

    /**
     * Analysis spinner: first visit with nothing to show for this window.
     * Once this scope has analyzed rows, a finished drain, or a complete
     * cache, keep the report visible — tab switch must not remount a spinner.
     */
    callQualityLoading() {
      if (this.periodCacheReady) return false
      if (this._periodCacheHasScopeRows() && this.analyzedPeriodCalls.length) return false
      return this.isFetchingSearchCache || this.isLoading
    },

    canSeeWiderCallScope() {
      return this.isAdminLike
        || this.$can('read', 'account_manager')
        || this.canManageCalls
    },

    myCallsSliceEmpty() {
      return this.viewMode === 'my'
        && (Number(this.callAnalyticsTotalRecords) || 0) === 0
        && !this.allCalls.length
    },

    displayNotAnswered() {
      if (this.recentNotAnswered.length) return this.recentNotAnswered
      // Latest-state already ran on the live period set — empty means nobody's
      // latest call is outbound NA (they moved to Missed or Connected).
      if (
        this.periodCacheReady
        || this._periodCacheHasScopeRows()
        || this.alertScopeCalls.length
      ) {
        return []
      }
      if (this.hasExtraListFilters) return []
      if (this.canSeeWiderCallScope) {
        return uniqueMissedByCaller(this._filterAttentionRows(this.attentionNotAnswered))
      }
      return []
    },

    displayMissed() {
      let rows
      if (this.missedInboundCalls.length) {
        rows = this.missedInboundCalls
      } else if (
        this.periodCacheReady
        || this._periodCacheHasScopeRows()
        || (this.missedPeriodScopeCalls && this.missedPeriodScopeCalls.length)
      ) {
        rows = []
      } else if (this.hasExtraListFilters) {
        rows = []
      } else if (this.canSeeWiderCallScope) {
        rows = this._filterAttentionRows(this.attentionMissed)
      } else {
        rows = []
      }
      return uniqueMissedByCaller(rows)
    },

    /** API 1 count fields for this date + My/Team/Org scope (not the current page). */
    hasApiKpiCounts() {
      if (!this.periodSummaryCounts || this.periodSummaryCountsKey !== this._alertScopeKey()) {
        return false
      }
      // Client-only Filters-modal fields (client / status / vertical / category).
      // History All-calls / analyzed is not part of this KPI set.
      if (this.hasExtraListFilters) return false
      return true
    },

    missedWidgetRangeLabel() {
      const start = this.appliedFilters.startDate
      const end = this.appliedFilters.endDate
      if (!start && !end) return ''
      const def = defaultDateFilter()
      if (start === def.startDate && end === def.endDate) return 'Last 7 days'
      if (start && end && start !== end) return `${start} – ${end}`
      return start || end
    },

    notAnsweredDisplayCount() {
      // Unique callers (Missed key). History / alert list stay every NA row.
      const uniqued = uniqueMissedByCaller(this.displayNotAnswered)
      if (uniqued.length > 0) return uniqued.length
      // Loaded scope already uniqued → 0, not raw API row counts.
      if (
        this.periodCacheReady
        || this._periodCacheHasScopeRows()
        || this._loadedRowsHaveNotAnswered()
      ) {
        return 0
      }
      return 0
    },

    notAnsweredCountPartial() {
      if (this.displayNotAnswered.length > 0) return false
      if (this._trustApiNotAnsweredCount()) return false
      return this.alertUsesOrgAttention || this.aggregationsPartial
    },

    missedDisplayCount() {
      // Unique callers from the painted card. Do not apply analyzed= to this KPI.
      const q = historySearchText(this)
      const rows = q
        ? uniqueMissedByCaller(
          (this.displayMissed || []).filter(c => callMatchesHistorySearch(this, c, q)),
        )
        : this.displayMissed
      if (rows.length > 0) return rows.length
      // Loaded scope already uniqued (including all called back) → 0, not raw API rows.
      if (
        this.periodCacheReady
        || this._periodCacheHasScopeRows()
        || this._loadedRowsHaveMissed()
      ) {
        return 0
      }
      return 0
    },

    missedCountPartial() {
      if (this.displayMissed.length > 0) return false
      if (this.isFetchingMissedWidget || this.aggregationsPartial) return true
      if (this._trustApiMissedCount()) return false
      return this.alertUsesOrgAttention
    },

    outputCardCount() {
      return (Number(this.callingTrackingCount) || 0) + (this.upcomingReminders.length || 0)
    },

    alertUsesOrgAttention() {
      return this.canSeeWiderCallScope
        && !this.recentNotAnswered.length
        && !this.missedInboundCalls.length
        && (this.attentionNotAnswered.length > 0 || this.attentionMissed.length > 0)
    },

    recentNotAnswered() {
      const buckets = latestCallerStateBuckets(this.alertScopeCalls, {
        keyFn: missedCallerUniqueKey,
        timeFn: callTimestamp,
      })
      return Array.from(buckets.notAnsweredByCaller.values()).sort(sortNewestCallFirst)
    },

    kpi() {
      // Latest-call-wins unique callers — never raw API 1 counts or History analyzed=.
      // Window: date / recruiter / search / client (and other Filters-modal fields).
      const fullyLoaded = this.periodCacheReady
      const pageScoped = !fullyLoaded

      const source = applyHistorySearchFilter(this, this.kpiSourceCalls)
      const buckets = uniqueKpiCallerBuckets(source)
      const displayTotal = buckets.totalByCaller.size
      const connected = buckets.connectedByCaller.size
      const notAnswered = buckets.notAnsweredByCaller.size
      const failed = buckets.failedByCaller.size
      const missed = this.missedDisplayCount

      // Avg = newest unique-connected duration per caller (same as Org Performance).
      let connectedRowDuration = 0
      buckets.connectedByCaller.forEach(call => {
        connectedRowDuration += call.durationSec || 0
      })
      const connectionRate = displayTotal > 0 ? Math.round((connected / displayTotal) * 100) : 0
      const avgSec = connected > 0 ? Math.round(connectedRowDuration / connected) : 0
      const avgMin = Math.floor(avgSec / 60)
      const avgRemSec = avgSec % 60

      return {
        total: displayTotal,
        pageTotal: source.length,
        pageScoped,
        attentionFromOrg: this.alertUsesOrgAttention,
        connected,
        notAnswered,
        failed,
        failedIsRows: false,
        missed,
        missedRangeLabel: this.alertUsesOrgAttention ? '' : this.missedWidgetRangeLabel,
        totalDuration: connectedRowDuration,
        connectionRate,
        avgDuration: avgMin > 0 ? `${avgMin}m ${avgRemSec}s` : `${avgRemSec}s`,
      }
    },

    teamAnalytics() {
      const showAnalytics = (this.$can('read', 'account_manager') && this.viewMode === 'team')
        || (this.isAdminLike && (this.viewMode === 'team' || this.viewMode === 'org'))
      if (!showAnalytics) return []

      const map = {}
      this.periodFilteredCalls.forEach((c, idx) => {
        const empId = c._rawRecruiter || ''
        if (!empId) return
        if (!map[empId]) {
          map[empId] = {
            empId,
            name: this.recruiterNameMap[empId] || empId,
            _calls: [],
            // Unique callers (Missed key) per volume bucket — not raw call rows
            _totalByCaller: new Map(),
            _outboundByCaller: new Map(),
            _inboundByCaller: new Map(),
            _busyByCaller: new Map(),
            // Unique profile keys (not raw share/call events)
            _sharedCalledKeys: new Set(),
            _sharedNotCalledKeys: new Set(),
          }
        }
        const rec = map[empId]
        rec._calls.push(c)
        const callerKey = missedCallerUniqueKey(c) || `row:${empId}:${idx}`
        const s = (c._rawStatus || '').toLowerCase()
        const isConnected = isConnectedStatus(s)

        keepNewestCallerRow(rec._totalByCaller, callerKey, c)
        if (isInbound(c._rawDirection)) keepNewestCallerRow(rec._inboundByCaller, callerKey, c)
        else keepNewestCallerRow(rec._outboundByCaller, callerKey, c)
        if (s === 'busy') keepNewestCallerRow(rec._busyByCaller, callerKey, c)

        // Unique Profiles Shared: dedupe by candidateId → phone → sid/rowKey
        const candidateStatus = this.getCandidateStatus(c) || c.candidateStatus || ''
        if (isSharedPlus(candidateStatus)) {
          const key = profileUniqueKey(c)
          if (key) {
            if (isConnected) rec._sharedCalledKeys.add(key)
            else rec._sharedNotCalledKeys.add(key)
          }
        }
      })

      return Object.values(map)
        .map(r => {
          const latest = latestCallerStateBuckets(r._calls, {
            keyFn: missedCallerUniqueKey,
            timeFn: callTimestamp,
          })
          const total = r._totalByCaller.size
          const connected = latest.connectedByCaller.size
          const notAnswered = latest.notAnsweredByCaller.size
          const busy = r._busyByCaller.size
          const failed = latest.failedByCaller.size
          const inbound = r._inboundByCaller.size
          const outbound = r._outboundByCaller.size
          // Duration from newest unique-connected row per caller (do not invent)
          let totalDuration = 0
          latest.connectedByCaller.forEach(call => {
            totalDuration += call.durationSec || 0
          })
          // Called: unique shared+ profiles with ≥1 connected call
          const profilesShared = r._sharedCalledKeys.size
          const empKey = String(r.empId)
          // Join tracker totals by emp_id (SH-228) or normalized name (work tracker rec_name)
          const totals = this.recruiterProfileSharedTotals || {}
          let trackerTotal = totals[empKey]
          if (trackerTotal == null && r.name) {
            trackerTotal = totals[`name:${normalizePersonName(r.name)}`]
          }
          let sharedNotConnected = 0
          if (trackerTotal != null) {
            // Not Called = work-tracker ProfileShared − Called (meaningful when join works)
            sharedNotConnected = Math.max(0, Number(trackerTotal) - profilesShared)
          } else {
            // Fallback: unique shared+ with only non-connected calls in range
            r._sharedNotCalledKeys.forEach(k => {
              if (!r._sharedCalledKeys.has(k)) sharedNotConnected++
            })
          }
          const connectionRate = total > 0 ? Math.round((connected / total) * 100) : 0
          // Conversion: unique Called ÷ unique Connected callers
          const conversionRate = connected > 0 ? Math.round((profilesShared / connected) * 100) : 0
          const avgSec = connected > 0 ? Math.round(totalDuration / connected) : 0
          const avgMin = Math.floor(avgSec / 60)
          const avgRemSec = avgSec % 60
          return {
            empId: r.empId,
            name: r.name,
            total,
            connected,
            notAnswered,
            busy,
            failed,
            inbound,
            outbound,
            totalDuration,
            profilesShared,
            sharedNotConnected,
            connectionRate,
            conversionRate,
            avgDuration: connected > 0 ? (avgMin > 0 ? `${avgMin}m ${avgRemSec}s` : `${avgRemSec}s`) : '—',
          }
        })
        .sort((a, b) => b.total - a.total)
    },

    datewiseAnalytics() {
      const showAnalytics = (this.$can('read', 'account_manager') && this.viewMode === 'team')
        || (this.isAdminLike && (this.viewMode === 'team' || this.viewMode === 'org'))
      if (!showAnalytics) return []

      const map = {}
      this.periodFilteredCalls.forEach(c => {
        const empId = c._rawRecruiter || ''
        if (!empId) return
        const rawDate = c._rawDate ? parseISTDate(c._rawDate) : null
        const datePart = c._istDay
          || (rawDate && !isNaN(rawDate) ? fmt(istDateObj(rawDate)) : '—')
        const key = `${datePart}__${empId}`
        if (!map[key]) {
          map[key] = {
            date: datePart,
            empId,
            recruiterName: this.recruiterNameMap[empId] || empId,
            total: 0, connected: 0, notAnswered: 0, failed: 0,
            inbound: 0, outbound: 0, totalDuration: 0,
            // Unique shared+ profile keys for this recruiter/day (call-log basis)
            _sharedCalledKeys: new Set(),
            _sharedNotCalledKeys: new Set(),
          }
        }
        const s = (c._rawStatus || '').toLowerCase()
        const isConnected = isConnectedStatus(s)
        map[key].total++
        if (isConnected) map[key].connected++
        else if (isNoAnswerStatus(s)) map[key].notAnswered++
        else if (isFailedStatus(s)) map[key].failed++
        if (isInbound(c._rawDirection)) map[key].inbound++
        else map[key].outbound++
        map[key].totalDuration += c.durationSec || 0

        // Unique PS (same keying as Team): Called + Not Called from call logs that day.
        // Work tracker is range-grain only, so daily PS uses call-log unique shared+.
        const candidateStatus = this.getCandidateStatus(c) || c.candidateStatus || ''
        if (isSharedPlus(candidateStatus)) {
          const uk = profileUniqueKey(c)
          if (uk) {
            if (isConnected) map[key]._sharedCalledKeys.add(uk)
            else map[key]._sharedNotCalledKeys.add(uk)
          }
        }
      })

      return Object.values(map)
        .map(r => {
          const profilesShared = r._sharedCalledKeys.size
          let sharedNotConnected = 0
          r._sharedNotCalledKeys.forEach(k => {
            if (!r._sharedCalledKeys.has(k)) sharedNotConnected++
          })
          // PS Count = unique Called + unique Not Called for that day (= unique shared+)
          const psCount = profilesShared + sharedNotConnected
          const avgSec = r.connected > 0 ? Math.round(r.totalDuration / r.connected) : 0
          const avgMin = Math.floor(avgSec / 60)
          const avgRemSec = avgSec % 60
          return {
            date: r.date,
            empId: r.empId,
            recruiterName: r.recruiterName,
            total: r.total,
            connected: r.connected,
            notAnswered: r.notAnswered,
            failed: r.failed,
            inbound: r.inbound,
            outbound: r.outbound,
            totalDuration: r.totalDuration,
            psCount,
            connectionRate: r.total > 0 ? Math.round((r.connected / r.total) * 100) : 0,
            // Occupancy = talk time as % of an 8-hour working day (28800s)
            occupancy: Math.min(100, Math.round((r.totalDuration / 28800) * 100)),
            avgDuration: r.connected > 0 ? (avgMin > 0 ? `${avgMin}m ${avgRemSec}s` : `${avgRemSec}s`) : '—',
          }
        })
        .sort((a, b) => b.date.localeCompare(a.date) || a.recruiterName.localeCompare(b.recruiterName))
    },

    hourlyAnalytics() {
      if (this.viewMode === 'my') return []

      const map = {}

      this.periodFilteredCalls.forEach(c => {
        const ts = callTimestamp(c)
        if (!ts) return
        const date = new Date(ts)
        const hour = parseInt(IST_HOUR_FMT.format(date), 10) % 24
        const key = hour

        if (!map[key]) {
          map[key] = {
            hour,
            label: `${String(hour).padStart(2, '0')}:00 - ${String(hour + 1).padStart(2, '0')}:00`,
            total: 0,
            connected: 0,
            notAnswered: 0,
            missed: 0,
            totalDuration: 0,
          }
        }

        const s = (c._rawStatus || '').toLowerCase()
        const isConnected = isConnectedStatus(s)
        const isInboundCall = isInbound(c._rawDirection)

        map[key].total++
        if (isConnected) map[key].connected++
        else if (isNoAnswerStatus(s) && !isInboundCall) map[key].notAnswered++
        if (isInboundCall && !isConnected) map[key].missed++
        map[key].totalDuration += c.durationSec || 0
      })

      const result = []
      for (let h = 0; h < 24; h++) {
        if (map[h]) {
          const r = map[h]
          result.push({
            ...r,
            connectionRate: r.total > 0 ? Math.round((r.connected / r.total) * 100) : 0,
            avgDuration: r.connected > 0 ? Math.round(r.totalDuration / r.connected) : 0,
          })
        }
      }
      return result
    },

    missedInboundCalls() {
      return unresolvedMissedInbound(this.missedPeriodScopeCalls, this.calledBackKeySet)
    },

    /** Extra last-7 fetch for this My/Team/Org + emp/mgr scope. */
    missedWidgetExtraCalls() {
      const extraKey = this._missedWidgetScopeKey()
      return (this.missedWidgetCacheKey === extraKey && Array.isArray(this.missedWidgetCache))
        ? this.missedWidgetCache
        : []
    },

    /**
     * Extra last-7 inbound-miss rows, then the same client-side filters as History.
     * Status=Answered must not reintroduce Not Answered / missed from this drain.
     */
    missedWidgetExtraScopedCalls() {
      const extra = this.missedWidgetExtraCalls
      if (!extra.length) return extra
      if (!this.hasExtraListFilters) return extra
      return filterCallsList(this, extra, { skipCallAnalysis: true })
    },

    /**
     * Period rows for Missed — date / search / recruiter (and other shared
     * filters). Unique-by-caller runs on this filtered set. Analyzed is skipped
     * so the History toggle cannot empty inbound skips.
     */
    missedPeriodScopeCalls() {
      return mergeUnfilteredCallScope(
        this.periodScopeCalls,
        this.missedWidgetExtraScopedCalls,
        this.appliedFilters.startDate,
        this.appliedFilters.endDate,
      )
    },

    /**
     * Missed widget source: same page date filter as History / KPI (not a
     * separate rolling last-7). Includes dateless inbound skips.
     */
    missedWidgetScopeCalls() {
      return mergeUnfilteredCallScope(
        this.periodScopeCalls,
        this.missedWidgetExtraScopedCalls,
        this.appliedFilters.startDate,
        this.appliedFilters.endDate,
      )
    },

    widgetMissedInboundCalls() {
      return unresolvedMissedInbound(this.missedWidgetScopeCalls, this.calledBackKeySet)
    },

    upcomingReminders() {
      const results = []
      const callLookup = new Map()
      const lookupSource = this.outputSourceCalls
      lookupSource.forEach(c => {
        if (!c.candidateId || !c.jobId) return
        callLookup.set(`${c.candidateId}_${c.jobId}`, c)
      })
      Object.entries(this.candidateMap).forEach(([candidateId, entry]) => {
        const jobs = entry.candidateJobs || []
        jobs.forEach(j => {
          const rawExtra = j.extra_details || {}
          let extra = rawExtra
          if (typeof rawExtra === 'string') {
            try { extra = JSON.parse(rawExtra) } catch { extra = {} }
          }
          const reminders = Array.isArray(extra.callback_reminders) ? extra.callback_reminders : []
          reminders.forEach(r => {
            const dismissKey = `${candidateId}_${r.remindAt}_${r.phone || ''}`
            if (this.dismissedReminders[dismissKey]) return
            const matchingCall = callLookup.get(`${candidateId}_${j.job_id || j.id}`)
            // Client / status / direction / search: only keep reminders tied to a filtered call.
            if (this.hasExtraListFilters && !matchingCall) return
            const recruiterEmpId = matchingCall?._rawRecruiter || j.recruiter || getUserData().emp_id || ''
            const recruiterName = this.recruiterNameMap[recruiterEmpId] || recruiterEmpId || ''
            results.push({
              ...r,
              candidateId: Number(candidateId),
              jobId: j.job_id || j.id,
              candidateName: r.candidateName || entry.name || '',
              recruiterEmpId,
              recruiterName,
              _dismissKey: dismissKey,
            })
          })
        })
      })
      results.sort((a, b) => new Date(a.remindAt) - new Date(b.remindAt))
      return results
    },

    fields() {
      const dateCol = { width: '210px', minWidth: '210px', maxWidth: '210px' }
      const detailsCol = { width: '270px', minWidth: '220px', maxWidth: '270px' }
      // width:100% = share leftover equally (not Details). min keeps pills/bars readable.
      const statusCol = { width: '100%', minWidth: '128px', maxWidth: 'none' }
      const scoreCol = { width: '100%', minWidth: '120px', maxWidth: 'none' }
      const analysisCol = { width: '100%', minWidth: '110px', maxWidth: 'none' }
      const actionsCol = { width: '64px', minWidth: '64px', maxWidth: '64px' }
      return [
        { key: 'date', label: 'Date & Time (IST)', sortable: true, thStyle: dateCol, tdStyle: dateCol },
        { key: 'candidateAndJob', label: 'Call Details', sortable: false, thStyle: detailsCol, tdStyle: detailsCol },
        { key: 'status', label: 'Call Status', sortable: true, thStyle: statusCol, tdStyle: statusCol },
        { key: 'hygiene', label: 'Hygiene', sortable: false, thStyle: { ...scoreCol }, tdStyle: { ...scoreCol } },
        { key: 'quality', label: 'Quality', sortable: false, thStyle: { ...scoreCol }, tdStyle: { ...scoreCol } },
        { key: 'interest', label: 'Interest', sortable: false, thStyle: { ...scoreCol }, tdStyle: { ...scoreCol } },
        { key: 'outcome', label: 'Outcome', sortable: false, thStyle: { ...scoreCol }, tdStyle: { ...scoreCol } },
        {
          key: 'analysis',
          label: 'Analysis',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { ...analysisCol },
          tdStyle: { ...analysisCol },
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: actionsCol,
          tdStyle: actionsCol,
        },
      ]
    },

    showRecruiterInHistory() {
      return (this.canManageCalls || this.$can('read', 'account_manager') || this.isAdminLike)
        && this.viewMode !== 'my'
    },

    recruiterNameMap() {
      const map = {}

      // Primary source: allusers stored at login — has full first/last names
      try {
        const allUsers = Object.freeze(JSON.parse(localStorage.getItem('allusers') || '[]'))
        allUsers.forEach(u => {
          const id = u.emp_id
          if (!id) return
          const fullName = u.name
            || (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : null)
            || u.title
            || null
          if (fullName) map[id] = fullName
        })
      } catch (e) { /* ignore */ }

      // Overlay with API data (teamRecruiters / recruitersList) for any not in allusers
      const allRecrs = [...(this.teamRecruiters || []), ...(this.recruitersList || [])]
      allRecrs.forEach(r => {
        const id = r.value || r.emp_id
        if (!id || map[id]) return  // already resolved from allusers
        const fullName = r.name
          || (r.first_name ? `${r.first_name} ${r.last_name || ''}`.trim() : null)
          || r.title
          || r.text
          || r.emp_name
          || id
        map[id] = fullName
      })

      Object.entries(this.apiRecruiterNames || {}).forEach(([id, name]) => {
        if (id && name) map[id] = name
      })

      return Object.freeze(map)
    },
  },

  created() {
    this._debouncedLoadCallNotes = this._debounce(() => {
      this.loadCallNotes()
    }, 150)
    this._flushCandidateMap = () => {}
    this._flushJobMap = () => {}
    this._flushPhoneLookup = () => {}
    // Org-view (isAdminLike) lands on Call History, not Team. ?tab= applied in mounted.
    if (this.isAdminLike && !this._callAnalyticsTabQuery()) {
      this.tabIndex = 2
      this.historyTabIndex = 0
    }
  },

  async mounted() {
    // Restore filters before first fetch (survives refresh; cleared on tab close)
    this.restoreFiltersFromSession()
    this._clearPersistedCallRowCaches()
    this._hydratePeriodCacheFromTtl()
    if (this.appliedFilters && this.appliedFilters.client && this.appliedFilters.client !== 'all') {
      this.loadClients().catch(() => {})
    }

    // Use login-cached allusers so recruiter/manager dropdowns work without
    // prefetching users/* on mount. Full lists load when Filters is opened.
    try {
      const cachedUsers = JSON.parse(localStorage.getItem('allusers') || '[]')
      if (Array.isArray(cachedUsers) && cachedUsers.length > 0) {
        this.allUsersList = cachedUsers
        this._allUsersReady = true
      }
    } catch (e) { /* ignore */ }

    // Deep link from inbound_call push: ?direction=inbound (overrides restored filters)
    const qDir = (this.$route && this.$route.query && this.$route.query.direction) || ''
    if (String(qDir).toLowerCase() === 'inbound') {
      this.filters.direction = 'inbound'
      this.appliedFilters = { ...this.appliedFilters, direction: 'inbound' }
      this.viewMode = 'my'
      this.tabIndex = 0
      this.historyTabIndex = 0
      this.kpiFilter = 'all'
    }

    // Set default viewMode based on role (skip if deep-link already set my).
    // Keep _viewModeReady false until after the initial fetch so the viewMode
    // watcher does not double-fire (Vue 2 flushes watchers after the first await)
    // and supersede the mount fetch via the generation guard.
    this._skipNextViewModeFetch = true
    if (String(qDir).toLowerCase() !== 'inbound') {
      if (this.isAdminLike) {
        this.viewMode = 'org'
      } else if (this.$can('read', 'account_manager')) {
        this.viewMode = 'team'
      } else {
        this.viewMode = 'my'
      }
    }

    // ?tab= wins; otherwise org view opens Call History (not Team / Analysis).
    this._applyInitialCallAnalyticsTab()

    // Opening Call Analytics: one list page. Period drain starts after page 1
    // only when API 1 did not send KPI counts (or filters/tabs need every row).
    try {
      await this.fetchCalls(1)
    } catch (e) {
      console.error('[MyCalls] initial fetchCalls failed:', e)
      this.isLoading = false
      this.isFetchingMore = false
      this._clearAnalyticsLoadingGuard()
    }

    this._applyRoutePhoneDeepLink()

    this._viewModeReady = true
    // Let deferred viewMode watcher consume _skipNextViewModeFetch once, then reset.
    await this.$nextTick()
    if (this._skipNextViewModeFetch) {
      this._skipNextViewModeFetch = false
    }

    try {
      await this.loadExotelConfig()
    } catch (e) { /* DID / availability are non-critical */ }

    if (!this._availabilityUnsub) {
      this._availabilityUnsub = onAvailabilityChange(detail => {
        if (this.callsAvailableSaving) return
        this.callsAvailable = !detail || detail.calls_available !== false
      })
    }

    this.reminderInterval = setInterval(() => {
      this.checkReminders()
    }, 60000)
  },

  watch: {
    '$route.query.phone'(phone) {
      if (!this._viewModeReady) return
      this._applyRoutePhoneDeepLink(phone)
    },
    '$route.query.tab'() {
      if (!this._viewModeReady) return
      this._applyTabQuery()
    },
    viewMode() {
      if (!this._viewModeReady) return
      if (this._skipNextViewModeFetch) {
        this._skipNextViewModeFetch = false
        return
      }
      const maxTabIndex = this.viewMode === 'my' ? 0 : 3
      if (this.tabIndex > maxTabIndex) {
        this.tabIndex = this.viewMode === 'my' ? 0 : 2
      }
      this.historyTabIndex = 0
      // Scope change → new key immediately. Reuse a still-fresh 5-min entry if we have one.
      this._clearPeriodCache()
      this._hydratePeriodCacheFromTtl()
      this.fetchCalls(1)
    },
    allCalls() {
      this._debouncedLoadCallNotes()
    },
    kpiFilter() {
      // Stay on the current API page; drain only if chips need the full set.
      if (this._viewModeReady && this._periodCacheNeeded()) this._ensurePeriodCache()
    },
    /**
     * Classic pagination: fetch the clicked page with offset/limit.
     * No auto-prefetch / auto-drain. A newer click supersedes an in-flight page fetch.
     */
    currentPage(page) {
      if (!this._viewModeReady) return
      if (this._pagingSilent) return
      // Range search paginates the live period set — do not refetch API pages.
      if (this.historyClientPaginationActive) return
      const target = Math.max(1, Number(page) || 1)
      if (target === this._loadedAnalyticsPage && !this.isFetchingMore && !this.isLoading) return
      // Always allow a new fetch — generation guard drops stale responses.
      // Do not early-return when isLoading: that can permanently block page-1
      // recovery if a prior generation was orphaned.
      this.fetchCalls(target)
    },
    perPage(next, prev) {
      if (!this._viewModeReady) return
      const n = Math.min(100, Math.max(1, Number(next) || 50))
      const p = Math.min(100, Math.max(1, Number(prev) || 50))
      if (Number(this.perPage) !== n) {
        this.perPage = n
        return
      }
      if (n === p) return
      if (this.historyClientPaginationActive) {
        this._setCurrentPageSilent(1)
        return
      }
      this.fetchCalls(1)
    },
    tabIndex(idx) {
      if (!this._viewModeReady) return
      if (this.viewMode !== 'my' && idx === 0) {
        this.fetchRecruiterProfileSharedTotals()
      }
      if (this._periodCacheNeeded()) this._ensurePeriodCache()
    },
    historyTabIndex() {
      if (!this._viewModeReady) return
      if (this._periodCacheNeeded()) this._ensurePeriodCache()
    },
  },

  beforeDestroy() {
    if (this._availabilityUnsub) {
      this._availabilityUnsub()
      this._availabilityUnsub = null
    }
    if (this.reminderInterval) {
      clearInterval(this.reminderInterval)
    }
    this._clearAnalyticsLoadingGuard()
    if (this._enrichTimer) clearTimeout(this._enrichTimer)
    if (this._missedWidgetTimer) clearTimeout(this._missedWidgetTimer)
    this._clearPeriodCacheExpireTimer()
    this.enrichFetchGen += 1
    ;['page', 'period', 'missed', 'historySearch'].forEach(slot => this._cancelRequest(slot))
    this._clearPeriodCache()
    this._clearPersistedCallRowCaches()
    if (callAnalyticsService && typeof callAnalyticsService.clearDetailCache === 'function') {
      callAnalyticsService.clearDetailCache()
    }
  },

  methods: {
    // ── KPI filter ──────────────────────────────────────────────────────────

    setKpiFilter(key) {
      if (
        this.myCallsSliceEmpty
        && this.canSeeWiderCallScope
        && (key === 'not-answered' || key === 'missed')
      ) {
        this.viewMode = this.isAdminLike ? 'org' : 'team'
      }
      this.kpiFilter = key
      this.tabIndex = this.viewMode === 'my' ? 0 : 2
      this.historyTabIndex = 0
      this.$nextTick(() => {
        const tabsEl = this.$refs.mainTabs
        if (tabsEl) tabsEl.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    /** Widget View All: same page date range + missed History filter. */
    viewAllMissedCalls() {
      this.setKpiFilter('missed')
    },

    onCallingAttention(payload) {
      const next = payload && typeof payload === 'object' ? payload : {}
      this.attentionNotAnswered = Array.isArray(next.notAnswered) ? next.notAnswered : []
      this.attentionMissed = Array.isArray(next.missed) ? next.missed : []
      this.attentionNotAdded = Array.isArray(next.notAdded) ? next.notAdded : []
      this.attentionFlagged = Array.isArray(next.flagged) ? next.flagged : []
      this.attentionScope = next.scope || ''
      if (Array.isArray(next.items)) {
        this.callingTrackingCount = next.items.length
      }
    },

    // ── Debounce utility & candidateMap flush ────────────────────────────────

    _debounce(fn, ms) {
      let t
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms) }
    },

    async loadExotelConfig() {
      try {
        const response = await adminConfigService.getExotelConfig()
        if (response && response.status === 'OK') {
          const raw = response.data
          this.exotelConfig = Array.isArray(raw)
            ? raw
            : (Array.isArray(raw && raw.data) ? raw.data : [])
        }
      } catch (error) {
        // silently ignore — DID display is non-critical
      }
      try {
        await loadAgentAvailability({ force: true })
        this.callsAvailable = getCallsAvailableOrDefault()
      } catch (e) {
        this.callsAvailable = getCallsAvailableOrDefault()
      }
    },

    async onCallsAvailableToggle(nextVal) {
      const next = nextVal === true || nextVal === 1 || nextVal === 'true' || nextVal === '1'
      if (this.callsAvailableSaving) {
        this.callsAvailable = !next
        return
      }
      const prev = !next
      this.callsAvailableSaving = true
      try {
        await setAgentAvailability(next)
        this.callsAvailable = next
        this.$bvToast.toast(
          next
            ? 'You are available for inbound and outbound calls.'
            : 'Calling paused. Inbound calls will notify you but will not ring your phone. Outbound is blocked.',
          {
            title: next ? 'Available for calls' : 'Calling Paused',
            variant: next ? 'success' : 'secondary',
            solid: true,
            autoHideDelay: 5000,
          },
        )
      } catch (e) {
        this.callsAvailable = prev
        this.$bvToast.toast(
          (e && e.message) || 'Could not update calling availability. Try again.',
          { title: 'Update Failed', variant: 'danger', solid: true, autoHideDelay: 6000 },
        )
      } finally {
        this.callsAvailableSaving = false
      }
    },

    // ── Team / recruiter context (loaded before fetchCalls) ─────────────────

    /** Race a promise against a timeout so hung users/* cannot block the page. */
    _withTimeout(promise, ms, label = 'request') {
      let timer
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
      })
      return Promise.race([Promise.resolve(promise), timeout]).finally(() => {
        if (timer) clearTimeout(timer)
      })
    },

    /** Failsafe: never leave Org/Team KPIs on a permanent spinner. */
    _armAnalyticsLoadingGuard() {
      this._clearAnalyticsLoadingGuard()
      this._analyticsLoadingGuard = setTimeout(() => {
        this._analyticsLoadingGuard = null
        if (!this.isLoading && !this.isFetchingMore) return
        console.warn('[MyCalls] analytics loading guard — clearing stuck spinner')
        this.isLoading = false
        this.isFetchingMore = false
        this.$bvToast.toast(
          'Call analytics is taking too long. Try Refresh or a smaller date range.',
          { title: 'Load Timeout', variant: 'warning', solid: true, autoHideDelay: 8000 },
        )
      }, 45000)
    },

    _clearAnalyticsLoadingGuard() {
      if (this._analyticsLoadingGuard) {
        clearTimeout(this._analyticsLoadingGuard)
        this._analyticsLoadingGuard = null
      }
    },

    onFiltersModalShown() {
      this.loadClients().catch(e => {
        console.warn('[MyCalls] loadClients failed:', e)
      })
      this.loadJobsConfigOptions().catch(e => {
        console.warn('[MyCalls] loadJobsConfigOptions failed:', e)
      })
      if (this._teamContextLoaded) return
      this._teamContextLoaded = true
      this.loadTeamContext().catch(e => {
        console.warn('[MyCalls] loadTeamContext failed:', e)
        this._allUsersReady = true
      })
    },

    _titlesFromConfigList(list) {
      return (Array.isArray(list) ? list : [])
        .map(item => {
          if (typeof item === 'string') return item.trim()
          return String((item && (item.title || item.value || item.text)) || '').trim()
        })
        .filter(Boolean)
    },

    async loadJobsConfigOptions() {
      if (this._jobsConfigLoaded) return
      this._jobsConfigLoaded = true
      try {
        const res = await adminConfigService.getJobsDropdownList()
        const payload = res && res.data && res.data.data
        if (!payload) return
        const verticals = this._titlesFromConfigList(payload.verticalList)
        const categories = this._titlesFromConfigList(payload.categoryList)
        if (verticals.length) this.jobsConfigVerticals = verticals
        if (categories.length) this.jobsConfigCategories = categories
      } catch (e) {
        this._jobsConfigLoaded = false
      }
    },

    async loadClients() {
      if (Array.isArray(this.clientsList) && this.clientsList.length) return
      try {
        const res = await clientService.getClients()
        const raw = res && Array.isArray(res.data)
          ? res.data
          : (res && res.data && Array.isArray(res.data.data) ? res.data.data : [])
        this.clientsList = raw
      } catch (e) {
        this.clientsList = []
      }
    },

    _filterAttentionByClient(list) {
      return this._filterAttentionRows(list)
    },

    _filterAttentionRows(list) {
      return filterCallsList(this, Array.isArray(list) ? list : [], { skipCallAnalysis: true })
    },

    refreshAnalytics() {
      callAnalyticsService.invalidatePeriodListCache(this._alertScopeKey())
      this._clearPeriodCache()
      this.fetchCalls(1)
      this.attentionRefreshKey += 1
      if (this.viewMode !== 'my' && this.tabIndex === 0) {
        this.fetchRecruiterProfileSharedTotals()
      }
    },

    async loadTeamContext() {
      const { emp_id } = getUserData() || {}
      // users/* has no axios timeout — bound each task so Org/Team never waits forever.
      const TASK_TIMEOUT_MS = 8000
      const tasks = []

      // Prime from cache so recruiter/manager dropdowns work immediately when
      // allusers was persisted from a prior session; refresh in parallel.
      try {
        const cachedUsers = JSON.parse(localStorage.getItem('allusers') || '[]')
        if (Array.isArray(cachedUsers) && cachedUsers.length > 0) {
          this.allUsersList = cachedUsers
          this._allUsersReady = true
        }
      } catch (e) { /* ignore */ }

      tasks.push(
        this._withTimeout(
          userService.getAllUsersDdActiveInactive()
            .then(res => {
              const users = Array.isArray(res && res.data)
                ? res.data
                : (Array.isArray(res) ? res : [])
              if (users.length) {
                this.allUsersList = users
                localStorage.setItem('allusers', JSON.stringify(users))
              }
            }),
          TASK_TIMEOUT_MS,
          'users/all',
        )
          .catch(err => {
            console.warn('[MyCalls] users context soft-fail:', err && err.message ? err.message : err)
          })
          .finally(() => {
            this._allUsersReady = true
          }),
      )

      if (this.canManageCalls) {
        tasks.push(
          this._withTimeout(
            userService.getAllRecruitersList()
              .then(res => {
                if (res && res.data) {
                  this.recruitersList = res.data.map(r => ({
                    value: r.emp_id,
                    text: r.emp_name || r.name || r.emp_id,
                  }))
                }
              }),
            TASK_TIMEOUT_MS,
            'users/recruiters',
          ).catch(err => {
            console.warn('[MyCalls] recruiters context soft-fail:', err && err.message ? err.message : err)
          }),
        )
      }

      if (this.$can('read', 'account_manager')) {
        tasks.push(
          this._withTimeout(
            userService.getAllTeamMembersList(emp_id)
              .then(res => {
                this.teamRecruiters = (res && res.data) ? res.data : []
              }),
            TASK_TIMEOUT_MS,
            'users/team-members',
          ).catch(err => {
            console.warn('[MyCalls] team-members context soft-fail:', err && err.message ? err.message : err)
          }),
        )
      }

      await Promise.all(tasks)
      this._allUsersReady = true
    },

    // ── Data fetching ────────────────────────────────────────────────────────

    persistFiltersToSession() {
      try {
        sessionStorage.setItem(MY_CALLS_FILTERS_KEY, JSON.stringify({
          filters: { ...this.filters },
          appliedFilters: { ...this.appliedFilters },
        }))
      } catch (e) { /* noop */ }
    },
    restoreFiltersFromSession() {
      try {
        const raw = sessionStorage.getItem(MY_CALLS_FILTERS_KEY)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved || typeof saved !== 'object') return
        if (saved.filters && typeof saved.filters === 'object') {
          this.filters = { ...this.filters, ...saved.filters }
        }
        if (saved.appliedFilters && typeof saved.appliedFilters === 'object') {
          this.appliedFilters = { ...this.appliedFilters, ...saved.appliedFilters }
        }
        this.filters.search = ''
        this.appliedFilters.search = ''
        // History always opens on All so page 1 shows `limit` rows immediately.
        this.filters.callAnalysis = 'all'
        this.appliedFilters.callAnalysis = 'all'
        if (typeof this.filters.client !== 'string' || !this.filters.client) {
          this.filters.client = 'all'
        }
        if (typeof this.appliedFilters.client !== 'string' || !this.appliedFilters.client) {
          this.appliedFilters.client = 'all'
        }
        delete this.filters.recruiterStatus
        delete this.appliedFilters.recruiterStatus
        if (typeof this.filters.vertical !== 'string' || !this.filters.vertical) this.filters.vertical = 'all'
        if (typeof this.appliedFilters.vertical !== 'string' || !this.appliedFilters.vertical) {
          this.appliedFilters.vertical = 'all'
        }
        if (typeof this.filters.category !== 'string' || !this.filters.category) this.filters.category = 'all'
        if (typeof this.appliedFilters.category !== 'string' || !this.appliedFilters.category) {
          this.appliedFilters.category = 'all'
        }
        if (typeof this.filters.candidateStatus !== 'string' || !this.filters.candidateStatus) {
          this.filters.candidateStatus = 'all'
        }
        if (typeof this.appliedFilters.candidateStatus !== 'string' || !this.appliedFilters.candidateStatus) {
          this.appliedFilters.candidateStatus = 'all'
        }
        // Ensure Call Analysis + dates always have safe defaults after restore
        const validAnalysis = ['all', 'analyzed', 'not_analyzed']
        if (!validAnalysis.includes(this.filters.callAnalysis)) this.filters.callAnalysis = 'all'
        if (!validAnalysis.includes(this.appliedFilters.callAnalysis)) {
          this.appliedFilters.callAnalysis = 'all'
        }
        const dateOk = s => typeof s === 'string'
          && /^\d{4}-\d{2}-\d{2}$/.test(s)
          && !Number.isNaN(new Date(`${s}T00:00:00`).getTime())
        const dates = defaultDateFilter()
        const start = this.appliedFilters.startDate
        const end = this.appliedFilters.endDate
        if (!dateOk(start) || !dateOk(end) || start > end) {
          // Soft-fail bad/corrupt session filters — do not send a broken range to API.
          this.filters = { ...this.filters, ...dates }
          this.appliedFilters = { ...this.appliedFilters, ...dates }
        } else if (describeWideRangeFilterError(this.appliedFilters, {
          includeManager: true,
          includeRecruiter: true,
        })) {
          // Wide range with no extra filters — snap back to last 7 so first load stays complete.
          this.filters = { ...this.filters, ...dates }
          this.appliedFilters = { ...this.appliedFilters, ...dates }
        }
      } catch (e) {
        try { sessionStorage.removeItem(MY_CALLS_FILTERS_KEY) } catch (err) { /* noop */ }
      }
    },
    clearPersistedFilters() {
      try {
        sessionStorage.removeItem(MY_CALLS_FILTERS_KEY)
      } catch (e) { /* noop */ }
    },

    _narrowingFilterOptions() {
      return {
        includeManager: this.isAdminLike && (this.viewMode === 'org' || this.viewMode === 'team'),
        includeRecruiter: (this.canManageCalls || this.$can('read', 'account_manager'))
          && (this.viewMode === 'team' || this.viewMode === 'org'),
      }
    },

    async applyFilters() {
      // History All-calls / analyzed stays on the table toolbar — not the modal.
      const next = {
        ...this.filters,
        search: (this.filters && this.filters.search) || '',
        callAnalysis: (this.appliedFilters && this.appliedFilters.callAnalysis) || 'all',
      }
      const wideRangeError = describeWideRangeFilterError(next, this._narrowingFilterOptions())
      if (wideRangeError) {
        this.$bvToast.toast(wideRangeError, {
          title: 'Add more filters',
          variant: 'warning',
          solid: true,
          autoHideDelay: 8000,
        })
        return
      }
      const prev = this.appliedFilters
      this.appliedFilters = next
      this.historySearchQuery = next.search || ''
      this.persistFiltersToSession()
      this.analysisFilterClientOnly = false
      if (
        (next.vertical && next.vertical !== 'all')
        || (next.category && next.category !== 'all')
        || (next.client && next.client !== 'all')
      ) {
        this._hydrateJobMetaForFilters()
      }
      const serverScopeChanged = !prev
        || prev.startDate !== this.appliedFilters.startDate
        || prev.endDate !== this.appliedFilters.endDate
        || prev.recruiter !== this.appliedFilters.recruiter
        || prev.manager !== this.appliedFilters.manager
      // Client / status / direction / candidateStatus / vertical / category are local —
      // refine the live period set. Date / recruiter / manager refetch now.
      if (!serverScopeChanged) {
        this._setCurrentPageSilent(1)
        this._ensurePeriodCache()
        return
      }
      this._clearPeriodCache()
      this._hydratePeriodCacheFromTtl()
      this.fetchCalls(1)
    },

    resetFilters() {
      const dates = defaultDateFilter()
      this.filters.datePreset = dates.datePreset
      this.filters.startDate = dates.startDate
      this.filters.endDate = dates.endDate
      this.filters.status = 'all'
      this.filters.direction = 'all'
      this.filters.recruiter = 'all'
      this.filters.manager = 'all'
      this.filters.client = 'all'
      this.filters.candidateStatus = 'all'
      this.filters.callAnalysis = 'all'
      this.filters.vertical = 'all'
      this.filters.category = 'all'
      this.filters.search = ''
      this.appliedFilters = {
        ...dates,
        status: 'all',
        direction: 'all',
        search: '',
        recruiter: 'all',
        manager: 'all',
        client: 'all',
        candidateStatus: 'all',
        callAnalysis: 'all',
        vertical: 'all',
        category: 'all',
      }
      this.historySearchQuery = ''
      this.analysisFilterClientOnly = false
      this.clearPersistedFilters()
      this._clearPeriodCache()
      this._hydratePeriodCacheFromTtl()
      this.fetchCalls(1)
    },

    // Filters modal + History toolbar: refetch API 1 with analyzed=.
    onAnalysisFilterChange(val) {
      this._applyCallAnalysisFilter(val)
    },

    _applyCallAnalysisFilter(val) {
      const next = val || 'all'
      const wasAnalysis = this.appliedFilters.callAnalysis === 'analyzed'
        || this.appliedFilters.callAnalysis === 'not_analyzed'
      // Snapshot unfiltered rows before history refetch replaces allCalls.
      if (!wasAnalysis && Array.isArray(this.allCalls) && this.allCalls.length) {
        this._captureAlertCallsSnapshot(
          Array.isArray(this.searchCallsCache) && this.searchCallsCache.length
            ? this.searchCallsCache
            : this.allCalls,
        )
      }
      this.filters.callAnalysis = next
      this.appliedFilters = { ...this.appliedFilters, callAnalysis: next }
      this.analysisFilterClientOnly = false
      this.persistFiltersToSession()
      this._setCurrentPageSilent(1)
      // History refetches with analyzed=; KPI live set is kept (same dates + emp + mgr).
      this.fetchCalls(1)
      this._ensurePeriodCache()
    },

    _periodCacheIsFresh() {
      const fetchedAt = Number(this.periodCacheFetchedAt) || 0
      if (!fetchedAt) return false
      return (Date.now() - fetchedAt) < PERIOD_CACHE_TTL_MS
    },

    _periodCacheCoversScope() {
      const key = this._alertScopeKey()
      if (this.periodCacheKey !== key || !Array.isArray(this.searchCallsCache)) return false
      if (!this._periodCacheIsFresh()) return false
      if (this.periodCacheComplete) return true
      const total = Number(this.periodTotalRecords) || 0
      return total > 0 && this.searchCallsCache.length >= total
    },

    /** True when period rows for the current filter window are already in memory. */
    _periodCacheHasScopeRows() {
      const key = this._alertScopeKey()
      return this.periodCacheKey === key && Array.isArray(this.searchCallsCache)
    },

    /** True when Team / Datewise / Hourly / Analysis / My Call Quality need the full period set. */
    _isAggregationTab() {
      // My view always shows Call Quality — date / search / recruiter must hit the full range.
      if (this.viewMode === 'my') return true
      if (this.tabIndex === 0 || this.tabIndex === 1 || this.tabIndex === 3) return true
      return this.tabIndex === 2 && this.historyTabIndex === 1
    },

    /**
     * Unique KPI cards need every row in the date / recruiter / search / client
     * window. API 1 `counts` are raw call rows — do not skip the drain for them.
     */
    _periodCacheNeeded() {
      return true
    },

    _trustApiNotAnsweredCount() {
      if (!this.hasApiKpiCounts) return false
      const n = this.periodSummaryCounts.not_answered
      if (!isPresentKpiCount(n)) return false
      if (Number(n) === 0 && this._loadedRowsHaveNotAnswered()) return false
      return true
    },

    _trustApiMissedCount() {
      if (!this.hasApiKpiCounts) return false
      const n = this.periodSummaryCounts.missed
      if (!isPresentKpiCount(n)) return false
      if (Number(n) === 0 && this._loadedRowsHaveMissed()) return false
      return true
    },

    _loadedRowsHaveNotAnswered() {
      if (this.recentNotAnswered.length) return true
      return listHasOutboundNoAnswer(this.allCalls) || listHasOutboundNoAnswer(this.periodRawCalls)
    },

    _loadedRowsHaveMissed() {
      if (this.missedInboundCalls.length) return true
      return listHasInboundMissed(this.allCalls) || listHasInboundMissed(this.periodRawCalls)
    },

    /**
     * Load all unfiltered pages for KPIs / cards / Analysis.
     * Skip when a complete set for this date / recruiter / manager is still
     * inside the 5-minute TTL, or a drain for that key is already in flight.
     */
    _ensurePeriodCache() {
      if (!this._periodCacheNeeded()) return
      const key = this._alertScopeKey()
      if (this._hydratePeriodCacheFromTtl()) return
      if (this._periodCacheCoversScope()) return
      if (this.isFetchingSearchCache && this.periodCacheFetchingKey === key) return
      this.fetchAllCallsCache()
    },

    _scheduleMissedWidgetCache() {
      if (this._missedWidgetTimer) clearTimeout(this._missedWidgetTimer)
      this._missedWidgetTimer = setTimeout(() => {
        this._missedWidgetTimer = null
        this._ensureMissedWidgetCache()
      }, 400)
    },

    _ensureHistoryFullPeriodCache() {
      this._ensurePeriodCache()
    },

    _setCurrentPageSilent(page) {
      this._pagingSilent = true
      this.currentPage = page
      this.$nextTick(() => { this._pagingSilent = false })
    },

    /** Cancel an in-flight axios slot (page / period / missed / historySearch). */
    _cancelRequest(slot) {
      const src = this[`_cancel_${slot}`]
      if (src) {
        try { src.cancel('superseded') } catch (e) { /* noop */ }
        this[`_cancel_${slot}`] = null
      }
    },

    _beginRequest(slot) {
      this._cancelRequest(slot)
      const src = axios.CancelToken.source()
      this[`_cancel_${slot}`] = src
      return src.token
    },

    _clearHistorySearchCache() {
      this.historySearchFetchGen += 1
      this._cancelRequest('historySearch')
      this.historySearchCache = null
      this.isFetchingHistorySearch = false
    },

    _clearPeriodCacheExpireTimer() {
      if (this._periodCacheExpireTimer) {
        clearTimeout(this._periodCacheExpireTimer)
        this._periodCacheExpireTimer = null
      }
    },

    _schedulePeriodCacheExpiry() {
      this._clearPeriodCacheExpireTimer()
      const fetchedAt = Number(this.periodCacheFetchedAt) || 0
      if (!fetchedAt) return
      const remain = PERIOD_CACHE_TTL_MS - (Date.now() - fetchedAt)
      if (remain <= 0) {
        this._onPeriodCacheExpired()
        return
      }
      this._periodCacheExpireTimer = setTimeout(() => {
        this._periodCacheExpireTimer = null
        this._onPeriodCacheExpired()
      }, remain)
    },

    _onPeriodCacheExpired() {
      const key = this._alertScopeKey()
      callAnalyticsService.invalidatePeriodListCache(key)
      if (this.periodCacheKey === key) {
        this.periodCacheComplete = false
        this.periodCacheFetchedAt = 0
      }
      this.fetchAllCallsCache()
    },

    /**
     * Restore a still-fresh 5-minute drain for the current scope.
     * Client / status / direction keep refining this same set.
     */
    _hydratePeriodCacheFromTtl() {
      const key = this._alertScopeKey()
      if (
        this.periodCacheKey === key
        && Array.isArray(this.searchCallsCache)
        && this.periodCacheComplete
        && this._periodCacheIsFresh()
      ) {
        this._schedulePeriodCacheExpiry()
        return true
      }
      const entry = callAnalyticsService.getPeriodListCache(key)
      if (!entry) return false
      this.searchCallsCache = entry.rows
      this.periodCacheKey = key
      this.periodTotalRecords = Number(entry.total) || entry.rows.length
      this.periodCacheComplete = true
      this.periodCacheFetchedAt = Number(entry.fetchedAt) || Date.now()
      this.periodSummaryCounts = entry.summaryCounts || null
      this.periodSummaryCountsKey = entry.summaryCounts ? key : ''
      this._captureAlertCallsSnapshot(entry.rows)
      this._schedulePeriodCacheExpiry()
      return true
    },

    _clearPeriodCache() {
      this.periodCacheFetchGen += 1
      this._cancelRequest('period')
      this._clearPeriodCacheExpireTimer()
      this.searchCallsCache = null
      this.isFetchingSearchCache = false
      this.periodCacheKey = ''
      this.periodCacheFetchingKey = ''
      this.periodCacheDrainKey = ''
      this.periodCacheFetchedAt = 0
      this.periodTotalRecords = 0
      this.periodCacheComplete = false
      this.periodSummaryCounts = null
      this.periodSummaryCountsKey = ''
      this.alertCallsCache = null
      this.alertCallsScopeKey = ''
      this.periodCacheInflight = null
      this._clearHistorySearchCache()
    },

    /** Drop leftover session maps that used to skip enrichment with stale IDs. */
    _clearPersistedCallRowCaches() {
      ;['myCalls_candidateMap', 'myCalls_jobMap', 'myCalls_phoneLookup'].forEach(key => {
        try { sessionStorage.removeItem(key) } catch (err) { /* noop */ }
      })
    },

    /**
     * Live period key: viewer|viewMode|startDate|endDate|emp_id|mgr_id
     * Date / recruiter / manager / My-Team-Org change is a new key (refetch now).
     * Vertical/category/search/KPI/client/status/direction refine the same
     * live rows — they do not restart the drain.
     */
    _alertScopeKey() {
      const af = this.appliedFilters || {}
      const params = this.buildCallAnalyticsParams()
      const viewer = (getUserData() || {}).emp_id || ''
      return [
        viewer,
        this.viewMode || '',
        af.startDate || '',
        af.endDate || '',
        params.emp_id || '',
        params.mgr_id || '',
      ].join('|')
    },

    _captureAlertCallsSnapshot(rows) {
      if (!Array.isArray(rows)) return
      this.alertCallsCache = rows
      this.alertCallsScopeKey = this._alertScopeKey()
    },

    /**
     * Not Answered / Missed use the full-period unfiltered live set (same key as KPIs).
     * Snapshot preferRows immediately so Analyzed-only never empties the cards.
     */
    _ensureAlertCallsSnapshot(preferRows) {
      if (Array.isArray(preferRows) && preferRows.length) {
        this._captureAlertCallsSnapshot(preferRows)
      }
      this._ensurePeriodCache()
    },

    async fetchAlertCallsCache() {
      this._ensurePeriodCache()
    },

    _missedWidgetScopeKey() {
      const params = this.buildCallAnalyticsParams()
      return [
        this.viewMode || '',
        params.emp_id || '',
        params.mgr_id || '',
        last7StartStr(),
        todayStr(),
      ].join('|')
    },

    _ensureMissedWidgetCache() {
      // Missed card + widget share the page date range via the live period set.
      // Do not fetch a separate rolling last-7 (that made 11 vs 108).
      return

      const start = last7StartStr()
      const today = todayStr()
      const key = this._missedWidgetScopeKey()
      if (
        this.missedWidgetCacheKey === key
        && this.missedWidgetCacheComplete
        && this.missedWidgetCacheStart
        && this.missedWidgetCacheEnd
        && this.missedWidgetCacheStart <= start
        && this.missedWidgetCacheEnd >= today
      ) return
      if (this.isFetchingMissedWidget && this.missedWidgetFetchingKey === key) return

      const periodCoversLast7 = periodCoversDay(this.appliedFilters.startDate, this.appliedFilters.endDate, start)
        && periodCoversDay(this.appliedFilters.startDate, this.appliedFilters.endDate, today)
      const apiTotal = Number(this.callAnalyticsTotalRecords) || 0
      const pageHasAll = periodCoversLast7
        && !this.historyAnalysisFilterActive
        && apiTotal > 0
        && Array.isArray(this.allCalls)
        && this.allCalls.length >= apiTotal
      const periodHasAll = periodCoversLast7
        && this.periodCacheReady
        && this.periodCacheKey === this._alertScopeKey()
      // Default last-7 + My view never drains the period cache — still fetch
      // every last-7 page so skipped inbound past page 1 appear in the card.
      if (pageHasAll || periodHasAll) return

      const missing = missedWidgetMissingRange(
        this.appliedFilters.startDate,
        this.appliedFilters.endDate,
        start,
        today,
      )
      this.fetchMissedWidgetCache(missing || { startDate: start, endDate: today })
    },

    /**
     * Fetch only the IST calendar days the page range is missing so the Missed
     * widget can show last 7 days without changing history / KPI dates.
     * API 1: GET /api/exotel/call-analytics/analysis-summary (call data +
     * analysis badges). Fat list only if that route 404s — goes away after deploy.
     */
    async fetchMissedWidgetCache(missing) {
      const key = this._missedWidgetScopeKey()
      const fetchGen = (this.missedWidgetFetchGen += 1)
      this.isFetchingMissedWidget = true
      this.missedWidgetFetchingKey = key

      try {
        const PAGE_LIMIT = 100
        const ANALYTICS_FETCH_TIMEOUT_MS = 45000
        const MAX_SCAN_PAGES = 100
        const analyticsParams = { ...this.buildCallAnalyticsParams() }
        delete analyticsParams.analyzed

        const mapBatch = rawRows => this._enrichCallsWithPhoneCandidates(mapCallBatch(rawRows))
        const seen = new Set()
        const reuseSameKey = this.missedWidgetCacheKey === key && Array.isArray(this.missedWidgetCache)
        const allRows = reuseSameKey ? this.missedWidgetCache.slice() : []
        allRows.forEach(row => markSeenCallRow(seen, row))
        let page = 1
        let scanApiTotal = 0
        let anchorRowId = null
        const cancelToken = this._beginRequest('missed')

        while (page <= MAX_SCAN_PAGES) {
          const response = await this._withTimeout(
            callAnalyticsService.getPeriodAnalytics({
              startDate: missing.startDate,
              endDate: missing.endDate,
              ...analyticsParams,
              page,
              limit: PAGE_LIMIT,
              offset: (page - 1) * PAGE_LIMIT,
              cancelToken,
            }),
            ANALYTICS_FETCH_TIMEOUT_MS,
            'call-analytics-missed-widget',
          ).catch(() => null)

          if (fetchGen !== this.missedWidgetFetchGen) return
          if (!response || response.status === 'CANCELLED') return
          if (!response || response.status !== 'OK') break

          scanApiTotal = Number(response.totalRecords) || scanApiTotal
          const pageRaw = Array.isArray(response.data) ? response.data : []
          if (!pageRaw.length) break

          const scanFirstId = pageRaw[0]?.id ?? pageRaw[0]?.call_id ?? null
          if (page === 1) {
            anchorRowId = scanFirstId
          } else if (scanFirstId != null && scanFirstId === anchorRowId) {
            break
          }

          mapBatch(pageRaw).forEach(row => {
            if (isSeenCallRow(seen, row)) return
            markSeenCallRow(seen, row)
            allRows.push(row)
          })

          await yieldToMain()
          if (fetchGen !== this.missedWidgetFetchGen) return

          if (pageRaw.length < PAGE_LIMIT) break
          if (scanApiTotal && page * PAGE_LIMIT >= scanApiTotal) break
          page += 1
        }

        if (fetchGen !== this.missedWidgetFetchGen) return

        const prevStart = reuseSameKey ? this.missedWidgetCacheStart : ''
        const prevEnd = reuseSameKey ? this.missedWidgetCacheEnd : ''
        this.missedWidgetCache = allRows
        this.missedWidgetCacheKey = key
        this.missedWidgetCacheStart = prevStart && prevStart < missing.startDate
          ? prevStart
          : missing.startDate
        this.missedWidgetCacheEnd = prevEnd && prevEnd > missing.endDate
          ? prevEnd
          : missing.endDate
        this.missedWidgetCacheComplete = true
      } catch (err) {
        if (fetchGen !== this.missedWidgetFetchGen) return
        console.error('[MyCalls] fetchMissedWidgetCache failed:', err)
      } finally {
        if (fetchGen === this.missedWidgetFetchGen) {
          this.isFetchingMissedWidget = false
          this.missedWidgetFetchingKey = ''
        }
      }
    },

    onHistorySearchChange(query) {
      const next = query || ''
      this.historySearchQuery = next
      this.filters.search = next
      this.appliedFilters = { ...this.appliedFilters, search: next }
      this._setCurrentPageSilent(1)
      if (next.trim()) this._ensurePeriodCache()
    },

    _ensureHistorySearchCache() {
      this._ensurePeriodCache()
    },

    /** Search uses the live period set — do not start a second drain. */
    fetchHistorySearchCache() {
      this._ensureHistorySearchCache()
    },

    /** Raw ?tab= slug from the current route (empty when absent). */
    _callAnalyticsTabQuery() {
      const raw = this.$route && this.$route.query ? this.$route.query.tab : ''
      if (raw == null || String(raw).trim() === '') return ''
      return String(raw).toLowerCase().trim()
    },

    /**
     * Map ?tab= to main / inner tab indexes.
     * Team/Datewise/Hourly only exist when viewMode !== 'my'.
     */
    _resolveCallAnalyticsTab(slug) {
      const key = String(slug || '').toLowerCase().trim()
      if (!key) return null
      const isMy = this.viewMode === 'my'
      const historyMain = isMy ? 0 : 2
      const aliases = {
        team: { tabIndex: 0, historyTabIndex: 0 },
        datewise: { tabIndex: 1, historyTabIndex: 0 },
        date: { tabIndex: 1, historyTabIndex: 0 },
        'date-wise': { tabIndex: 1, historyTabIndex: 0 },
        history: { tabIndex: historyMain, historyTabIndex: 0 },
        calls: { tabIndex: historyMain, historyTabIndex: 0 },
        'call-history': { tabIndex: historyMain, historyTabIndex: 0 },
        analysis: { tabIndex: historyMain, historyTabIndex: isMy ? 0 : 1 },
        summary: { tabIndex: historyMain, historyTabIndex: isMy ? 0 : 1 },
        'analysis-summary': { tabIndex: historyMain, historyTabIndex: isMy ? 0 : 1 },
        hourly: { tabIndex: 3, historyTabIndex: 0 },
        hour: { tabIndex: 3, historyTabIndex: 0 },
        0: { tabIndex: 0, historyTabIndex: 0 },
        1: { tabIndex: 1, historyTabIndex: 0 },
        2: { tabIndex: historyMain, historyTabIndex: 0 },
        3: { tabIndex: 3, historyTabIndex: 0 },
      }
      const next = aliases[key]
      if (!next) return null
      if (isMy && next.tabIndex !== 0) return { tabIndex: 0, historyTabIndex: 0 }
      return next
    },

    /** Apply ?tab= if present. Returns true when a valid tab was set. */
    _applyTabQuery() {
      const slug = this._callAnalyticsTabQuery()
      if (!slug) return false
      const next = this._resolveCallAnalyticsTab(slug)
      if (!next) return false
      this.tabIndex = next.tabIndex
      this.historyTabIndex = next.historyTabIndex
      return true
    },

    /**
     * Initial tab: ?tab= wins; org view (viewMode === 'org') opens Call History;
     * team / my keep the existing default (Team Performance / History).
     */
    _applyInitialCallAnalyticsTab() {
      if (this._applyTabQuery()) return
      if (this.viewMode === 'org') {
        this.tabIndex = 2
        this.historyTabIndex = 0
      }
    },

    /** Deep link from dialer: /apps/call-analytics?phone=9876543210 */
    _applyRoutePhoneDeepLink(rawPhone) {
      const phone = cleanPhone(rawPhone ?? this.$route?.query?.phone)
      if (!phone) return
      this.tabIndex = this.viewMode === 'my' ? 0 : 2
      this.historyTabIndex = 0
      this.historySearchQuery = phone
      this.filters.search = phone
      this.appliedFilters = { ...this.appliedFilters, search: phone }
      this._ensurePeriodCache()
    },

    /**
     * History-page date lift: when analysis-summary omitted created_at,
     * copy timestamps from the same fat GET /call-analytics page.
     */
    async _liftAnalysisSummaryPageDates(
      pageRaw,
      response,
      analyticsParams,
      pageNum,
      cancelToken,
      timeoutMs,
      label,
    ) {
      if (!Array.isArray(pageRaw) || !pageRaw.length) return pageRaw
      if (!pageMissingCallDates(pageRaw) || !response || response.source !== 'analysis-summary') {
        return pageRaw
      }
      const PAGE_LIMIT = 100
      const fat = await this._withTimeout(
        callAnalyticsService.getCallAnalytics({
          startDate: this.appliedFilters.startDate,
          endDate: this.appliedFilters.endDate,
          ...analyticsParams,
          page: pageNum,
          limit: PAGE_LIMIT,
          offset: (pageNum - 1) * PAGE_LIMIT,
          cancelToken,
        }),
        timeoutMs,
        label,
      ).catch(() => null)
      if (fat && fat.status === 'OK' && Array.isArray(fat.data)) {
        return mergeFatCallDates(pageRaw, fat.data)
      }
      return pageRaw
    },

    /**
     * Drain API 1 period rows for Team / Datewise / Hourly / Analysis / KPIs.
     * GET /exotel/call-analytics/analysis-summary (call data + analysis badges,
     * no full analysis / transcript). Fat list only if that route 404s —
     * goes away after deploy. Never analyzed=.
     */
    async fetchAllCallsCache() {
      const scopeKey = this._alertScopeKey()
      if (this._hydratePeriodCacheFromTtl()) return true
      if (this._periodCacheCoversScope()) return true
      if (this.isFetchingSearchCache && this.periodCacheFetchingKey === scopeKey) {
        return this.periodCacheInflight || Promise.resolve(true)
      }

      let settleInflight
      this.periodCacheInflight = new Promise(resolve => { settleInflight = resolve })

      const fetchGen = (this.periodCacheFetchGen += 1)
      this.isFetchingSearchCache = true
      this.periodCacheFetchingKey = scopeKey
      this.periodCacheComplete = false

      try {
        const canReuseHistoryPage = !this.historyAnalysisFilterActive
          && this.currentPage === 1
          && Array.isArray(this.allCalls)
        const listTotal = !this.historyAnalysisFilterActive
          ? (Number(this.callAnalyticsTotalRecords) || 0)
          : 0
        if (
          canReuseHistoryPage
          && listTotal > 0
          && this.allCalls.length >= listTotal
        ) {
          this._applyPeriodCacheRows(this.allCalls, scopeKey, listTotal, true)
          return
        }

        const PAGE_LIMIT = 100
        const ANALYTICS_FETCH_TIMEOUT_MS = 45000
        const MAX_SEARCH_SCAN_PAGES = 100
        const PAGE_CONCURRENCY = 3
        const APPLY_EVERY_PAGES = 12
        const analyticsParams = { ...this.buildCallAnalyticsParams() }
        delete analyticsParams.analyzed
        // Drain the lead's full Team / My scope. Client refines the live list locally
        // so changing Client does not restart a long-lived cache.
        delete analyticsParams.client
        delete analyticsParams.client_name
        delete analyticsParams.client_id

        const mapBatch = rawRows => this._enrichCallsWithPhoneCandidates(mapCallBatch(rawRows))
        const seen = new Set()
        let allRows = []
        let page = 1
        let scanApiTotal = listTotal
        const cancelToken = this._beginRequest('period')

        if (canReuseHistoryPage && this.allCalls.length) {
          this.allCalls.forEach(row => {
            if (isSeenCallRow(seen, row)) return
            markSeenCallRow(seen, row)
            allRows.push(row)
          })
          this._applyPeriodCacheRows(allRows, scopeKey, listTotal, false)
          page = 2
        }

        const fetchScanPage = pageNum => this._withTimeout(
          callAnalyticsService.getPeriodAnalytics({
            startDate: this.appliedFilters.startDate,
            endDate: this.appliedFilters.endDate,
            ...analyticsParams,
            page: pageNum,
            limit: PAGE_LIMIT,
            offset: (pageNum - 1) * PAGE_LIMIT,
            cancelToken,
          }),
          ANALYTICS_FETCH_TIMEOUT_MS,
          'call-analytics-cache',
        ).catch(() => null)

        let pagesSinceApply = 0
        let hitEnd = false

        while (page <= MAX_SEARCH_SCAN_PAGES && !hitEnd) {
          if (fetchGen !== this.periodCacheFetchGen) return

          const knownPages = scanApiTotal
            ? Math.min(MAX_SEARCH_SCAN_PAGES, Math.ceil(scanApiTotal / PAGE_LIMIT))
            : MAX_SEARCH_SCAN_PAGES
          const batch = []
          while (batch.length < PAGE_CONCURRENCY && page <= knownPages) {
            batch.push(page)
            page += 1
          }
          if (!batch.length) break

          const responses = await Promise.all(batch.map(p => fetchScanPage(p)))
          if (fetchGen !== this.periodCacheFetchGen) return

          for (let i = 0; i < responses.length; i += 1) {
            const response = responses[i]
            if (!response || response.status === 'CANCELLED') return
            if (!response || response.status !== 'OK') {
              hitEnd = true
              break
            }
            scanApiTotal = Number(response.totalRecords) || scanApiTotal
            this._capturePeriodSummaryCounts(response, scopeKey)
            let pageRaw = Array.isArray(response.data) ? response.data : []
            pageRaw = await this._liftAnalysisSummaryPageDates(
              pageRaw,
              response,
              analyticsParams,
              batch[i],
              cancelToken,
              ANALYTICS_FETCH_TIMEOUT_MS,
              'call-analytics-cache-dates',
            )
            if (fetchGen !== this.periodCacheFetchGen) return
            if (!pageRaw.length) {
              hitEnd = true
              break
            }
            mapBatch(pageRaw).forEach(row => {
              if (isSeenCallRow(seen, row)) return
              markSeenCallRow(seen, row)
              allRows.push(row)
            })
            if (pageRaw.length < PAGE_LIMIT) hitEnd = true
          }

          pagesSinceApply += batch.length
          const firstPaint = !Array.isArray(this.searchCallsCache)
          if (firstPaint || pagesSinceApply >= APPLY_EVERY_PAGES || hitEnd) {
            this._applyPeriodCacheRows(allRows, scopeKey, scanApiTotal, false)
            pagesSinceApply = 0
            await yieldToMain()
            if (fetchGen !== this.periodCacheFetchGen) return
          }

          if (scanApiTotal && allRows.length >= scanApiTotal) break
        }

        if (fetchGen !== this.periodCacheFetchGen) return

        const recruiterNames = { ...this.apiRecruiterNames }
        allRows.forEach(c => {
          if (c._rawRecruiter && c._recruiterName) {
            recruiterNames[c._rawRecruiter] = c._recruiterName
          }
        })
        this.apiRecruiterNames = recruiterNames

        const complete = !scanApiTotal || allRows.length >= scanApiTotal
        this._applyPeriodCacheRows(allRows, scopeKey, scanApiTotal, complete)
      } catch (err) {
        if (fetchGen !== this.periodCacheFetchGen) return
        console.error('[MyCalls] fetchAllCallsCache failed:', err)
        this.$bvToast.toast(
          'Could not load all calls for KPIs and reports. Try a smaller date range.',
          { title: 'Load Failed', variant: 'warning', solid: true, autoHideDelay: 8000 },
        )
      } finally {
        if (fetchGen === this.periodCacheFetchGen) {
          this.isFetchingSearchCache = false
          this.periodCacheFetchingKey = ''
          this.periodCacheDrainKey = this.periodCacheComplete ? scopeKey : ''
        }
        if (typeof settleInflight === 'function') settleInflight()
        if (fetchGen === this.periodCacheFetchGen) this.periodCacheInflight = null
      }
    },

    _capturePeriodSummaryCounts(response, scopeKey) {
      if (!response || response.status !== 'OK') return
      const counts = response.kpiCounts
      if (!counts || typeof counts !== 'object') return
      const key = scopeKey || this._alertScopeKey()
      this.periodSummaryCounts = counts
      this.periodSummaryCountsKey = key
      if (counts.total_calls != null && Number(counts.total_calls) > 0) {
        this.periodTotalRecords = Number(counts.total_calls)
      }
    },

    _applyPeriodCacheRows(rows, scopeKey, total, complete) {
      const list = dedupeMappedCalls((Array.isArray(rows) ? rows : []).map(stripMappedCallTranscripts))
      this.searchCallsCache = list
      this.periodCacheKey = scopeKey
      if (total > 0) this.periodTotalRecords = total
      this.periodCacheComplete = !!complete
      if (complete) {
        this.periodCacheFetchedAt = Date.now()
        callAnalyticsService.setPeriodListCache(scopeKey, {
          rows: list,
          total: this.periodTotalRecords,
          summaryCounts: this.periodSummaryCountsKey === scopeKey ? this.periodSummaryCounts : null,
        })
        this._schedulePeriodCacheExpiry()
      }
      this._captureAlertCallsSnapshot(list)
      const af = this.appliedFilters || {}
      if (
        (af.vertical && af.vertical !== 'all')
        || (af.category && af.category !== 'all')
        || (af.client && af.client !== 'all')
      ) {
        this._hydrateJobMetaForFilters(list)
      }
    },

    /** @deprecated Alias for fetchAllCallsCache — kept for any stale callers. */
    fetchAllCallsForSearch() {
      return this.fetchAllCallsCache()
    },

    // ── Data fetching ────────────────────────────────────────────────────────

    buildCallAnalyticsParams() {
      const { emp_id: myEmpId } = getUserData()
      const af = this.appliedFilters
      const params = {}

      const canViewTeam = this.$can('read', 'account_manager') || this.canManageCalls
      const isTalentAdvisor = !this.isAdminLike && !canViewTeam
      if (isTalentAdvisor) {
        params.emp_id = myEmpId
      } else {
        if (this.viewMode === 'my') {
          params.emp_id = myEmpId
        }

        if (af.recruiter && af.recruiter !== 'all') {
          params.emp_id = af.recruiter
        }

        if (af.manager && af.manager !== 'all') {
          params.mgr_id = af.manager
        }

        if (
          this.viewMode === 'team'
          && canViewTeam
          && !params.emp_id
          && !params.mgr_id
        ) {
          params.mgr_id = myEmpId
        }
      }

      // Prefer server-side analysis filter when backend supports `analyzed`.
      // Without API support, client still filters the current page via hasCompletedAnalysis.
      if (af.callAnalysis === 'analyzed') {
        params.analyzed = true
      } else if (af.callAnalysis === 'not_analyzed') {
        params.analyzed = false
      }

      if (af.vertical && af.vertical !== 'all') params.vertical = af.vertical
      if (af.category && af.category !== 'all') params.category = af.category
      // Hint only — prod get_enriched_call_logs may ignore these. Live-list
      // Client filter still runs in filterCallsList for Team / My / History / KPIs.
      if (af.client && af.client !== 'all') {
        params.client = af.client
        params.client_name = af.client
        const target = resolveClientFilterTarget(af.client, this.clientsList)
        const numericId = target.ids && [...target.ids].find(id => /^\d+$/.test(id))
        if (numericId) params.client_id = numericId
      }

      params.sort = 'created_at'
      params.order = 'desc'

      return params
    },

    /**
     * Load ProfileShared totals per recruiter for Org/Team Performance "Not Called".
     * Same source + params as Recruiter Work Tracker:
     * GET /api/report/recruiter/tracker?startDate&endDate&emp_id=<viewer>
     * Field: ProfileShared (summed across client/position rows per recruiter).
     */
    _aggregateRecruiterProfileShared(raw) {
      const list = Array.isArray(raw) ? raw : []
      if (!list.length) return {}
      const af = this.appliedFilters || {}
      const clientTarget = (af.client && af.client !== 'all')
        ? resolveClientFilterTarget(af.client, this.clientsList)
        : null
      const nameToEmpId = {}
      Object.entries(this.recruiterNameMap || {}).forEach(([id, name]) => {
        const n = normalizePersonName(name)
        if (n && !nameToEmpId[n]) nameToEmpId[n] = String(id)
      })
      const map = {}
      const addToKeys = (keys, count) => {
        keys.forEach(k => {
          if (!k) return
          map[k] = (map[k] || 0) + count
        })
      }
      list.forEach(amData => {
        if (!amData.data || !Array.isArray(amData.data)) return
        amData.data.forEach(recData => {
          if (!recData.tracker || !Array.isArray(recData.tracker)) return
          const recName = recData.rec_name
            || recData.Recruiter
            || recData.recruiter
            || ''
          const recIdFromRow = recData.rec_id
            || recData.RecId
            || recData.emp_id
            || recData.RecruiterId
            || recData.recruiterId
            || null
          let profileSharedSum = 0
          let matchedLine = false
          let recIdFromTracker = null
          let lineRecruiterName = recName
          recData.tracker.forEach(trackerItem => {
            if (clientTarget && clientTarget.want && !trackerLineMatchesClientTarget(trackerItem, clientTarget)) {
              return
            }
            matchedLine = true
            const rawShared = trackerItem.ProfileShared != null
              ? trackerItem.ProfileShared
              : (trackerItem.PS != null
                ? trackerItem.PS
                : (trackerItem.profileShared != null
                  ? trackerItem.profileShared
                  : trackerItem.profile_shared))
            profileSharedSum += parseTrackerCount(rawShared)
            if (!recIdFromTracker) {
              recIdFromTracker = trackerItem.RecId
                || trackerItem.RecruiterId
                || trackerItem.rec_id
                || trackerItem.recruiterId
                || null
            }
            if (!lineRecruiterName) {
              lineRecruiterName = trackerItem.Recruiter || trackerItem.recruiter || ''
            }
          })
          if (clientTarget && clientTarget.want && !matchedLine) return
          const resolvedFromName = lineRecruiterName
            ? nameToEmpId[normalizePersonName(lineRecruiterName)]
            : null
          const keys = new Set()
          if (recIdFromRow) keys.add(String(recIdFromRow))
          if (recIdFromTracker) keys.add(String(recIdFromTracker))
          if (resolvedFromName) keys.add(String(resolvedFromName))
          if (lineRecruiterName) keys.add(`name:${normalizePersonName(lineRecruiterName)}`)
          if (!keys.size) return
          addToKeys(keys, profileSharedSum)
        })
      })
      return map
    },

    async fetchRecruiterProfileSharedTotals() {
      const showAnalytics = (this.$can('read', 'account_manager') && this.viewMode === 'team')
        || (this.isAdminLike && (this.viewMode === 'team' || this.viewMode === 'org'))
      if (!showAnalytics) {
        this.recruiterTrackerRaw = []
        return
      }

      try {
        const { emp_id: viewerEmpId } = getUserData() || {}
        const res = await reportService.getRecTracker(
          this.appliedFilters.startDate,
          this.appliedFilters.endDate,
          viewerEmpId || '',
        )
        this.recruiterTrackerRaw = (res && res.status === 'OK' && Array.isArray(res.data))
          ? res.data
          : []
      } catch (err) {
        console.warn('[MyCalls] recruiter tracker ProfileShared totals failed:', err)
        this.recruiterTrackerRaw = []
      }
    },

    /**
     * Detect when backend ignored analyzed=true/false (client must filter the page).
     * Signals: page has rows that contradict the requested filter, or a large API total
     * with zero matching rows on a full page.
     */
    _detectAnalysisFilterClientOnly(requestedAnalyzed, pageMapped, pageTotal) {
      if (requestedAnalyzed !== true && requestedAnalyzed !== false) return false
      if (!pageMapped.length) {
        // Empty page with a huge total usually means pagination over an unfiltered set.
        return pageTotal > (Number(this.perPage) || 50)
      }
      const wantAnalyzed = requestedAnalyzed === true
      const mismatch = pageMapped.some(c => hasCompletedAnalysis(c) !== wantAnalyzed)
      if (mismatch) return true
      // Zero matches on a non-final page → API almost certainly ignored analyzed=.
      const matches = pageMapped.filter(c => hasCompletedAnalysis(c) === wantAnalyzed).length
      return matches === 0 && pageTotal > pageMapped.length
    },

    /**
     * Classic pagination fetch: load exactly one page (replace, never append/drain).
     * - Open Call Analytics / Refresh / date apply / filter apply → page 1.
     * - Pagination click → that page only via page + offset/limit.
     * - Never loops pages or prefetches aliases. Entire body is try/finally.
     */
    async fetchCalls(page = 1) {
      const targetPage = Math.max(1, Number(page) || 1)
      const fetchGen = (this.fetchGeneration += 1)
      this.enrichFetchGen += 1
      if (this._enrichTimer) {
        clearTimeout(this._enrichTimer)
        this._enrichTimer = null
      }
      let pageReady = false

      // Full spinner on first load / date-filter reset; lighter flag on page clicks.
      // Keep prior rows visible while paging so we never flash "0 on this page" + spinner.
      if (!this._loadedAnalyticsPage || targetPage === 1) {
        this.isLoading = true
        this.isFetchingMore = false
        this._armAnalyticsLoadingGuard()
      } else {
        this.isFetchingMore = true
      }

      try {
        const PAGE_LIMIT = Math.min(Math.max(1, Number(this.perPage) || 50), 100)
        const analyticsParams = this.buildCallAnalyticsParams()
        const requestedAnalyzed = analyticsParams.analyzed
        const analysisFilterActive = requestedAnalyzed === true || requestedAnalyzed === false

        if (targetPage === 1) {
          const nextKey = this._alertScopeKey()
          const sameScope = this.periodCacheKey === nextKey
            || this.periodCacheFetchingKey === nextKey
          this._clearHistorySearchCache()
          if (!sameScope) {
            this._clearPeriodCache()
          }
          this.apiRecruiterNames = {}
          this.calledBackKeys = []
        }

        this._setCurrentPageSilent(targetPage)

        const ANALYTICS_FETCH_TIMEOUT_MS = 45000
        const cancelToken = this._beginRequest('page')
        const response = await this._withTimeout(
          callAnalyticsService.getPeriodAnalytics({
            startDate: this.appliedFilters.startDate,
            endDate: this.appliedFilters.endDate,
            ...analyticsParams,
            page: targetPage,
            limit: PAGE_LIMIT,
            offset: (targetPage - 1) * PAGE_LIMIT,
            cancelToken,
          }),
          ANALYTICS_FETCH_TIMEOUT_MS,
          'call-analytics',
        ).catch(err => ({
          status: 'FAILED',
          message: (err && err.message) || 'Call analytics request failed',
          data: { message: (err && err.message) || 'Call analytics request failed' },
        }))

        if (fetchGen !== this.fetchGeneration) return
        if (response && response.status === 'CANCELLED') return

        if (!response || response.status !== 'OK') {
          const httpStatus = response?.status
          const apiMessage = response?.data?.message || response?.data?.error || response?.message
          const detail = apiMessage
            || (httpStatus != null ? `HTTP ${httpStatus}` : 'No response from server')
          console.error('[MyCalls] call-analytics failed:', detail, response)
          // Soft-fail: clear data + toast — never leave Org/Team KPIs spinning.
          this.$bvToast.toast(
            `Could not load call analytics. ${detail}`,
            { title: 'Load Failed', variant: 'danger', solid: true, autoHideDelay: 8000 },
          )
          this.allCalls = []
          this.callAnalyticsTotalRecords = 0
          this.analysisFilterClientOnly = false
          this._loadedAnalyticsPage = targetPage
          return
        }

        let pageRaw = Array.isArray(response.data) ? response.data : []
        const pageTotal = Number(response.totalRecords) || 0
        // History only: if API 1 omitted every timestamp, pull the same
        // page/limit from fat GET /call-analytics and lift dates onto these rows.
        if (pageMissingCallDates(pageRaw) && response.source === 'analysis-summary') {
          const fat = await this._withTimeout(
            callAnalyticsService.getCallAnalytics({
              startDate: this.appliedFilters.startDate,
              endDate: this.appliedFilters.endDate,
              ...analyticsParams,
              page: targetPage,
              limit: PAGE_LIMIT,
              offset: (targetPage - 1) * PAGE_LIMIT,
              cancelToken,
            }),
            ANALYTICS_FETCH_TIMEOUT_MS,
            'call-analytics-history-dates',
          ).catch(() => null)
          if (fetchGen !== this.fetchGeneration) return
          if (fat && fat.status === 'CANCELLED') return
          if (fat && fat.status === 'OK' && Array.isArray(fat.data)) {
            pageRaw = mergeFatCallDates(pageRaw, fat.data)
          }
        }
        if (targetPage === 1 && !analysisFilterActive) {
          this._capturePeriodSummaryCounts(response, this._alertScopeKey())
        }
        const serverSideAnalysis = response.filters?.serverSideAnalysisFilter === true

        const mapBatch = rawRows => mapCallBatch(rawRows)

        // One page per action — no extra-page scan / alias drain.
        // Never client-filter analyzed on this page (that produced "0 of 3645").
        const batchMapped = sortCallsNewestFirst(mapBatch(pageRaw).map(stripMappedCallTranscripts))
        const lastApiTotal = pageTotal
        const pageMapped = batchMapped.slice(0, PAGE_LIMIT)

        pageMapped.forEach(c => {
          if (c._rawRecruiter && c._recruiterName) {
            this.apiRecruiterNames[c._rawRecruiter] = c._recruiterName
          }
        })

        // Replace — never merge/append pages (avoids pull-all / auto-drain).
        this.allCalls = this._enrichCallsWithPhoneCandidates(pageMapped)
        this._hydrateMapsFromCallRows(this.allCalls)
        this.analysisFilterClientOnly = analysisFilterActive
          && !serverSideAnalysis
          && this._detectAnalysisFilterClientOnly(
            requestedAnalyzed,
            analysisFilterActive && !serverSideAnalysis ? batchMapped : this.allCalls,
            lastApiTotal,
          )
        this.callAnalyticsTotalRecords = lastApiTotal || this.allCalls.length
        if (
          this.analysisFilterClientOnly
          && targetPage === 1
          && (requestedAnalyzed === true || requestedAnalyzed === false)
        ) {
          this.$bvToast.toast(
            'Call Analysis filter needs backend support for analyzed=true|false. Showing this API page as returned — not a client-only slice.',
            { title: 'Partial filter', variant: 'warning', solid: true, autoHideDelay: 10000 },
          )
        }
        this._loadedAnalyticsPage = targetPage

        this.loadCallNotes()

        // Seed this page's totals + first page so KPIs paint while remaining pages load.
        if (targetPage === 1) {
          const nextKey = this._alertScopeKey()
          if (!analysisFilterActive && lastApiTotal > 0) {
            this.periodTotalRecords = lastApiTotal
            if (this.allCalls.length >= lastApiTotal) {
              this.periodCacheFetchGen += 1
              this._cancelRequest('period')
              this.isFetchingSearchCache = false
              this.periodCacheFetchingKey = ''
              this._applyPeriodCacheRows(this.allCalls, nextKey, lastApiTotal, true)
            } else if (this.periodCacheKey !== nextKey || !Array.isArray(this.searchCallsCache)) {
              this._applyPeriodCacheRows(this.allCalls, nextKey, lastApiTotal, false)
            }
          }
        }
        pageReady = true
      } catch (err) {
        if (fetchGen !== this.fetchGeneration) return
        console.error('[MyCalls] Failed to fetch calls:', err)
        this.allCalls = []
        this.callAnalyticsTotalRecords = 0
        this.analysisFilterClientOnly = false
        this._loadedAnalyticsPage = targetPage
        this.$bvToast.toast(
          'Could not load call analytics. Please try again.',
          { title: 'Load Failed', variant: 'danger', solid: true, autoHideDelay: 8000 },
        )
      } finally {
        // Only the latest generation owns the spinner. Stale gens must not clear
        // it early — but the latest gen always reaches this finally (setup is inside try).
        if (fetchGen === this.fetchGeneration) {
          this.isLoading = false
          this.isFetchingMore = false
          this._clearAnalyticsLoadingGuard()
        }
      }

      if (!pageReady || fetchGen !== this.fetchGeneration) return
      this._scheduleVisibleEnrichment(this.allCalls)
      await this.$nextTick()
      if (fetchGen !== this.fetchGeneration) return
      if (this._periodCacheNeeded()) this._ensurePeriodCache()
    },

    /** @deprecated Use fetchCalls(page). Kept so any leftover callers still hit classic paging. */
    fetchMoreCalls() {
      if (this.isFetchingMore || this.isLoading) return
      if (!this.hasMoreCalls) return
      return this.fetchCalls(this.currentPage + 1)
    },

    _enrichCallsWithPhoneCandidates(rows) {
      const phoneToCandidate = {}
      rows.forEach(c => {
        if (c.candidateId && c.phone && c.phone !== '—') {
          phoneToCandidate[c.phone] = {
            candidateId: c.candidateId,
            candidateIdDisplay: c.candidateIdDisplay,
          }
        }
      })
      return rows.map(c => {
        if (!c.candidateId && c.phone && c.phone !== '—' && phoneToCandidate[c.phone]) {
          return Object.freeze({
            ...c,
            candidateId: phoneToCandidate[c.phone].candidateId,
            candidateIdDisplay: phoneToCandidate[c.phone].candidateIdDisplay,
          })
        }
        return c
      })
    },

    /** Seed candidate/job maps from already-mapped call rows — no API. */
    _hydrateMapsFromCallRows(rows) {
      if (!Array.isArray(rows) || !rows.length) return
      const nextCandidates = { ...this.candidateMap }
      const nextJobs = { ...this.jobMap }
      let cChanged = false
      let jChanged = false
      rows.forEach(c => {
        if (c.candidateId) {
          const prev = nextCandidates[c.candidateId]
          const name = (c.candidate && c.candidate !== '—') ? c.candidate : ''
          const phone = (c.phone && c.phone !== '—') ? c.phone : ''
          const email = (c.email && String(c.email).trim()) ? c.email : ''
          if (!prev) {
            nextCandidates[c.candidateId] = { name, phone, email }
            cChanged = true
          } else if ((name && !prev.name) || (phone && !prev.phone) || (email && !prev.email)) {
            nextCandidates[c.candidateId] = {
              ...prev,
              name: prev.name || name,
              phone: prev.phone || phone,
              email: prev.email || email,
            }
            cChanged = true
          }
        }
        if (c.jobId) {
          const prev = nextJobs[c.jobId]
          const position = c.job || ''
          const client = c.clientName || ''
          const code = c.jobCode || ''
          const vertical = resolveCallJobVertical(c, prev || {})
          const category = resolveCallJobCategory(c, prev || {})
          if (!prev) {
            nextJobs[c.jobId] = { position, client, code, vertical, category }
            jChanged = true
          } else if (
            (client && !prev.client)
            || (position && !prev.position)
            || (vertical && !prev.vertical)
            || (category && !prev.category)
          ) {
            nextJobs[c.jobId] = {
              position: prev.position || position,
              client: prev.client || client,
              code: prev.code || code,
              vertical: prev.vertical || vertical,
              category: prev.category || category,
            }
            jChanged = true
          }
        }
      })
      if (cChanged) {
        this.candidateMap = nextCandidates
        if (this._flushCandidateMap) this._flushCandidateMap()
      }
      if (jChanged) {
        this.jobMap = nextJobs
        if (this._flushJobMap) this._flushJobMap()
      }
    },

    _mergeCandidateJobsIntoJobMap(jobs) {
      if (!Array.isArray(jobs) || !jobs.length) return
      let changed = false
      const next = { ...this.jobMap }
      jobs.forEach(j => {
        const id = j && (j.id != null ? j.id : (j.job_id != null ? j.job_id : j.jobId))
        if (id == null) return
        const vertical = String(j.vertical || j.job_vertical || '').trim()
        const category = String(j.category || j.job_category || '').trim()
        const prev = next[id] || {}
        if ((vertical && !prev.vertical) || (category && !prev.category)) {
          next[id] = {
            ...prev,
            vertical: prev.vertical || vertical,
            category: prev.category || category,
            position: prev.position || j.position_name || j.position || '',
            client: prev.client || j.client_name || j.client || '',
          }
          changed = true
        }
      })
      if (changed) {
        this.jobMap = next
        if (this._flushJobMap) this._flushJobMap()
      }
    },

    _jobRecordFromData(res) {
      if (!res) return null
      const body = res.data && typeof res.data === 'object' && (res.data.category || res.data.vertical || res.data.data)
        ? res.data
        : res
      if (body && body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
        return body.data
      }
      return body && typeof body === 'object' ? body : null
    },

    async _hydrateJobMetaForFilters(preferRows) {
      const af = this.appliedFilters || {}
      const wantClient = af.client && af.client !== 'all'
      if (
        (!af.vertical || af.vertical === 'all')
        && (!af.category || af.category === 'all')
        && !wantClient
      ) return
      const rows = Array.isArray(preferRows) && preferRows.length
        ? preferRows
        : (Array.isArray(this.searchCallsCache) && this.searchCallsCache.length
          ? this.searchCallsCache
          : this.allCalls)
      const missing = []
      const seen = new Set()
      ;(Array.isArray(rows) ? rows : []).forEach(c => {
        const id = c && (c.jobId != null ? c.jobId : c.job_id)
        if (id == null) return
        const key = String(id)
        if (seen.has(key)) return
        seen.add(key)
        const meta = this.jobMap[id] || this.jobMap[key] || {}
        const hasV = resolveCallJobVertical(c, meta)
        const hasC = resolveCallJobCategory(c, meta)
        const hasClient = resolveCallClientName(this, c) || (meta && (meta.client || meta.client_name))
        if (
          (af.vertical && af.vertical !== 'all' && !hasV)
          || (af.category && af.category !== 'all' && !hasC)
          || (wantClient && !hasClient)
        ) {
          missing.push(id)
        }
      })
      const MAX_JOB_LOOKUPS = 40
      const slice = missing.slice(0, MAX_JOB_LOOKUPS)
      if (!slice.length) return
      const CONCURRENCY = 3
      let cursor = 0
      const next = { ...this.jobMap }
      let changed = false
      const worker = async () => {
        while (cursor < slice.length) {
          const id = slice[cursor]
          cursor += 1
          try {
            const res = await jobService.getJobData(id)
            const rec = this._jobRecordFromData(res)
            if (!rec) continue
            const vertical = String(rec.vertical || rec.job_vertical || '').trim()
            const category = String(rec.category || rec.job_category || '').trim()
            const client = String(rec.client_name || rec.client || rec.clientName || rec.title || '').trim()
            if (!vertical && !category && !client) continue
            const prev = next[id] || {}
            next[id] = {
              ...prev,
              vertical: prev.vertical || vertical,
              category: prev.category || category,
              position: prev.position || rec.position_name || rec.position || '',
              client: prev.client || client,
              client_id: prev.client_id || rec.client_id || rec.clientId || '',
            }
            changed = true
          } catch (e) { /* skip one job */ }
        }
      }
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, slice.length) }, () => worker()))
      if (changed) {
        this.jobMap = next
        if (this._flushJobMap) this._flushJobMap()
      }
    },

    /** Debounce ATS lookups so first paint never waits on candidate APIs. */
    _scheduleVisibleEnrichment(rows) {
      if (this._enrichTimer) clearTimeout(this._enrichTimer)
      const snapshot = Array.isArray(rows) ? rows : []
      this._enrichTimer = setTimeout(() => {
        this._enrichTimer = null
        this._enrichVisiblePage(snapshot)
      }, 80)
    },

    _parseCandidateView(res) {
      if (!res || res.status !== 'OK' || !res.data) return null
      const data = res.data
      const row = Array.isArray(data.candidateData) ? data.candidateData[0] : (data.candidateData || data)
      if (!row || typeof row !== 'object') return null
      const jobs = data.candidateJobs || data.candidate_jobs || row.candidateJobs || []
      return {
        name: row.name || row.candidate_name || row.candidateName || '',
        phone: row.contact_no || row.phone || '',
        email: row.email || row.candidate_email || row.candidateEmail || '',
        candidateJobs: Array.isArray(jobs) ? jobs : [],
      }
    },

    _parseCandidateExist(res) {
      if (!res) return null
      if (res.status === 400 || res.status === 404) return null
      const body = (res.data && typeof res.data === 'object' && ('status' in res.data || 'data' in res.data))
        ? res.data
        : res
      if (!body || body.status !== 'OK') return null
      const data = body.data
      if (Array.isArray(data)) return data.length ? data[0] : null
      if (data && typeof data === 'object' && (data.id || data.candidate_id)) return data
      return null
    },

    async _enrichVisiblePage(rows) {
      const list = Array.isArray(rows) ? rows : []
      if (!list.length) return
      const gen = (this.enrichFetchGen += 1)
      this._hydrateMapsFromCallRows(list)

      const missingIds = []
      const seenIds = new Set()
      const missingPhones = []
      const seenPhones = new Set()
      list.forEach(c => {
        if (c.candidateId) {
          const key = String(c.candidateId)
          if (seenIds.has(key)) return
          seenIds.add(key)
          const entry = this.candidateMap[c.candidateId]
          // Always load candidateJobs — extra_details holds call notes + reminders.
          // Skipping when the row already has a name left production lists empty.
          if (entry && Array.isArray(entry.candidateJobs)) return
          missingIds.push(c.candidateId)
        } else if (c.phone && c.phone !== '—') {
          if (seenPhones.has(c.phone) || this.phoneLookupCache[c.phone] !== undefined) return
          seenPhones.add(c.phone)
          missingPhones.push(c.phone)
        }
      })

      if (missingIds.length) {
        await candidateService.getCandidatesByIds(missingIds, {
          concurrency: 4,
          shouldAbort: () => gen !== this.enrichFetchGen,
          onEach: (id, res) => {
            if (gen !== this.enrichFetchGen) return
            const parsed = this._parseCandidateView(res)
            if (!parsed) return
            this.$set(this.candidateMap, id, {
              ...(this.candidateMap[id] || {}),
              name: parsed.name || this.candidateMap[id]?.name || '',
              phone: parsed.phone || this.candidateMap[id]?.phone || '',
              email: parsed.email || this.candidateMap[id]?.email || '',
              candidateJobs: parsed.candidateJobs,
            })
            this._mergeCandidateJobsIntoJobMap(parsed.candidateJobs)
          },
        })
        if (gen !== this.enrichFetchGen) return
        if (this._flushCandidateMap) this._flushCandidateMap()
        this.loadCallNotes()
      }

      if (missingPhones.length) {
        await this._lookupPhonesForPage(missingPhones, gen)
      }
    },

    async _lookupPhonesForPage(phones, gen) {
      const list = Array.isArray(phones) ? phones : []
      if (!list.length) return
      const CONCURRENCY = 3
      let cursor = 0
      const patches = []

      const worker = async () => {
        while (cursor < list.length) {
          if (gen !== this.enrichFetchGen) return
          const phone = list[cursor]
          cursor += 1
          if (this.phoneLookupCache[phone] !== undefined) continue
          try {
            const res = await candidateService.checkCandidateExist('', phone)
            if (gen !== this.enrichFetchGen) return
            const raw = this._parseCandidateExist(res)
            const id = raw && (raw.id || raw.candidate_id)
            this.$set(this.phoneLookupCache, phone, id || null)
            if (id) {
              const name = raw.name || raw.candidate_name || ''
              this.$set(this.candidateMap, id, {
                ...(this.candidateMap[id] || {}),
                name: name || this.candidateMap[id]?.name || '',
                phone,
                email: raw.email || raw.candidate_email || this.candidateMap[id]?.email || '',
              })
              patches.push({ phone, id, name })
            }
          } catch (e) {
            this.$set(this.phoneLookupCache, phone, null)
          }
        }
      }

      await Promise.all(Array.from(
        { length: Math.min(CONCURRENCY, list.length) },
        () => worker(),
      ))
      if (gen !== this.enrichFetchGen) return
      if (patches.length) {
        const byPhone = {}
        patches.forEach(p => { byPhone[p.phone] = p })
        this.allCalls = this.allCalls.map(c => {
          if (c.candidateId || !byPhone[c.phone]) return c
          const p = byPhone[c.phone]
          return Object.freeze({
            ...c,
            candidateId: p.id,
            candidateIdDisplay: `SHC-${Number(p.id) + 1000}`,
            candidate: p.name || c.candidate,
          })
        })
      }
      if (this._flushCandidateMap) this._flushCandidateMap()
      if (this._flushPhoneLookup) this._flushPhoneLookup()
    },

    // ── Column helpers ───────────────────────────────────────────────────────

    getCandidateName(item) {
      if (item.candidate && item.candidate !== '—') return item.candidate
      if (item.candidateId && this.candidateMap[item.candidateId]?.name) {
        return this.candidateMap[item.candidateId].name
      }
      return null
    },

    hasDisplayPhone(item) {
      return Boolean(item?.phone && item.phone !== '—')
    },

    canDialItem(item) {
      if (!this.hasDisplayPhone(item)) return false
      if (item.phoneDialable === true) return true
      if (item.phoneDialable === false) return false
      return isDialableIndianPhone(item.phone)
    },

    canDialReminder(reminder) {
      return isDialableIndianPhone(reminder?.phone)
    },

    getCandidateStatus(item) {
      return resolveCallCandidateStatus(item, candidateStatusJobMatch(this, item)) || null
    },

    getJobPosition(item) {
      if (item.job && item.job !== '—') return item.job
      if (item.jobId && this.jobMap[item.jobId]?.position) {
        return this.jobMap[item.jobId].position
      }
      return item.jobCode || '—'
    },

    getJobClient(item) {
      if (item.clientName) return item.clientName
      if (item.jobId && this.jobMap[item.jobId]?.client) {
        return this.jobMap[item.jobId].client
      }
      return ''
    },

    getJobCode(item) {
      if (item.jobCode && item.jobCode !== '—') return item.jobCode
      if (item.jobId && this.jobMap[item.jobId]?.code) {
        return this.jobMap[item.jobId].code
      }
      return item.jobId ? `SHJ-${item.jobId}` : '—'
    },

    // ── Feature 1: Export CSV ────────────────────────────────────────────────

    _escapeCsv(val) {
      const s = val === null || val === undefined ? '' : String(val)
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`
      }
      return s
    },

    _downloadCsv(headers, rows, filename) {
      const csv = this._buildCsvString(headers, rows)
      const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    _buildCsvString(headers, rows) {
      const escape = this._escapeCsv
      return [headers.map(escape).join(','), ...rows.map(row => row.map(escape).join(','))].join('\n')
    },

    _getExportTabKey() {
      if (this.viewMode === 'my') return 'history'
      if (this.tabIndex === 0) return 'team'
      if (this.tabIndex === 1) return 'datewise'
      if (this.tabIndex === 2) return this.historyTabIndex === 1 ? 'analysis' : 'history'
      if (this.tabIndex === 3) return 'hourly'
      return 'history'
    },

    _buildExportFilename(tabKey) {
      const { startDate, endDate } = this.appliedFilters
      const start = startDate || todayStr()
      const end = endDate || start
      return `calls_${this.viewMode}_${tabKey}_${start}_${end}.csv`
    },

    _buildExportZipFilename() {
      const { startDate, endDate } = this.appliedFilters
      const start = startDate || todayStr()
      const end = endDate || start
      return `calls_${this.viewMode}_export_${start}_${end}.zip`
    },

    _getAllExportReports() {
      const reports = [
        {
          key: 'history',
          filename: 'call_history.csv',
          build: () => this._buildCallHistoryExport(),
        },
      ]

      if (this.viewMode !== 'my') {
        reports.unshift(
          {
            key: 'team',
            filename: 'team_performance.csv',
            build: () => this._buildTeamExport(),
          },
          {
            key: 'datewise',
            filename: 'datewise_performance.csv',
            build: () => this._buildDatewiseExport(),
          },
        )
        reports.push(
          {
            key: 'hourly',
            filename: 'hourly_report.csv',
            build: () => this._buildHourlyExport(),
          },
          {
            key: 'analysis',
            filename: 'analysis_summary.csv',
            build: () => this._buildAnalysisExport(),
          },
        )
      }

      return reports
    },

    _filterHistoryForExport(calls) {
      return Array.isArray(calls) ? calls : []
    },

    /**
     * All rows matching the active shared filter (date / search / recruiter /
     * client / status / …). Not the current History page.
     * Analyzed / KPI chips apply only when exporting from History.
     */
    _getExportHistoryCalls(options = {}) {
      const applyHistoryToggles = options.applyHistoryToggles === true
      const key = this._alertScopeKey()
      const source = (this.periodCacheKey === key && Array.isArray(this.searchCallsCache))
        ? this.searchCallsCache
        : this.periodScopeCalls
      let rows = applyHistorySearchFilter(
        this,
        filterCallsList(this, source, { skipCallAnalysis: true }),
      )
      if (applyHistoryToggles) {
        rows = applyHistoryAnalysisFilter(this.appliedFilters.callAnalysis, rows)
      }
      if (applyHistoryToggles && this.kpiFilter && this.kpiFilter !== 'all') {
        rows = applyKpiFilter(this.kpiFilter, rows)
      }
      return sortCallsNewestFirst(rows)
    },

    async _awaitPeriodCacheForExport() {
      if (this._periodCacheCoversScope()) return true
      const loaded = Array.isArray(this.searchCallsCache) ? this.searchCallsCache.length : 0
      const total = Number(this.periodTotalRecords) || Number(this.callAnalyticsTotalRecords) || 0
      const hint = total > 0
        ? `Loading all matching records (${loaded.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')})…`
        : 'Loading all matching records for export…'
      this.$bvToast.toast(hint, {
        title: 'Export', variant: 'info', solid: true, autoHideDelay: 8000,
      })
      await this.fetchAllCallsCache()
      return Array.isArray(this.searchCallsCache)
    },

    _getCallNoteData(item) {
      const sid = this.getCallSid(item)
      return this.callNotes[sid] || {}
    },

    _getEngagementLabel(item) {
      const analysis = parseAnalysis(item)
      const engagement = getCallQuality(analysis)?.candidate_engagement
      return engagement ? formatQualityLevel(engagement) : ''
    },

    _getRedFlagCount(item) {
      return getRedFlags(parseAnalysis(item)).length
    },

    _buildCallHistoryExport() {
      const showRecruiter = (this.canManageCalls || this.$can('read', 'account_manager') || this.isAdminLike)
        && this.viewMode !== 'my'
      const applyHistoryToggles = this._getExportTabKey() === 'history'
      const calls = this._filterHistoryForExport(this._getExportHistoryCalls({ applyHistoryToggles }))

      const headers = [
        'Date & Time',
        ...(showRecruiter ? ['Recruiter Name', 'Recruiter Emp ID'] : []),
        'Call Type',
        'Direction',
        'Candidate Name',
        'SHC-ID',
        'Phone',
        'Job Code',
        'Client',
        'Position',
        'Candidate Status',
        'Call Status',
        'Duration',
        'Hygiene %',
        'Engagement',
        'Red Flag Count',
        'Interest',
        'AI Outcome',
        'Note Outcome',
        'Note Text',
        'Analysis Status',
        'Recording URL',
        'Call SID',
      ]

      const rows = calls.map(c => {
        const analysis = parseAnalysis(c)
        const note = this._getCallNoteData(c)
        const jobCode = this.getJobCode(c)
        const jobPosition = this.getJobPosition(c)
        const recruiterName = resolveRecruiterDisplayName(c._rawRecruiter, this.recruiterNameMap, c._recruiterName)

        const base = [
          c.date,
          ...(showRecruiter ? [recruiterName, c._rawRecruiter || ''] : []),
          c.callTypeTag?.label || '',
          getDisplayDirection(c),
          this.getCandidateName(c) || (c.candidate !== '—' ? c.candidate : ''),
          c.candidateIdDisplay !== '—' ? c.candidateIdDisplay : '',
          c.phone !== '—' ? c.phone : '',
          jobCode !== '—' ? jobCode : '',
          this.getJobClient(c) || '',
          jobPosition !== '—' ? jobPosition : '',
          this.getCandidateStatus(c) || '',
          c.status,
          c.duration,
          hasCompletedAnalysis(c) ? formatScorePercent(getHygieneScore(analysis)) : '',
          hasCompletedAnalysis(c) ? this._getEngagementLabel(c) : '',
          hasCompletedAnalysis(c) ? this._getRedFlagCount(c) : '',
          hasCompletedAnalysis(c) ? formatInterestLabel(analysis) : '',
          hasCompletedAnalysis(c) ? formatOutcomeLabel(analysis) : '',
          note.outcome || '',
          note.note || '',
          c._callAnalysisStatus || '',
          c.recordingUrl || '',
          c.sid || '',
        ]
        return base
      })

      return { headers, rows }
    },

    _buildTeamExport() {
      const headers = [
        'Recruiter',
        'Recruiter Emp ID',
        'Total',
        'Outbound',
        'Inbound',
        'Connected',
        'Not Ans.',
        'Failed',
        'Connect Rate',
        'Avg Duration',
        'Profiles Shared (Called)',
        'Profiles Shared (Not Called)',
        'Conversion',
      ]
      const rows = this.teamAnalytics.map(r => [
        r.name,
        r.empId,
        r.total,
        r.outbound,
        r.inbound,
        r.connected,
        r.notAnswered,
        r.failed,
        `${r.connectionRate}%`,
        r.avgDuration,
        r.profilesShared || 0,
        r.sharedNotConnected || 0,
        `${r.conversionRate}%`,
      ])
      return { headers, rows }
    },

    _buildDatewiseExport() {
      const headers = [
        'Date',
        'Recruiter',
        'Recruiter Emp ID',
        'Total',
        'Connected',
        'Not Ans.',
        'Failed',
        'Inbound',
        'Outbound',
        'Connect%',
        'Occupancy%',
        'PS Count',
        'Avg Duration',
      ]
      const rows = this.datewiseAnalytics.map(r => [
        r.date,
        r.recruiterName,
        r.empId,
        r.total,
        r.connected,
        r.notAnswered,
        r.failed,
        r.inbound,
        r.outbound,
        `${r.connectionRate}%`,
        `${r.occupancy}%`,
        r.psCount,
        r.avgDuration,
      ])
      return { headers, rows }
    },

    _buildHourlyExport() {
      const headers = [
        'Hour',
        'Total',
        'Connected',
        'Connection Rate',
        'Not Ans.',
        'Missed',
        'Avg Duration',
      ]

      const formatAvgDuration = seconds => {
        const s = parseInt(seconds, 10)
        if (!s || Number.isNaN(s)) return '—'
        const m = Math.floor(s / 60)
        const rem = s % 60
        if (m > 0) return `${m}m ${rem}s`
        return `${rem}s`
      }

      const rows = this.hourlyAnalytics.map(r => [
        r.label,
        r.total,
        r.connected,
        `${r.connectionRate}%`,
        r.notAnswered,
        r.missed,
        formatAvgDuration(r.avgDuration),
      ])

      const totals = this.hourlyAnalytics.reduce(
        (acc, r) => {
          acc.total += r.total
          acc.connected += r.connected
          acc.notAnswered += r.notAnswered
          acc.missed += r.missed
          acc.totalDuration += r.totalDuration
          return acc
        },
        { total: 0, connected: 0, notAnswered: 0, missed: 0, totalDuration: 0 },
      )
      const avgSec = totals.connected > 0 ? Math.round(totals.totalDuration / totals.connected) : 0
      rows.push([
        'Total',
        totals.total,
        totals.connected,
        totals.total > 0 ? `${Math.round((totals.connected / totals.total) * 100)}%` : '0%',
        totals.notAnswered,
        totals.missed,
        formatAvgDuration(avgSec),
      ])

      return { headers, rows }
    },

    _buildAnalysisExport() {
      const summary = groupAnalysisByRecruiter(this.callsWithAnalysis, this.recruiterNameMap)
      const headers = [
        'Recruiter',
        'Recruiter Emp ID',
        'Total Analyzed',
        'Fresh Call',
        'Follow-up',
        'Avg Hygiene',
        'Flags',
      ]
      const rows = summary.map(r => [
        r.name,
        r.empId,
        r.total,
        r.freshCall,
        r.followUp,
        r.avgHygiene != null ? formatScorePercent(r.avgHygiene) : '',
        r.flagCount > 0 ? r.flagCount : '',
      ])
      return { headers, rows }
    },

    async exportCSV() {
      if (this.isExporting) return
      this.isExporting = true
      try {
        const ready = await this._awaitPeriodCacheForExport()
        if (!ready) {
          this.$bvToast.toast('Could not load all matching records. Try a smaller date range.', {
            title: 'Export', variant: 'danger', solid: true, autoHideDelay: 6000,
          })
          return
        }
        const tabKey = this._getExportTabKey()
        let payload

        switch (tabKey) {
          case 'team':
            payload = this._buildTeamExport()
            break
          case 'datewise':
            payload = this._buildDatewiseExport()
            break
          case 'hourly':
            payload = this._buildHourlyExport()
            break
          case 'analysis':
            payload = this._buildAnalysisExport()
            break
          case 'history':
          default:
            payload = this._buildCallHistoryExport()
            break
        }

        if (!payload.rows.length) {
          this.$bvToast.toast('No data to export for the current tab and filters.', {
            title: 'Export', variant: 'warning', solid: true, autoHideDelay: 4000,
          })
          return
        }

        this._downloadCsv(payload.headers, payload.rows, this._buildExportFilename(tabKey))
        this.$bvToast.toast(
          `Exported ${payload.rows.length.toLocaleString('en-IN')} filtered record${payload.rows.length === 1 ? '' : 's'}.`,
          { title: 'Export', variant: 'success', solid: true, autoHideDelay: 4000 },
        )
      } finally {
        this.isExporting = false
      }
    },

    async exportAllReports() {
      if (this.isExporting) return
      this.isExporting = true
      try {
        const ready = await this._awaitPeriodCacheForExport()
        if (!ready) {
          this.$bvToast.toast('Could not load all matching records. Try a smaller date range.', {
            title: 'Export', variant: 'danger', solid: true, autoHideDelay: 6000,
          })
          return
        }
        const files = this._getAllExportReports()
          .map(report => {
            const payload = report.build()
            if (!payload.rows.length) return null
            return {
              name: report.filename,
              content: `\uFEFF${this._buildCsvString(payload.headers, payload.rows)}`,
            }
          })
          .filter(Boolean)

        if (!files.length) {
          this.$bvToast.toast('No data to export for the selected filters.', {
            title: 'Export', variant: 'warning', solid: true, autoHideDelay: 4000,
          })
          return
        }

        saveAs(createZipBlob(files), this._buildExportZipFilename())
        this.$bvToast.toast(
          `Exported ${files.length} report file${files.length === 1 ? '' : 's'} for the active filter.`,
          { title: 'Export', variant: 'success', solid: true, autoHideDelay: 4000 },
        )
      } finally {
        this.isExporting = false
      }
    },

    // ── Add to ATS pre-fill ───────────────────────────────────────────────

    /** Navigate to Add Candidate form pre-filled with call data */
    openAddToATS(item) {
      const query = {}

      // Phone
      const phone = cleanPhone(item.phone)
      if (phone) query.phone = phone

      // Candidate name — prefer enriched map name over raw (often "—")
      const rawName = item.candidate !== '—' ? item.candidate : ''
      const enrichedName = (this.candidateMap[item.candidateId]?.name || '').trim()
      const name = enrichedName || rawName
      if (name) {
        const parts = name.trim().split(/\s+/)
        query.fname = parts[0] || ''
        query.lname = parts.length > 1 ? parts.slice(1).join(' ') : '.'
      }

      // Source — inbound calls are likely references/callbacks; outbound are direct dials
      query.source = (item.direction || '').toLowerCase() === 'inbound' ? 'Referenced' : 'Direct'

      // Call notes / tag if any
      const note = (this.callNotes[item.rowKey] || this.callNotes[item.sid] || {}).note || ''
      if (note) query.remarks = note

      this.$router.push({ name: 'apps-candidates-add', query })
    },

    // ── Feature 2: Click-to-call with confirmation ───────────────────────────

    async confirmCall(item) {
      const rawPhone = item.phone && item.phone !== '—' ? item.phone : null
      if (!rawPhone || !isDialableIndianPhone(rawPhone)) {
        this.$bvToast.toast('No valid phone number on this call record.', {
          title: 'Cannot Call', variant: 'warning', solid: true, autoHideDelay: 5000,
        })
        return false
      }

      const phone = String(rawPhone).replace(/^\+?91/, '')
      const name = this.getCandidateName(item)
      const hasName = name && name !== '—'
      const message = hasName
        ? `Are you sure you want to call ${name} at ${phone}?`
        : `Are you sure you want to call ${phone}?`

      const ok = await this.$bvModal.msgBoxConfirm(message, {
        title: 'Confirm Call',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: 'Call Now',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        centered: true,
      })
      if (!ok) return false
      return this.callAgainDirect(item)
    },

    async callAgainDirect(item) {
      const rawPhone = item.phone && item.phone !== '—' ? item.phone : null
      if (!rawPhone) return false
      const phone = String(rawPhone).replace(/^\+?91/, '')
      const userData = getUserData()

      if (!userData.contact_no) {
        this.$bvToast.toast('Add your mobile number in your profile before making calls.', {
          title: 'Missing Phone', variant: 'warning', solid: true, autoHideDelay: 6000,
        })
        return false
      }

      try {
        await loadAgentAvailability({ force: true })
        this.callsAvailable = getCallsAvailableOrDefault()
      } catch (e) { /* keep last known */ }
      if (!this.callsAvailable) {
        this.$bvToast.toast(
          CALLS_UNAVAILABLE_MESSAGE,
          { title: 'Calling Paused', variant: 'warning', solid: true, autoHideDelay: 6000 },
        )
        return false
      }

      const callerId = this.myDid || resolveCallerId(this.exotelConfig, userData.emp_id)
      if (!callerId) {
        this.$bvToast.toast('No DID assigned to your account. Contact your admin.', {
          title: 'Cannot Call', variant: 'warning', solid: true, autoHideDelay: 6000,
        })
        return false
      }

      const rowKey = item.rowKey
      this.callingRowId = rowKey

      const enrichedName = (this.candidateMap[item.candidateId]?.name || item.candidate || '').trim()
      const callData = buildExotelCallPayload({
        userContactNo: userData.contact_no,
        targetPhone: phone,
        callerId,
        candidateId: item.candidateId || null,
        jobId: item.jobId || null,
        recruiterEmpId: userData.emp_id || 'Unknown',
        candidateName: enrichedName && enrichedName !== '—' ? enrichedName : null,
      })

      try {
        const res = await adminConfigService.makeExotelCall(callData)
        if (isExotelCallSuccess(res)) {
          this.callingRowId = null
          this.$set(this.callOutcomeByRowKey, rowKey, 'initiated')
          this.openPostDialModal(item, phone, enrichedName)
          return true
        }
        this.$set(this.callOutcomeByRowKey, rowKey, 'failed')
        this.$bvToast.toast(getExotelCallErrorMessage(res), { title: 'Call Failed', variant: 'danger', solid: true, autoHideDelay: 8000 })
        return false
      } catch (e) {
        this.$set(this.callOutcomeByRowKey, rowKey, 'failed')
        this.$bvToast.toast('Unexpected error initiating call', { title: 'Error', variant: 'danger', solid: true })
        return false
      } finally {
        if (this.callingRowId === rowKey) this.callingRowId = null
      }
    },

    buildStatusFlowContext(item, jobRecord, jobMeta) {
      const meta = jobMeta || (item && item.jobId ? (this.jobMap[item.jobId] || {}) : {})
      return statusFlowContextFromJob(jobRecord || meta, {
        client: this.getJobClient(item) || (meta && meta.client) || '',
        category: (item && (item._jobCategory || item.jobCategory)) || (meta && meta.category) || '',
        vertical: (item && (item._jobVertical || item.jobVertical)) || (meta && meta.vertical) || '',
        positionType: (jobRecord && (jobRecord.position_type || jobRecord.positionType))
          || (meta && (meta.position_type || meta.positionType))
          || '',
        payroll: (jobRecord && jobRecord.payroll) || (meta && meta.payroll) || '',
      })
    },

    getJobRecordForItem(item) {
      if (!item?.candidateId || !item?.jobId) return null
      const entry = this.candidateMap[item.candidateId]
      if (!entry?.candidateJobs?.length) return null
      return entry.candidateJobs.find(
        j => String(j.id) === String(item.jobId)
          || String(j.job_id) === String(item.jobId)
          || String(j.jobId) === String(item.jobId),
      ) || null
    },

    openPostDialModal(item, phone, candidateName) {
      const hasCandidateJob = Boolean(item.candidateId && item.jobId)
      const jobMeta = item.jobId ? (this.jobMap[item.jobId] || {}) : {}
      const jobRecord = this.getJobRecordForItem(item)
      this.postDialModal = {
        show: true,
        item,
        phone,
        candidateName: candidateName && candidateName !== '—' ? candidateName : '',
        candidateId: item.candidateId || null,
        jobId: item.jobId || null,
        jobCategory: resolveCallJobCategory(item, jobMeta),
        jobVertical: resolveCallJobVertical(item, jobMeta),
        hasCandidateJob,
        currentStatus: resolveJobPipelineStatus(
          this.getCandidateStatus(item),
          jobRecord && jobRecord.candidateStatus,
          item && item.candidate_status,
          item && item.pipeline_status,
        ),
        statusFlowContext: this.buildStatusFlowContext(item, jobRecord, jobMeta),
      }
    },

    onPostDialDismissed() {
      const item = this.postDialModal.item
      if (item?.rowKey && this.callOutcomeByRowKey[item.rowKey] === 'initiated') {
        this.$delete(this.callOutcomeByRowKey, item.rowKey)
      }
      this.postDialModal.item = null
    },

    async onPostDialOutcome({
      connected,
      postCallStatus,
      saveStatus,
      notesOnly,
      notInterestedReason,
      duplicateReason,
      coolingTimeline,
      comment,
      interested,
    }) {
      const item = this.postDialModal.item
      if (!item?.rowKey) return

      // Candidate pipeline status (postCallStatus) is separate from telephony connection.
      // Only stamp transient post-dial labels on rows that are not already connected per Exotel.
      const telephonyConnected = isTelephonyConnectedCall(item)
      const outcome = connected ? 'connected' : 'not_answered'
      const shouldStampOutcome = !telephonyConnected

      if (shouldStampOutcome) {
        this.$set(this.callOutcomeByRowKey, item.rowKey, outcome)
      }

      if (interested) {
        this.postDialModal.item = null
        if (shouldStampOutcome) this.scheduleClearCallOutcome(item.rowKey, outcome)
        return
      }

      if (notesOnly && item.candidateId && item.jobId) {
        const notes = String(comment || '').trim()
        if (notes) {
          this.postDialSaving = true
          try {
            const notesRes = await candidateService.updateCandidateStatus(
              item.candidateId,
              item.jobId,
              buildCallNotesOnlyPayload({ userData: getUserData(), comment: notes }),
            )
            if (notesRes && notesRes.status === 'OK') {
              this.$bvToast.toast('Call notes saved. Pipeline status was not changed.', {
                title: 'Notes saved', variant: 'success', solid: true, autoHideDelay: 3000,
              })
            } else {
              this.$bvToast.toast('Could not save call notes. Please try again.', {
                title: 'Error', variant: 'danger', solid: true, autoHideDelay: 6000,
              })
            }
          } catch (e) {
            this.$bvToast.toast(e?.message || 'Could not save call notes.', {
              title: 'Save Failed', variant: 'danger', solid: true, autoHideDelay: 6000,
            })
          } finally {
            this.postDialSaving = false
          }
        }
        this.postDialModal.item = null
        if (shouldStampOutcome) this.scheduleClearCallOutcome(item.rowKey, outcome)
        return
      }

      if (saveStatus && postCallStatus && item.candidateId && item.jobId) {
        this.postDialSaving = true
        try {
          const userData = getUserData()
          const jobRecord = this.getJobRecordForItem(item)
          let latestJobEntry = jobRecord
          let currentStatus = resolveJobPipelineStatus(
            this.getCandidateStatus(item),
            jobRecord && jobRecord.candidateStatus,
            this.postDialModal.currentStatus,
            item && item.candidate_status,
            item && item.pipeline_status,
          )
          try {
            const viewRes = await candidateService.getCandidateViewData(item.candidateId)
            const parsedView = this._parseCandidateView(viewRes)
            const jobs = parsedView?.candidateJobs || []
            const jobEntry = jobs.find(
              j => String(j.id) === String(item.jobId)
                || String(j.job_id) === String(item.jobId)
                || String(j.jobId) === String(item.jobId),
            )
            if (jobEntry) {
              latestJobEntry = jobEntry
              currentStatus = resolveJobPipelineStatus(jobEntry.candidateStatus, currentStatus)
            }
          } catch (refreshErr) {
            console.warn('[MyCalls] Could not refresh pipeline status before save:', refreshErr)
          }
          const flowContext = this.buildStatusFlowContext(item, latestJobEntry, this.jobMap[item.jobId])
          const verdict = evaluateCallSurfaceStatusWrite(currentStatus, postCallStatus, flowContext)
          if (!verdict.changeStatus) {
            if (verdict.reason === 'blocked') {
              this.$bvToast.toast(verdict.message, {
                title: 'Status flow', variant: 'warning', solid: true, autoHideDelay: 5000,
              })
            }
            const notes = String(comment || '').trim()
            if (notes) {
              const notesRes = await candidateService.updateCandidateStatus(
                item.candidateId,
                item.jobId,
                buildCallNotesOnlyPayload({ userData, comment: notes }),
              )
              if (notesRes && notesRes.status === 'OK') {
                this.$bvToast.toast('Call notes saved. Pipeline status was not changed.', {
                  title: 'Notes saved', variant: 'success', solid: true, autoHideDelay: 3000,
                })
              }
            }
            this.postDialModal.item = null
            if (shouldStampOutcome) this.scheduleClearCallOutcome(item.rowKey, outcome)
            return
          }
          const payload = buildCallOutcomeStatusPayload({
            postCallStatus,
            userData,
            jobRecord,
            notInterestedReason,
            duplicateReason,
            coolingTimeline,
            comment,
            canReadTalentAcquisition: this.$can ? this.$can('read', 'talent_acquisition') : false,
          })
          const saveRes = await candidateService.updateCandidateStatus(item.candidateId, item.jobId, payload)
          if (saveRes && saveRes.status === 'OK') {
            this.$bvToast.toast('Status updated successfully.', {
              title: 'Saved', variant: 'success', solid: true, autoHideDelay: 2000,
            })
            await this.fetchCalls(this.currentPage || 1)
          } else {
            const serverMsg = saveRes?.data?.message
              || saveRes?.data?.error
              || saveRes?.message
              || (typeof saveRes?.data === 'string' ? saveRes.data : null)
            this.$bvToast.toast(serverMsg || 'Could not update status. Please try again.', {
              title: 'Error', variant: 'danger', solid: true, autoHideDelay: 8000,
            })
          }
        } catch (e) {
          const serverMsg = e?.response?.data?.message || e?.response?.data?.error || e?.message
          this.$bvToast.toast(serverMsg || 'Could not update candidate status.', {
            title: 'Save Failed', variant: 'danger', solid: true, autoHideDelay: 8000,
          })
        } finally {
          this.postDialSaving = false
        }
      }

      this.postDialModal.item = null
      if (shouldStampOutcome) this.scheduleClearCallOutcome(item.rowKey, outcome)
    },

    scheduleClearCallOutcome(rowKey, outcome) {
      setTimeout(() => {
        if (this.callOutcomeByRowKey[rowKey] === outcome) {
          this.$delete(this.callOutcomeByRowKey, rowKey)
        }
      }, 8000)
    },

    // ── Feature 3: Call notes ────────────────────────────────────────────────

    getCallSid(item) {
      return item.sid || item.rowKey || `${item.candidateId || ''}_${item.jobId || ''}`
    },

    getNoteVariant(outcome) {
      const map = {
        Interested: 'light-success',
        'Not Interested': 'light-danger',
        Callback: 'light-warning',
        'Wrong Number': 'light-secondary',
        'No Answer - Follow Up': 'light-info',
      }
      return map[outcome] || 'light-secondary'
    },

    loadCallNotes() {
      const notes = {}
      const takeNote = (sid, n) => {
        if (!sid || !n || typeof n !== 'object') return
        notes[sid] = { outcome: n.outcome, note: n.note, savedAt: n.savedAt }
      }
      this.allCalls.forEach(call => {
        const callNotesList = Array.isArray(call._pipelineNotes) ? call._pipelineNotes : []
        if (!callNotesList.length) return
        const sid = call.sid || call.rowKey
        const match = callNotesList.find(n => n.sid === sid)
        if (match) takeNote(sid, match)
      })
      // Notes are saved to extra_details.call_notes; list SQL reads offerDetails.call_notes
      // (usually empty). Merge extras from candidate view so production refresh still shows them.
      Object.values(this.candidateMap || {}).forEach(entry => {
        (entry.candidateJobs || []).forEach(j => {
          let extra = j.extra_details || {}
          if (typeof extra === 'string') {
            try { extra = JSON.parse(extra) } catch { extra = {} }
          }
          const list = Array.isArray(extra.call_notes) ? extra.call_notes : []
          list.forEach(n => takeNote(n && n.sid, n))
        })
      })
      this.callNotes = notes
    },

    openNoteModal(item) {
      this.noteModal.item = item
      const sid = this.getCallSid(item)
      const existing = this.callNotes[sid]
      this.noteForm.outcome = existing?.outcome || ''
      this.noteForm.note = existing?.note || ''
      this.noteModal.show = true
    },

    async saveNote() {
      if (!this.noteModal.item) return
      const item = this.noteModal.item
      const sid = this.getCallSid(item)

      if (!item.candidateId || !item.jobId) {
        this.$bvToast.toast(
          'This call is not linked to a candidate in the ATS. Notes can only be saved for candidates who are already in the system.',
          { title: 'Cannot Save Note', variant: 'warning', solid: true, autoHideDelay: 6000 },
        )
        return
      }

      const noteData = {
        sid,
        outcome: this.noteForm.outcome,
        note: this.noteForm.note,
        savedAt: new Date().toISOString(),
        recruiter: getUserData().emp_id || '',
      }

      try {
        const viewRes = await candidateService.getCandidateViewData(item.candidateId)
        const parsedView = this._parseCandidateView(viewRes)
        const jobs = parsedView?.candidateJobs || viewRes?.data?.candidateJobs || []
        if (parsedView) {
          this.$set(this.candidateMap, item.candidateId, {
            ...(this.candidateMap[item.candidateId] || {}),
            name: parsedView.name || this.candidateMap[item.candidateId]?.name || '',
            phone: parsedView.phone || this.candidateMap[item.candidateId]?.phone || '',
            email: parsedView.email || this.candidateMap[item.candidateId]?.email || '',
            candidateJobs: parsedView.candidateJobs,
          })
        }
        const jobEntry = jobs.find(
          j => String(j.id) === String(item.jobId) || String(j.job_id) === String(item.jobId),
        )

        if (!jobEntry) {
          this.$bvToast.toast(
            'This candidate is not assigned to the job linked with this call. Notes can only be saved when the candidate is properly assigned to the job.',
            { title: 'Cannot Save Note', variant: 'warning', solid: true, autoHideDelay: 8000 },
          )
          return
        }

        const existingExtra = jobEntry.extra_details || {}
        const existingNotes = Array.isArray(existingExtra.call_notes) ? existingExtra.call_notes : []

        const idx = existingNotes.findIndex(n => n.sid === sid)
        if (idx >= 0) {
          existingNotes[idx] = noteData
        } else {
          existingNotes.push(noteData)
        }

        const saveRes = await candidateService.updateCandidateStatus(item.candidateId, item.jobId, {
          extra_details: { ...existingExtra, call_notes: existingNotes },
        })

        if (saveRes?.status === 400 || saveRes?.data?.status === 'FAILED') {
          this.$bvToast.toast(
            'The note could not be saved. The candidate may not be linked to this job in the system. Please contact your administrator if this keeps happening.',
            { title: 'Save Failed', variant: 'danger', solid: true, autoHideDelay: 8000 },
          )
          return
        }

        this.$set(this.callNotes, sid, { outcome: noteData.outcome, note: noteData.note, savedAt: noteData.savedAt })

        const callIdx = this.allCalls.findIndex(c => (c.sid || c.rowKey) === sid)
        if (callIdx >= 0) {
          const existing = this.allCalls[callIdx]
          const pipelineNotes = [...(existing._pipelineNotes || [])]
          const noteIdx = pipelineNotes.findIndex(n => n.sid === sid)
          if (noteIdx >= 0) {
            pipelineNotes[noteIdx] = noteData
          } else {
            pipelineNotes.push(noteData)
          }
          this.$set(this.allCalls, callIdx, Object.freeze({ ...existing, _pipelineNotes: pipelineNotes }))
        }

        const mapEntry = this.candidateMap[item.candidateId]
        if (mapEntry) {
          const mapJob = (mapEntry.candidateJobs || []).find(
            j => String(j.id) === String(item.jobId),
          )
          if (mapJob) {
            mapJob.extra_details = { ...(mapJob.extra_details || {}), call_notes: existingNotes }
          }
        }

        this.$bvToast.toast('Your note has been saved successfully.', { title: 'Note Saved', variant: 'success', solid: true, autoHideDelay: 3000 })
      } catch (err) {
        console.error('[MyCalls] Failed to save note to backend:', err)
        this.$bvToast.toast(
          'Something went wrong while saving the note. Please check your internet connection and try again.',
          { title: 'Save Failed', variant: 'danger', solid: true, autoHideDelay: 6000 },
        )
      }
    },

    resetNoteForm() {
      this.noteForm.outcome = ''
      this.noteForm.note = ''
      this.noteModal.item = null
    },

    // ── Feature 4: Reminders ─────────────────────────────────────────────────

    formatReminderTime(datetimeLocal) {
      if (!datetimeLocal) return ''
      try {
        return new Date(datetimeLocal).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
      } catch {
        return datetimeLocal
      }
    },

    openReminderModal(item) {
      this.reminderModal.item = item
      const d = new Date(Date.now() + 2 * 60 * 60 * 1000)
      const pad = n => String(n).padStart(2, '0')
      this.reminderModal.remindAt = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      this.reminderModal.show = true
    },

    async saveReminder() {
      if (!this.reminderModal.item || !this.reminderModal.remindAt) return
      const item = this.reminderModal.item
      const reminder = {
        remindAt: new Date(this.reminderModal.remindAt).toISOString(),
        candidateName: this.getCandidateName(item),
        phone: item.phone || '',
        candidateId: item.candidateId || null,
        jobId: item.jobId || null,
        note: (this.callNotes[item.rowKey] || this.callNotes[item.sid] || {}).note || '',
        setAt: new Date().toISOString(),
        fired: false,
      }

      if (item.candidateId && item.jobId) {
        // ── Backend save ──────────────────────────────────────────────────
        try {
          const viewRes = await candidateService.getCandidateViewData(item.candidateId)
          const parsedView = this._parseCandidateView(viewRes)
          const jobs = parsedView?.candidateJobs || viewRes?.data?.candidateJobs || []
          if (parsedView) {
            this.$set(this.candidateMap, item.candidateId, {
              ...(this.candidateMap[item.candidateId] || {}),
              name: parsedView.name || this.candidateMap[item.candidateId]?.name || '',
              phone: parsedView.phone || this.candidateMap[item.candidateId]?.phone || '',
              email: parsedView.email || this.candidateMap[item.candidateId]?.email || '',
              candidateJobs: parsedView.candidateJobs,
            })
          }
          const jobEntry = jobs.find(
            j => String(j.id) === String(item.jobId) || String(j.job_id) === String(item.jobId),
          )

          if (!jobEntry) {
            this.$bvToast.toast(
              'This candidate is not assigned to the job linked with this call. The reminder could not be saved. Please assign the candidate to the job first, then try again.',
              { title: 'Cannot Save Reminder', variant: 'warning', solid: true, autoHideDelay: 8000 },
            )
            return
          }

          const existingExtra = jobEntry.extra_details || {}
          const existingReminders = Array.isArray(existingExtra.callback_reminders)
            ? existingExtra.callback_reminders
            : []
          existingReminders.push(reminder)

          const saveRes = await candidateService.updateCandidateStatus(item.candidateId, item.jobId, {
            extra_details: { ...existingExtra, callback_reminders: existingReminders },
          })

          if (saveRes?.status === 400 || saveRes?.data?.status === 'FAILED') {
            this.$bvToast.toast(
              'The reminder could not be saved because the candidate is not linked to this job in the system. Please check if the candidate is properly assigned to the job.',
              { title: 'Save Failed', variant: 'danger', solid: true, autoHideDelay: 8000 },
            )
            return
          }

          // Patch candidateMap in-memory so upcomingReminders computed updates reactively
          const mapEntry = this.candidateMap[item.candidateId]
          if (mapEntry) {
            const mapJob = (mapEntry.candidateJobs || []).find(j => String(j.id) === String(item.jobId))
            if (mapJob) {
              const updatedExtra = { ...(mapJob.extra_details || {}), callback_reminders: existingReminders }
              this.$set(mapJob, 'extra_details', updatedExtra)
            }
          }

          this.$bvToast.toast(
            `Reminder set for ${reminder.candidateName}. You will be notified before the scheduled time.`,
            { title: 'Reminder Saved', variant: 'success', solid: true, autoHideDelay: 4000 },
          )
        } catch (err) {
          console.error('[MyCalls] Failed to save reminder:', err)
          this.$bvToast.toast(
            'Something went wrong while saving the reminder. Please check your internet connection and try again.',
            { title: 'Save Failed', variant: 'danger', solid: true, autoHideDelay: 6000 },
          )
        }
      } else {
        this.$bvToast.toast(
          'This call is not linked to a candidate in the ATS. Please add the candidate to the ATS first before setting a reminder.',
          { title: 'Cannot Save Reminder', variant: 'warning', solid: true, autoHideDelay: 6000 },
        )
      }
    },

    checkReminders() {
      const now = new Date()

      // ── A: Check backend reminders from enriched candidateMap ─────────
      // candidateMap[id].candidateJobs is populated by visible-page enrichment
      Object.entries(this.candidateMap).forEach(([candidateId, entry]) => {
        const jobs = entry.candidateJobs || []
        jobs.forEach(j => {
          const extra = j.extra_details || {}
          const reminders = Array.isArray(extra.callback_reminders) ? extra.callback_reminders : []
          reminders.forEach((r, rIdx) => {
            if (r.fired) return
            if (new Date(r.remindAt) <= now) {
              this._fireReminderNotification(r)
              // Use $set to keep Vue reactivity intact when marking as fired
              const updated = { ...r, fired: true }
              this.$set(extra.callback_reminders, rIdx, updated)
            }
          })
        })
      })

      // ── B: Check localStorage reminders (fallback / Not in ATS) ───────
      const stored = JSON.parse(localStorage.getItem('reminders') || '[]')
      const remaining = []
      stored.forEach(r => {
        if (!r.fired && new Date(r.remindAt) <= now) {
          this._fireReminderNotification(r)
          r.fired = true
        }
        remaining.push(r)
      })
      localStorage.setItem('reminders', JSON.stringify(remaining))
    },

    _fireReminderNotification(r) {
      const msg = `Call ${r.candidateName || r.phone || 'candidate'} — scheduled callback`
      if (window.Notification && Notification.permission === 'granted') {
        // eslint-disable-next-line no-new
        new Notification(`Call Reminder: ${r.candidateName || r.phone}`, {
          body: r.note || 'Scheduled callback reminder',
          icon: '/favicon.ico',
        })
      }
      this.$bvToast.toast(msg, {
        title: 'Call Reminder',
        variant: 'warning',
        solid: true,
        autoHideDelay: 10000,
      })
    },

    async callReminderDirect(r) {
      const matchRow = this.allCalls.find(
        c => String(c.candidateId) === String(r.candidateId) && c.phone === r.phone,
      )
      const item = matchRow || {
        phone: r.phone,
        candidateId: r.candidateId,
        jobId: r.jobId,
        rowKey: `reminder_${r.phone}`,
      }
      const success = await this.confirmCall(item)
      if (!success) return
      this.dismissReminder(r, -1)
    },

    async callBackMissed(c) {
      const success = await this.confirmCall(c)
      if (!success) return
      const key = c.rowKey || c.sid || c.phone
      if (key && !this.calledBackKeys.includes(key)) {
        this.calledBackKeys.push(key)
      }
    },

    dismissReminder(r, idx) {
      // Immediately hide from panel by adding to dismissedReminders (reactive via $set)
      const dismissKey = r._dismissKey || `${r.candidateId}_${r.remindAt}_${r.phone || ''}`
      this.$set(this.dismissedReminders, dismissKey, true)

      // Persist dismissal to backend by marking fired=true on the stored reminder
      if (!r.candidateId || !r.jobId) return
      const entry = this.candidateMap[r.candidateId]
      if (!entry) return
      const jobs = entry.candidateJobs || []
      const jobEntry = jobs.find(j => String(j.id) === String(r.jobId))
      if (!jobEntry) return
      const extra = jobEntry.extra_details || {}
      const reminders = Array.isArray(extra.callback_reminders) ? extra.callback_reminders : []
      const target = reminders.find(rem => rem.remindAt === r.remindAt && rem.phone === r.phone)
      if (target) {
        target.fired = true
        candidateService.updateCandidateStatus(r.candidateId, r.jobId, {
          extra_details: { ...extra, callback_reminders: reminders },
        }).catch(err => console.error('[MyCalls] Failed to dismiss reminder:', err))
      }
    },
  },
}
</script>

<style scoped>
/* Icon-only header action buttons (Filters, Export, Refresh) */
.header-icon-btn {
  min-width: 31px;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

/* Start Calling pill — match DID badge layout (compact inline-flex) */
.start-calling-pill {
  gap: 5px;
  font-size: 0.8rem;
  line-height: 1.2;
  white-space: nowrap;
}
.start-calling-pill--active {
  background: rgba(40, 199, 111, 0.08);
  border: 1px solid rgba(40, 199, 111, 0.25);
}
.start-calling-pill--paused {
  background: rgba(108, 117, 125, 0.08);
  border: 1px solid rgba(108, 117, 125, 0.25);
}
.start-calling-switch.custom-control,
.start-calling-switch {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0 !important;
  min-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  line-height: 1.2;
  flex: 0 0 auto;
  width: auto;
}
.start-calling-switch ::v-deep .custom-control-label {
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 2.375rem;
  font-size: 0.8rem;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
}
.start-calling-switch ::v-deep .custom-control-label::before {
  width: 2rem;
  height: 1.125rem;
  top: 50%;
  left: 0;
  margin-top: -0.5625rem;
  transform: none;
}
.start-calling-switch ::v-deep .custom-control-label::after {
  width: 0.875rem;
  height: 0.875rem;
  top: 50%;
  left: 0.125rem;
  margin-top: -0.4375rem;
  transform: none;
}
.start-calling-switch ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(0.875rem);
}
.start-calling-label {
  line-height: 1.2;
  vertical-align: middle;
}

/* Scoped styles for MyCalls layout — KPI and table styles live in their own child components */

/* Alert panel: three static cards — always occupy the strip */
.alert-panel-strip {
  display: flex !important;
  flex-wrap: wrap;
}
.alert-panel-strip__col {
  display: flex !important;
}
@media (min-width: 768px) {
  .alert-panel-strip__col {
    flex: 0 0 33.333%;
    max-width: 33.333%;
  }
}
.alert-panel-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 200px;
  width: 100%;
}
.alert-panel-card__header {
  flex-shrink: 0;
}
.alert-panel-card__body {
  flex: 1 1 auto;
  min-height: 140px;
  max-height: 260px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.alert-panel-card__footer {
  flex-shrink: 0;
  border-top: 1px solid #ebe9f1;
}

/* Alert panel rows */
.alert-call-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
}
.alert-call-row__info {
  flex: 1 1 auto;
  min-width: 0;
}
.alert-call-row__info > * + * {
  margin-top: 0.125rem;
}
.alert-call-row__name {
  font-size: 0.875rem;
  color: #5e5873;
  line-height: 1.3;
}
.alert-call-row__meta {
  font-size: 0.75rem;
  color: #6e6b7b;
  line-height: 1.35;
}
.alert-call-row__away {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  line-height: 1.25;
  white-space: nowrap;
}
.alert-call-row__recruiter {
  display: flex;
  align-items: center;
  min-width: 0;
}
.alert-call-row__action {
  flex-shrink: 0;
  align-self: center;
}
.alert-call-row__actions {
  display: flex;
  flex-shrink: 0;
  align-self: center;
  gap: 4px;
}

/* Make tab nav horizontally scrollable on mobile */
.main-tabs ::v-deep .nav-tabs {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  border-bottom: 1px solid #dee2e6;
}
.main-tabs ::v-deep .nav-tabs::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}
.main-tabs ::v-deep .nav-tabs .nav-link {
  white-space: nowrap;
}

.history-inner-tabs ::v-deep .nav-tabs {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  border-bottom: 1px solid #dee2e6;
}
.history-inner-tabs ::v-deep .nav-tabs::-webkit-scrollbar {
  display: none;
}
.history-inner-tabs ::v-deep .nav-tabs .nav-link {
  white-space: nowrap;
}

@media (max-width: 576px) {
  .page-header-row {
    flex-direction: column;
    align-items: flex-start !important;
    row-gap: 8px;
  }
  .page-header-row > div {
    width: 100%;
    flex-wrap: wrap;
  }
  .header-actions {
    justify-content: flex-start;
  }
}
</style>
